<!--
  Pre-Pour prototype. Reads the current patient + meds from the app session (keyon GetPatientMeds,
  Add Meds posts InsertAdminMecationInfo). Pours, administrations, statuses and the rest go through
  @/api/prepourApi, which sends a real patient's records to the port's pre-pour routes and keeps only
  the sample sheet and the kinds main keeps in the browser in localStorage (ppProto:* keys). Every
  layout uses the same handlers, so none of them touches storage directly.
  Layout variants: ?variant=A|B|C|D · time selector: ?times=blocks|chips|tiles|list|presets.
  The switcher strip is always on while this page is under review — it used to be dev-builds-only,
  which meant nobody could switch layouts on staging. A badge next to the tabs names the active
  variant, so feedback can be attributed to the layout it was actually about even when someone
  arrives on a URL rather than through the strip. Strip, badge and the losing variants all go when
  the layout is chosen.

  Rules this page implements (confirmed with the stakeholder, 2026-09-10):
  - An Administration Slot is one med at one date and one time; Pre-Pour schedules forward-dated slots
    over the SAME records Medication Administration later reads. There is no separate pre-pour entity.
  - Pouring covers a chosen RANGE of days (range picker, or non-consecutive days), from today forward.
    Backdating is not allowed. Pre-pouring an organizer days ahead is the point of the module.
  - "Today only" is an ADMINISTRATION rule (2026-09-15), not a pouring rule: see the window below. PR #277
    read it as a pouring rule and removed the date pickers; restored 2026-09-21 after the stakeholder
    clarified that the rule was only ever about which doses may be GIVEN.
  - Sign-off is required and locks every poured med-day; pouring a signed day again needs an explicit
    "Re-pour" plus an overlap-confirm at sign-off that names the earlier signer.
  - A pre-poured dose may be recorded as given only within PREPOUR_ADMIN_WINDOW_HOURS (default 1) before or
    after its scheduled time; outside that window the Administer button is disabled with the reason. This is
    what enforces "today only": past and future slots are read-only, whichever day they were poured on.
  - Discontinue (= inactivate) stops a med for any clinical reason, even after it was poured: pre-poured doses
    not yet given are withdrawn, recorded ones stay. Needs an NPI-bearing signer — enforced once the backend is wired.
  - On-hand (tabs available) decrements ONCE, when the pill leaves the bottle: at pour time for
    pre-poured doses, at administration for doses given straight from the bottle. Never both.
  - The witness is captured at ADMINISTRATION, per slot, as a typed name (no login or privilege):
    Taken / Except for (notes required) / Refused, shown as a colour-coded mark plus a text label.
  - Only solid oral forms are pourable; PRN / as-directed meds are listed but never poured.
  - Pouring is gated on MEDICATION_PREPOUR (24); Add Meds on MEDICATION_ADD_UNORDERED (16).
-->
<template>
  <div class="pp-proto">
    <!-- no-access gate -->
    <div v-if="!canViewPrepour" class="pp-noaccess">
      You don't have the Pre-Pour privilege (medication_prepour) or the Discontinue privilege
      (medication_discontinue) for this account.
    </div>

    <template v-else>
      <div class="medications-header">
        <h1 class="medications-title">Medications</h1>
        <div class="medications-nav">
          <router-link to="/currentMedications" class="nav-link">Current</router-link>
          <span class="nav-separator">|</span>
          <router-link to="/inactiveMedications" class="nav-link">Inactive</router-link>
          <span class="nav-separator">|</span>
          <router-link to="/medAdministration" class="nav-link">Administration</router-link>
          <span class="nav-separator">|</span>
          <span class="nav-link active">Prepour</span>
        </div>
      </div>

      <div class="pp-head">
        <div>
          <span class="pp-pt">{{ patientName }}</span>
          <span class="pp-dob" v-if="patientDob">DOB {{ patientDob }}</span>
        </div>
      </div>
      <div class="pp-modes" role="tablist">
        <button class="pp-mode" role="tab" :class="{ active: mode === 'pour' }" @click="mode = 'pour'">Pour sheet</button>
        <button class="pp-mode" role="tab" :class="{ active: mode === 'locked' }" @click="mode = 'locked'">Locked pours <span class="cnt">{{ pours.length }}</span></button>
        <button class="pp-mode" role="tab" :class="{ active: mode === 'admin' }" @click="mode = 'admin'">Administer <span class="cnt">{{ pendingSlotCount }}</span></button>
        <!-- Which layout is on screen. Without this a reviewer cannot tell A from a mistyped
             ?variant=X, which falls back to A silently — and their feedback lands on the wrong
             layout. Pour sheet only: the other two tabs render the same in every variant. -->
        <span v-if="mode === 'pour'" class="pp-variant-badge" data-cy="variant-badge">{{ variant }} · {{ VNAMES[variant] }}</span>
      </div>
      <div class="pp-devbar" v-if="mode === 'pour'">
        <span>Layout</span>
        <button v-for="k in KEYS" :key="k" :class="{ on: variant === k }" @click="setVariant(k)">{{ k }} · {{ VNAMES[k] }}</button>
        <span>Times</span>
        <button v-for="t in TIMES_UI" :key="t" :class="{ on: timesUi === t }" @click="setTimes(t)">{{ t }}</button>
        <button class="reset" @click="resetLocal">Reset local data</button>
      </div>

      <div v-if="usingSample" class="pp-banner">No patient selected — showing sample medications.</div>
      <div v-else-if="loadError" class="pp-banner">{{ loadError }}</div>
      <div v-if="poursError" class="pp-banner">{{ poursError }}</div>
      <div v-else-if="loading" class="pp-banner lock">Loading medications…</div>
      <div v-else-if="!meds.length" class="pp-banner">No medications on file for {{ patientName }}. Use Add Meds to create one.</div>
      <div v-if="!canPrepour" class="pp-banner lock">View only — you hold Discontinue but not Pre-Pour, so you can sign a pending discontinue here but not pour.</div>
      <div v-if="draftRestored && mode === 'pour'" class="pp-banner lock">Draft restored — your unsigned days, hours and choices from last time are back. <a href="#" @click.prevent="discardDraft">Start over</a></div>

      <!-- ====================== MODE: POUR SHEET ====================== -->
      <template v-if="mode === 'pour'">
        <!-- controls: date range + palette. Pouring covers the chosen days; the today-only rule is an
             administration rule and is enforced by the administration window, not here. -->
        <div class="pp-controls">
          <!-- One picker at a time. Showing both side by side with an "or" between them spent most of
               the row's width on the one the caregiver was not using, and then restated the result in a
               pill next to it. The mode switch was already the mobile pattern; it is now the only one. -->
          <div class="pp-row pp-dates pp-dates-desk">
            <span class="pp-lbl">Pouring for</span>
            <div class="pp-seg sm">
              <button :class="{ on: dateMode === 'range' }" title="Pour a run of consecutive days" @click="dateMode = 'range'">Range</button>
              <button :class="{ on: dateMode === 'multi' }" title="Pick individual days to pour" @click="dateMode = 'multi'">Specific days</button>
            </div>
            <div class="pp-dp narrow" v-if="dateMode === 'range'">
              <Datepicker v-model="range.picked" range :enable-time-picker="false" :min-date="new Date()" :max-date="maxPourDate" auto-apply
                :clearable="true" placeholder="Select date range" format="MM/dd/yyyy" @update:model-value="range.multi = []" />
            </div>
            <div class="pp-dp narrow" v-else>
              <Datepicker v-model="range.multi" multi-dates :enable-time-picker="false" :min-date="new Date()" :max-date="maxPourDate"
                :clearable="true" placeholder="Select non-consecutive days" format="MM/dd/yyyy" @update:model-value="range.picked = null" />
            </div>
            <span v-if="rangeDays.length" class="pp-daycount">{{ rangeDays.length }} day{{ rangeDays.length === 1 ? '' : 's' }}</span>
            <div class="grow"></div>
            <button class="pp-btn ghost" @click="openAddMed">Add Meds</button>
          </div>
          <!-- phones: one summary line, pickers only on demand -->
          <div class="pp-dates-mob">
            <div class="pp-dsum">
              <div class="grow">
                <div class="pp-lbl">Pouring for</div>
                <div class="pp-dsum-v">{{ rangeLabel }}<span v-if="rangeDays.length" class="pp-sub"> · {{ rangeDays.length }} day{{ rangeDays.length === 1 ? '' : 's' }}</span></div>
              </div>
              <button class="pp-btn ghost sm" :title="datesOpen ? 'Hide the day pickers' : 'Change the days to pour'" @click="datesOpen = !datesOpen">{{ datesOpen ? 'Done' : 'Change' }}</button>
              <button class="pp-btn ghost sm" title="Add a medication to this patient" @click="openAddMed">Add Meds</button>
            </div>
            <div v-if="datesOpen" class="pp-dpanel">
              <div class="pp-seg sm">
                <button :class="{ on: dateMode === 'range' }" @click="dateMode = 'range'">Date range</button>
                <button :class="{ on: dateMode === 'multi' }" @click="dateMode = 'multi'">Specific days</button>
              </div>
              <div class="pp-dp" v-if="dateMode === 'range'">
                <Datepicker v-model="range.picked" range :enable-time-picker="false" :min-date="new Date()" :max-date="maxPourDate" auto-apply
                  :clearable="true" placeholder="Pick first and last day" format="MM/dd/yyyy" @update:model-value="range.multi = []" />
              </div>
              <div class="pp-dp" v-else>
                <Datepicker v-model="range.multi" multi-dates :enable-time-picker="false" :min-date="new Date()" :max-date="maxPourDate"
                  :clearable="true" placeholder="Tap each day to pour" format="MM/dd/yyyy" @update:model-value="range.picked = null" />
              </div>
            </div>
          </div>
          <!-- PPU-2 / PPU-3 — "Time view": the hours are setup furniture, so once they are chosen the
               panel folds away to this one line and reopens on a tap. The summary stays visible while
               it is closed, so the caregiver never has to open it just to see what is set. -->
          <div class="pp-timeshead">
            <button class="pp-timestoggle" :class="{ open: timesOpen }" :aria-expanded="String(timesOpen)"
              :title="timesOpen ? 'Hide the administration hours' : 'Show the administration hours'"
              @click="toggleTimesPanel">
              <span class="pp-caret" aria-hidden="true"></span>
              <span>Time view</span>
              <span class="pp-timescount">{{ palette.length }}</span>
            </button>
            <div class="pp-timessum">
              <template v-if="palette.length"><span class="pp-time" v-for="h in palette" :key="'s'+h">{{ h }}</span></template>
              <span v-else class="pp-hint-t">no hours chosen yet — tap Time view to pick them</span>
            </div>
            <div class="grow"></div>
            <input v-if="variant !== 'B'" v-model="medQuery" class="pp-search" type="search" placeholder="Search medication or diagnosis" aria-label="Search medications or diagnosis">
            <label class="pp-autofill" :title="autoFill ? 'Hours are filled in for each medication from its frequency (marked Auto). Untick to choose every time by hand.' : 'Every time is chosen by hand. Tick to fill them in from each medication\'s frequency.'">
              <input type="checkbox" v-model="autoFill"> Auto-fill times
            </label>
            <!-- PPU-5 — sorting is a view preference, not a step in pouring, so it sits in the corner
                 the way a filter control does rather than taking a row of its own. A native select
                 keeps it to one line whatever the option labels are. `availableSorts` means a layout
                 that cannot honour an ordering never offers it. -->
            <label v-if="variant !== 'B'" class="pp-sortpick" :title="'Group the sheet — ' + (availableSorts.find(o => o.key === sortBy) || {}).label">
              <span>Group</span>
              <select v-model="sortBy">
                <option v-for="o in availableSorts" :key="o.key" :value="o.key">{{ o.label }}</option>
              </select>
            </label>
          </div>
          <div v-show="timesOpen" class="pp-timespanel">
          <!-- time selector style 1: four period blocks with checkboxes (PoC) -->
          <div v-if="timesUi === 'blocks'" class="pp-palette">
            <div class="pp-block" v-for="blk in blocks" :key="blk.t">
              <h4>{{ blk.t }}</h4>
              <div class="pp-hours">
                <label class="pp-hour" v-for="h in blk.hrs" :key="h">
                  <input type="checkbox" :checked="palette.includes(h)" @change="togglePalette(h)"> {{ h }}
                </label>
              </div>
            </div>
          </div>
          <!-- time selector style 2: tappable chips per period, wraps on phones -->
          <div v-else-if="timesUi === 'chips'" class="pp-chipsel">
            <div class="pp-chiprow" v-for="blk in blocks" :key="blk.t">
              <span class="pp-chiplbl">{{ blk.t }}</span>
              <button v-for="h in blk.hrs" :key="h" class="pp-tchip" :class="{ on: palette.includes(h) }" @click="togglePalette(h)">{{ h }}</button>
            </div>
          </div>
          <!-- time selector style 4: compact checklist, two columns of periods -->
          <div v-else-if="timesUi === 'list'" class="pp-tlist">
            <div class="pp-tlist-col" v-for="blk in blocks" :key="blk.t">
              <div class="pp-tlist-h">{{ blk.t }}</div>
              <label class="pp-tlist-row" v-for="h in blk.hrs" :key="h" :class="{ on: palette.includes(h) }">
                <input type="checkbox" :checked="palette.includes(h)" @change="togglePalette(h)"> {{ h }}
              </label>
            </div>
          </div>
          <!-- time selector style 5: quick picks (morning / midday / evening / bedtime) with an adjustable hour, plus custom hours -->
          <div v-else-if="timesUi === 'presets'" class="pp-presets">
            <div class="pp-preset" v-for="pr in PRESETS" :key="pr.key" :class="{ on: palette.includes(presetHour[pr.key]) }">
              <button class="pp-preset-btn" :title="(palette.includes(presetHour[pr.key]) ? 'Remove ' : 'Add ') + pr.label + ' (' + presetHour[pr.key] + ')'" @click="togglePreset(pr.key)">
                <b>{{ pr.label }}</b><span>{{ presetHour[pr.key] }}</span>
              </button>
              <select class="pp-preset-sel" :value="presetHour[pr.key]" title="Change the hour for this quick pick" @change="setPresetHour(pr.key, $event.target.value)">
                <option v-for="h in pr.hours" :key="h" :value="h">{{ h }}</option>
              </select>
            </div>
            <div class="pp-preset-custom">
              <span class="pp-lbl">Other hour</span>
              <select class="pp-pill" v-model="customHour">
                <option value="">Choose…</option>
                <option v-for="h in HOUR_ORDER.filter(x => !palette.includes(x))" :key="h" :value="h">{{ h }}</option>
              </select>
              <button class="pp-btn ghost sm" :disabled="!customHour" @click="addCustomHour">Add hour</button>
            </div>
            <div class="pp-tiles-sum"><b>Selected:</b> <span v-if="palette.length"><span class="pp-time" v-for="h in palette" :key="h">{{ h }} <span class="x" role="button" :title="'Remove ' + h" @click="togglePalette(h)">×</span></span></span><span v-else class="pp-hint-t">none yet</span></div>
          </div>
          <!-- time selector style 3: 24-hour tile clock (6 per row), selected times summarised -->
          <div v-else class="pp-tiles-wrap">
            <div class="pp-tiles">
              <button v-for="h in HOUR_ORDER" :key="h" class="pp-tile" :class="{ on: palette.includes(h), night: NIGHT.includes(h) }" @click="togglePalette(h)">{{ h }}</button>
            </div>
            <div class="pp-tiles-sum"><b>Selected:</b> <span v-if="palette.length">{{ palette.join(' · ') }}</span><span v-else class="pp-hint-t">none — tap hours above</span></div>
          </div>
          </div>
        </div>

        <!-- Status filters, as in the nursing module: each opens a list with dates and reasons; the sheet
             below is never filtered by them. -->
        <div class="pp-stfilters" role="group" aria-label="Medication status filters">
          <span class="pp-lbl">Status</span>
          <button v-for="f in STATUS_FILTERS" :key="f.key" class="pp-stfilter" :class="['s-' + f.key, { on: statusFilter === f.key }]"
            :aria-pressed="String(statusFilter === f.key)" :disabled="!filterCount(f)" @click="toggleStatusFilter(f.key)">
            {{ f.label }} <span class="cnt">{{ filterCount(f) }}</span>
          </button>
        </div>
        <div v-if="statusFilterDef" class="pp-stlist">
          <div class="pp-stlist-h"><b>{{ statusFilterDef.label }}</b> · {{ statusFilterMeds.length }} medication(s)
            <button class="pp-mini" @click="statusFilter = null">Close</button></div>
          <div v-for="m in statusFilterMeds" :key="m.id" class="pp-stlist-row">
            <div class="grow"><b>{{ m.name }}</b>
              <template v-if="statusFilter === 'Hold'">
                <div v-for="h in activeHolds(m)" :key="h.at || [h.start, h.end, h.times].join('|')" class="pp-stlist-line">
                  On hold {{ fmtSpan(h.start, h.end) }} · {{ timesText(h.times) }}<template v-if="h.reason"> — {{ h.reason }}</template>
                  <!-- keyon's holds carry no who / when; History has them -->
                  <span v-if="h.at" class="pp-sub"> · by {{ h.by || 'unknown user' }}, {{ fmtStamp(h.at) }}</span>
                  <button class="pp-mini" :title="'Release this hold — ' + shortName(m) + ' is poured on these days again'" @click="releaseHold(m, h)">Release</button>
                </div>
              </template>
              <div v-else v-for="line in filterLines(m)" :key="line" class="pp-stlist-line">{{ line }}</div>
            </div>
          </div>
        </div>

        <!-- PPU-7 — carried over is not signed. The review was explicit that every period is
             re-confirmed and signed before anything may be given, so this says so rather than
             letting a pre-filled sheet look like a finished one. -->
        <div v-if="carriedOver" class="pp-banner lock">
          <b>Carried over from your last pour</b> — {{ carriedOver.matched }} medication{{ carriedOver.matched === 1 ? '' : 's' }} placed at the same hours<template v-if="carriedOver.by"> (signed by {{ carriedOver.by }} {{ fmtStamp(carriedOver.at) }})</template>.
          <template v-if="carriedOver.fresh"> {{ carriedOver.fresh }} medication{{ carriedOver.fresh === 1 ? ' is' : 's are' }} new since then and still need times.</template>
          Nothing is poured until you review this and sign it. <a href="#" @click.prevent="discardTemplate">Start from scratch</a>
        </div>

        <!-- PPU-18 — say it plainly and once, at the top, before anything is poured. -->
        <div v-if="suggestedMeds.length" class="pp-banner auto">
          <b>{{ suggestedMeds.length }} medication{{ suggestedMeds.length === 1 ? ' was' : 's were' }} pre-poured automatically</b>
          from how often {{ suggestedMeds.length === 1 ? 'it is' : 'they are' }} due and the hours you selected.
          Check {{ suggestedMeds.length === 1 ? 'it' : 'them' }} before signing — tap any time to change it.
        </div>

        <div v-if="partlyLockedMeds.length" class="pp-banner lock">
          <b>{{ partlyLockedMeds.length }}</b> med(s) are already pre-poured for part of this range. Those days are left out of this pour — each medication says which.
        </div>
        <div v-if="lockedMeds.length" class="pp-banner lock">
          <b>{{ lockedMeds.length }}</b> med(s) already have signed pours in this range. Choose <b>Re-pour</b> on a med to pour those days again — sign-off will ask you to confirm the overlap.
        </div>

        <!-- ============ VARIANT A : spreadsheet ============ -->
        <div v-if="variant === 'A'" :class="{ 'pp-prnfirst': prnFirst }">
          <table class="pp-grid pp-resp">
            <thead><tr>
              <th>Medication</th><th>Status</th><th>Tabs Avail.</th><th>Frequency</th>
              <th>Dose</th><th>Needed</th><th>Remaining</th><th>Administration Times</th>
            </tr></thead>
            <tbody>
              <template v-for="g in medGroups" :key="g.key">
                <tr v-if="g.label || cupPicker === g.hour" class="pp-grouprow"><td colspan="8">
                  <div v-if="g.label" class="pp-grouphead">
                    <b>{{ g.label }}</b> <span class="pp-sub">· {{ g.meds.length }} med(s)</span>
                    <button v-if="g.hour && cupPicker !== g.hour && canAddTo(g.hour)" class="pp-addtime" :title="'Choose which medications are given at ' + g.hour" @click="cupPicker = g.hour">Add med to {{ g.hour }}</button>
                  </div>
                  <div v-if="g.hour && cupPicker === g.hour && canAddTo(g.hour)" class="pp-picker pp-grouppick">
                                      <button v-for="m in addableTo(g.hour)" :key="m.id" class="pp-pchip wide" :title="m._reason" @click="addToCup(m, g.hour)">
                      {{ shortName(m) }} <span class="pp-sub">· {{ m.times.length }} of {{ maxTimes(m) }}</span>
                    </button>
                                      <button class="pp-btn sm" @click="cupPicker = null">Done</button>
                  </div>
                  <div v-if="g.hour && !g.meds.length" class="pp-sub pp-groupempty">No medications at {{ g.hour }} yet.</div>
                </td></tr>
              <template v-for="m in g.meds" :key="g.key + ':' + m.id">
              <tr :class="['st-' + effStatus(m), { low: isLow(m), np: !m.pourable, locked: isFullyLocked(m), poured: allPoured(m) }]">
                <td class="c-name"><div class="pp-name">{{ m.name }}</div><div class="pp-sub">{{ m.route }} · {{ m.form }}</div>
                  <div v-if="isPartlyLocked(m)" class="pp-locktag">{{ lockNote(m) }}</div>
                  <div v-else-if="lockInfo(m) && !m.repour" class="pp-locktag">Poured · signed by {{ lockInfo(m).signer }}</div>
                  <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote" :class="'s-' + n.cls">{{ n.text }}</div>
                  <div class="pp-rowactions"><button class="pp-mini" :aria-expanded="String(!!detailRows[m.id])" :title="detailRows[m.id] ? 'Hide the full record' : 'Show the medication, prescription, pharmacy and prescriber record'" @click="toggleDetail(m)">{{ detailRows[m.id] ? 'Less' : 'More' }}</button> <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button></div></td>
                <td data-l="Status"><select class="pp-stsel" :class="'s-' + effStatus(m)" :value="effStatus(m)" :disabled="!canChangeStatus(m)" :title="'Status of ' + shortName(m) + ' — choose to hold, mark new, change, complete or discontinue'" @change="chooseStatus(m, $event)"><option v-for="o in statusOptionsFor(m)" :key="o" :value="o" :disabled="o === 'Partial' || o === 'DC-pending'">{{ statusLabel(o) }}</option></select></td>
                <td class="num" data-l="Tabs avail."><input v-if="m.pourable" class="pp-onhand" type="number" v-model.number="m.onhand" :disabled="isFullyLocked(m)"><span v-else>—</span></td>
                <td data-l="Frequency">
                  <select class="pp-freq" :value="m.freq" :disabled="isFullyLocked(m) || !canChangeStatus(m)" @change="requestFreqChange(m, $event)">
                    <option v-for="f in FREQS" :key="f" :value="f">{{ f }}</option>
                  </select>
                </td>
                <td class="num" data-l="Dose">{{ m.dose }}</td>
                <td class="num" data-l="Needed">{{ m.pourable ? needed(m) : '—' }}</td>
                <td class="num" data-l="Remaining"><span v-if="m.pourable" :class="{ lowtag: isLow(m) }">{{ remaining(m) }}</span><span v-else>—</span></td>
                <td data-l="Times" class="c-times">
                  <template v-if="!m.pourable"><span class="pp-np">Not pourable ({{ m.form }}) — administer directly</span></template>
                  <template v-else-if="isFullyLocked(m)"><span class="pp-time lock" v-for="t in lockInfo(m).times" :key="t">{{ t }}</span> <button class="pp-addtime" :title="'Pour ' + shortName(m) + ' again — replaces the pour signed by ' + lockInfo(m).signer" @click="startRepour(m)">Re-pour</button></template>
                  <template v-else>
                    <div v-if="m.repour" class="pp-locktag repour">Re-pouring · replaces the pour signed by {{ lockInfo(m).signer }} <a href="#" @click.prevent="cancelRepour(m)">cancel</a></div>
                    <!-- the bottle check leads the pouring controls: it is the step before anything goes in a cup -->
                    <div v-if="canScan(m)" class="pp-scanline">
                      <button class="pp-scanbtn" :class="{ ok: scans[m.id] }" :title="scanTitle(m)" @click="scanMed(m)">{{ scans[m.id] ? '✓ Scanned' : 'Scan bottle' }}</button>
                      <span class="pp-hint-t">{{ scans[m.id] ? 'Bottle matches this medication' : 'Confirm the bottle in your hand is this medication' }}</span>
                    </div>
                    <div v-if="stateLabel(m)" class="pp-tstate" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                    <template v-if="!m.skip">
                      <span class="pp-time tap" v-for="(t,i) in m.times" :key="t+i" role="button" :class="{ poured: isPoured(m, t), held: heldAllRange(m, t) }" :aria-pressed="String(isPoured(m, t))" :title="isPoured(m, t) ? t + ' is in its cup — tap to unmark' : 'Tap once ' + t + ' is in its cup'" @click="togglePoured(m, t)"><i v-if="isPoured(m, t)" class="ck">✓</i>{{ t }}<template v-if="heldAllRange(m, t)"> · held</template> <span class="x" role="button" :aria-label="'Move or discontinue ' + t" :title="'Move ' + t + ' to another hour, or discontinue it'" @click.stop="removeTime(m,i)">×</span></span>
                      <button v-if="!m.picking" class="pp-addtime" :disabled="!canOpenPicker(m)" :title="pickerHint(m)" @click="openPicker(m)">{{ m.times.length ? 'Change times' : 'Choose times' }}</button>
                      <div v-else class="pp-picker">
                        <div class="pp-sub">Pick {{ maxTimes(m) }} of the selected hours<span v-if="!palette.length"> — select hours in the palette above first</span></div>
                        <div class="pp-pchips">
                          <button v-for="t in palette" :key="t" class="pp-pchip" :class="{ on: m.times.includes(t) }" :disabled="!m.times.includes(t) && m.times.length >= maxTimes(m)"
                            :title="m.times.includes(t) ? 'Remove ' + t : (m.times.length >= maxTimes(m) ? m.freq + ' allows ' + maxTimes(m) : 'Add ' + t)" @click="toggleMedTime(m, t)">{{ t }}</button>
                        </div>
                        <button class="pp-btn sm" @click="m.picking = false">Done</button>
                      </div>
                    </template>
                    <label class="pp-skip" :title="m.skip ? 'Include this medication in the pour again' : 'Leave this medication out of this pour'">
                      <input type="checkbox" :checked="m.skip" @change="toggleSkip(m)"> Not pouring this range
                    </label>
                  </template>
                </td>
              </tr>
              <tr v-if="detailRows[m.id]" class="pp-recordrow"><td colspan="8"><PrePourMedRecord :med="m" /></td></tr>
              </template>
              </template>
            </tbody>
          </table>

          <!-- PPU-9 — as-needed medications are listed but never poured: every pour column is a dash,
               so the row says "this exists, it is not in the organizer" at a glance. -->
          <div class="pp-prnblock">
          <div class="pp-section-h">PRN / As-Needed (not auto-poured)</div>
          <table class="pp-grid pp-resp">
            <tbody>
              <tr v-for="m in prns" :key="m.id">
                <td class="c-name"><div class="pp-name">{{ m.name }}</div><div class="pp-sub">{{ m.route }} · {{ m.form }}</div>
                  <div class="pp-rowactions"><button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button></div></td>
                <td data-l="Status"><span class="pp-chip s-PRN">As needed</span></td>
                <td class="num na" data-l="Tabs avail.">&mdash;</td>
                <td class="num na" data-l="Frequency">&mdash;</td>
                <td class="num na" data-l="Dose">&mdash;</td>
                <td class="num na" data-l="Needed">&mdash;</td>
                <td class="num na" data-l="Remaining">&mdash;</td>
                <td data-l="Times" class="c-times"><span class="pp-time">On demand</span></td>
              </tr>
            </tbody>
          </table>
          </div>
          <div class="pp-sign"><span class="pp-lbl">Sign-off</span>
            <button class="pp-btn sign" @click="openSignOff" :disabled="!canPour" :title="pourBlockReason || 'Review and sign this pour'">Pour &amp; Sign</button>
            <span class="pp-hint-t" :class="{ warn: !canPour }">{{ pourBlockReason || pourReadyText }}</span>
          </div>
        </div>

        <!-- ============ VARIANT B : time-cup board ============ -->
        <div v-else-if="variant === 'B'">
          <div class="pp-board">
            <div class="pp-cup" v-for="t in palette" :key="t">
              <header>{{ t }} cup</header>
              <div class="body">
                <template v-if="cupMeds(t).length">
                  <div class="pp-medcard" :class="['st-' + effStatus(m), { low: isLow(m), locked: isFullyLocked(m), poured: isPoured(m, t) }]" v-for="m in cupMeds(t)" :key="m.id">
                    <div class="mc-top"><span class="pp-name">{{ shortName(m) }}</span>
                      <button v-if="!isFullyLocked(m)" class="pp-mini" :title="'Move ' + shortName(m) + ' out of the ' + t + ' cup to another hour, or discontinue that dose'" @click="removeTime(m, m.times.indexOf(t))">Remove</button>
                      <span v-else class="pp-chip" :class="'s-'+m.status">{{ statusLabel(m.status) }}</span></div>
                    <div class="mc-dose">{{ m.dose }} × {{ m.form }} · {{ m.freq }}
                      <span v-if="isLow(m)" class="lowtag"> · short {{ Math.abs(remaining(m)) }}</span></div>
                    <div v-if="stateLabel(m)" class="pp-tstate sm" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                    <div v-if="m.repour" class="pp-locktag repour">Re-pour · replaces {{ lockInfo(m).signer }}</div>
                    <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote" :class="'s-' + n.cls">{{ n.text }}</div>
                    <div v-if="canScan(m)" class="pp-cardacts">
                      <button class="pp-scanbtn" :class="{ ok: scans[m.id] }" :title="scanTitle(m)" @click="scanMed(m)">{{ scans[m.id] ? '✓ Scanned' : 'Scan bottle' }}</button>
                      <button class="pp-pourbtn" :class="{ on: isPoured(m, t) }" :aria-pressed="String(isPoured(m, t))" :title="isPoured(m, t) ? 'Unmark — not in the ' + t + ' cup yet' : 'Mark ' + shortName(m) + ' as in the ' + t + ' cup'" @click="togglePoured(m, t)">{{ isPoured(m, t) ? '✓ Poured' : 'Mark poured' }}</button>
                    </div>
                    <div v-else-if="isPartlyLocked(m)" class="pp-locktag">{{ lockNote(m) }}</div>
                  </div>
                </template>
                <div v-else class="pp-empty">Empty — add the meds due at {{ t }}</div>
                <button v-if="cupPicker !== t && canAddTo(t)" class="pp-addtime" :title="'Choose which medications go into the ' + t + ' cup'" @click="cupPicker = t">Add med to this cup</button>
                <div v-else-if="canAddTo(t)" class="pp-picker">
                  <button v-for="m in addableTo(t)" :key="m.id" class="pp-pchip wide" :title="m._reason" @click="addToCup(m, t)">
                    {{ shortName(m) }} <span class="pp-sub">· {{ m.times.length }} of {{ maxTimes(m) }}</span>
                  </button>
                  <button class="pp-btn sm" @click="cupPicker = null">Done</button>
                </div>
              </div>
              <div class="foot">{{ cupMeds(t).length }} med(s) in this cup</div>
            </div>
            <div class="pp-npcol">
              <div class="pp-section-h" style="margin-top:0">Can't be poured</div>
              <div class="pp-medcard" style="background:#fff;margin-bottom:8px" v-for="m in nonPourable" :key="m.id">
                <div class="pp-name">{{ shortName(m) }}</div>
                <div class="pp-np" style="margin-top:4px">{{ m.form }} — administer directly</div>
              </div>
              <div class="pp-section-h">PRN</div>
              <div class="pp-medcard" style="background:#fff" v-for="m in prns" :key="m.id">
                <div class="mc-top"><span class="pp-name">{{ shortName(m) }}</span><span class="pp-chip s-PRN">As needed</span></div>
                <div class="mc-dose">as directed — on demand, never pre-poured</div>
              </div>
            </div>
          </div>
          <div v-if="!palette.length" class="pp-banner">Select hours in the palette above to create cups.</div>
          <div class="pp-section-h">Medications to place</div>
          <div class="pp-place">
            <div class="pp-placerow" v-for="m in placeable" :key="m.id" :class="[stateClass(m), 'st-' + effStatus(m)]">
              <div class="grow"><b>{{ m.name }}</b> <span class="pp-sub">· {{ m.freq }} · needs {{ maxTimes(m) }} cup{{ maxTimes(m) === 1 ? '' : 's' }}</span>
                <div v-if="stateLabel(m)" class="pp-tstate sm" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote" :class="'s-' + n.cls">{{ n.text }}</div></div>
              <select class="pp-stsel" :class="'s-' + effStatus(m)" :value="effStatus(m)" :disabled="!canChangeStatus(m)" :title="'Status of ' + shortName(m) + ' — choose to hold, mark new, change, complete or discontinue'" @change="chooseStatus(m, $event)"><option v-for="o in statusOptionsFor(m)" :key="o" :value="o" :disabled="o === 'Partial' || o === 'DC-pending'">{{ statusLabel(o) }}</option></select>
              <label class="pp-skip" :title="m.skip ? 'Include this medication in the pour again' : 'Leave this medication out of this pour'">
                <input type="checkbox" :checked="m.skip" @change="toggleSkip(m)"> Not pouring this range
              </label>
              <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button>
            </div>
            <div class="pp-placerow locked" v-for="m in lockedMeds" :key="'l' + m.id">
              <div class="grow"><b>{{ m.name }}</b> <span class="pp-sub">· poured · signed by {{ lockInfo(m).signer }} · {{ lockInfo(m).times.join(', ') }}</span></div>
              <button class="pp-addtime" @click="startRepour(m)">Re-pour</button>
              <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button>
            </div>
            <div v-if="!placeable.length && !lockedMeds.length" class="pp-sub">No medications to place in this range.</div>
          </div>
          <div class="pp-sign"><span class="pp-lbl">Sign-off</span>
            <button class="pp-btn sign" @click="openSignOff" :disabled="!canPour" :title="pourBlockReason || 'Review and sign this pour'">Pour &amp; Sign</button>
            <span class="pp-hint-t" :class="{ warn: !canPour }">{{ pourBlockReason || pourReadyText }}</span>
          </div>
        </div>

        <!-- ============ VARIANT D : compact checklist (mobile-first, one line per med, chips always visible) ============ -->
        <div v-else-if="variant === 'D'">
          <div class="pp-clist" :class="{ 'pp-prnfirst': prnFirst }">
            <template v-for="g in medGroups" :key="g.key">
              <div v-if="g.label" class="pp-grouphead">
                <b>{{ g.label }}</b> <span class="pp-sub">· {{ g.meds.length }} med(s)</span>
                <button v-if="g.hour && cupPicker !== g.hour && canAddTo(g.hour)" class="pp-addtime" :title="'Choose which medications are given at ' + g.hour" @click="cupPicker = g.hour">Add med to {{ g.hour }}</button>
              </div>
              <div v-if="g.hour && cupPicker === g.hour && canAddTo(g.hour)" class="pp-picker pp-grouppick">
                              <button v-for="m in addableTo(g.hour)" :key="m.id" class="pp-pchip wide" :title="m._reason" @click="addToCup(m, g.hour)">
                  {{ shortName(m) }} <span class="pp-sub">· {{ m.times.length }} of {{ maxTimes(m) }}</span>
                </button>
                              <button class="pp-btn sm" @click="cupPicker = null">Done</button>
              </div>
              <div v-if="g.hour && !g.meds.length" class="pp-sub pp-groupempty">No medications at {{ g.hour }} yet.</div>
            <div class="pp-crow" v-for="m in g.meds" :key="g.key + ':' + m.id" :class="[stateClass(m), 'st-' + effStatus(m), { np: !m.pourable, locked: isFullyLocked(m), low: isLow(m), poured: allPoured(m) }]">
              <div class="pp-crow-top">
                <div class="grow">
                  <div class="pp-name">{{ m.name }}</div>
                  <div class="pp-sub">{{ m.freq }} · {{ m.dose }} × {{ m.form }}<template v-if="m.pourable"> · {{ m.onhand }} on hand, needs {{ needed(m) }}<span v-if="isLow(m)" class="lowtag"> (short {{ Math.abs(remaining(m)) }})</span></template></div>
                </div>
                <select class="pp-stsel" :class="'s-' + effStatus(m)" :value="effStatus(m)" :disabled="!canChangeStatus(m)" :title="'Status of ' + shortName(m) + ' — choose to hold, mark new, change, complete or discontinue'" @change="chooseStatus(m, $event)"><option v-for="o in statusOptionsFor(m)" :key="o" :value="o" :disabled="o === 'Partial' || o === 'DC-pending'">{{ statusLabel(o) }}</option></select>
                <button class="pp-mini" :aria-expanded="String(!!detailRows[m.id])" :title="detailRows[m.id] ? 'Hide the full record' : 'Show the medication, prescription, pharmacy and prescriber record'" @click="toggleDetail(m)">{{ detailRows[m.id] ? 'Less' : 'More' }}</button>
                <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button>
              </div>
              <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote" :class="'s-' + n.cls">{{ n.text }}</div>
              <div v-if="!m.pourable" class="pp-np">Not pourable ({{ m.form }}) — administer directly</div>
              <div v-else-if="isFullyLocked(m)" class="pp-locktag">Poured · {{ lockInfo(m).signer }} · {{ lockInfo(m).times.join(', ') }} <button class="pp-mini" @click="startRepour(m)">Re-pour</button></div>
              <template v-else>
                <div v-if="isPartlyLocked(m)" class="pp-locktag">{{ lockNote(m) }}</div>
                <div class="pp-crow-times">
                  <div v-if="m.repour" class="pp-locktag repour">Re-pouring · replaces the pour signed by {{ lockInfo(m).signer }} <a href="#" @click.prevent="cancelRepour(m)">cancel</a></div>

                  <!-- the bottle check leads the pouring controls: it is the step before anything goes in a cup -->
                  <div v-if="canScan(m)" class="pp-scanline">
                    <button class="pp-scanbtn" :class="{ ok: scans[m.id] }" :title="scanTitle(m)" @click="scanMed(m)">{{ scans[m.id] ? '✓ Scanned' : 'Scan bottle' }}</button>
                    <span class="pp-hint-t">{{ scans[m.id] ? 'Bottle matches this medication' : 'Confirm the bottle in your hand is this medication' }}</span>
                  </div>
                  <div v-if="stateLabel(m)" class="pp-tstate" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                  <!-- same controls as layout C: the chosen hours, and the others only once "Change times" is pressed -->
                  <div v-if="!m.skip" class="pp-etimechips">
                    <span class="pp-time tap" v-for="(t,i) in m.times" :key="t+i" role="button" :class="{ poured: isPoured(m, t), held: heldAllRange(m, t) }" :aria-pressed="String(isPoured(m, t))" :title="isPoured(m, t) ? t + ' is in its cup — tap to unmark' : 'Tap once ' + t + ' is in its cup'" @click="togglePoured(m, t)"><i v-if="isPoured(m, t)" class="ck">✓</i>{{ t }}<template v-if="heldAllRange(m, t)"> · held</template> <span class="x" role="button" :aria-label="'Move or discontinue ' + t" :title="'Move ' + t + ' to another hour, or discontinue it'" @click.stop="removeTime(m,i)">×</span></span>
                    <button v-if="!m.picking" class="pp-addtime" :disabled="!canOpenPicker(m)" :title="pickerHint(m)" @click="openPicker(m)">{{ m.times.length ? 'Change times' : 'Choose times' }}</button>
                    <div v-else class="pp-picker">
                      <div class="pp-sub">Pick {{ maxTimes(m) }} of the selected hours<span v-if="!palette.length"> — select hours in the palette above first</span></div>
                      <div class="pp-pchips">
                        <button v-for="t in palette" :key="t" class="pp-pchip" :class="{ on: m.times.includes(t) }" :disabled="!m.times.includes(t) && m.times.length >= maxTimes(m)"
                          :title="m.times.includes(t) ? 'Remove ' + t : (m.times.length >= maxTimes(m) ? m.freq + ' allows ' + maxTimes(m) : 'Add ' + t)" @click="toggleMedTime(m, t)">{{ t }}</button>
                      </div>
                      <button class="pp-btn sm" @click="m.picking = false">Done</button>
                    </div>
                  </div>
                  <label class="pp-skip" :title="m.skip ? 'Include this medication in the pour again' : 'Leave this medication out of this pour'">
                    <input type="checkbox" :checked="m.skip" @change="toggleSkip(m)"> Not pouring this range
                  </label>
                </div>
              </template>
              <PrePourMedRecord v-if="detailRows[m.id]" :med="m" />
            </div>
            </template>
            <div class="pp-crow prn pp-prnblock" v-for="m in prns" :key="m.id">
              <div class="pp-crow-top"><div class="grow"><div class="pp-name">{{ m.name }}</div><div class="pp-sub">as directed · on demand, not pre-poured</div></div><span class="pp-chip s-PRN">As needed</span> <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button></div>
            </div>
          </div>
          <div class="pp-sign"><span class="pp-lbl">Sign-off</span>
            <button class="pp-btn sign" @click="openSignOff" :disabled="!canPour" :title="pourBlockReason || 'Review and sign this pour'">Pour &amp; Sign</button>
            <span class="pp-hint-t" :class="{ warn: !canPour }">{{ pourBlockReason || pourReadyText }}</span>
          </div>
        </div>

        <!-- ============ VARIANT E : compact rows, C's detail on demand ============
          First draft of the combination the review asked for. D is compact but thin on information;
          C carries everything but costs a lot of screen. E leads with the compact row — the state a
          caregiver who pours this patient every week lives in — and keeps C's full detail one tap
          away per medication, so nobody has to switch layouts to answer a question about a dose. -->
        <div v-else-if="variant === 'E'" :class="{ 'pp-prnfirst': prnFirst }">
          <div class="pp-ehead">
            <span class="pp-sub">{{ scheduledSorted.length }} scheduled · tap a row's <b>Details</b> for the full record</span>
            <div class="grow"></div>
            <button class="pp-btn ghost sm" @click="allDetailsOpen ? collapseAllDetails() : expandAllDetails()">
              {{ allDetailsOpen ? 'Hide all details' : 'Show all details' }}
            </button>
          </div>

          <div class="pp-elist">
            <template v-for="g in medGroups" :key="g.key">
              <div v-if="g.label" class="pp-grouphead">
                <b>{{ g.label }}</b> <span class="pp-sub">· {{ g.meds.length }} med(s)</span>
                <button v-if="g.hour && cupPicker !== g.hour && canAddTo(g.hour)" class="pp-addtime" :title="'Choose which medications are given at ' + g.hour" @click="cupPicker = g.hour">Add med to {{ g.hour }}</button>
              </div>
              <div v-if="g.hour && cupPicker === g.hour && canAddTo(g.hour)" class="pp-picker pp-grouppick">
                              <button v-for="m in addableTo(g.hour)" :key="m.id" class="pp-pchip wide" :title="m._reason" @click="addToCup(m, g.hour)">
                  {{ shortName(m) }} <span class="pp-sub">· {{ m.times.length }} of {{ maxTimes(m) }}</span>
                </button>
                              <button class="pp-btn sm" @click="cupPicker = null">Done</button>
              </div>
              <div v-if="g.hour && !g.meds.length" class="pp-sub pp-groupempty">No medications at {{ g.hour }} yet.</div>
            <div class="pp-erow" v-for="m in g.meds" :key="g.key + ':' + m.id"
              :class="[stateClass(m), 'st-' + effStatus(m), { np: !m.pourable, locked: isFullyLocked(m), low: isLow(m), open: !!detailRows[m.id], poured: allPoured(m) }]">

              <!-- compact line: everything needed to pour, nothing else -->
              <div class="pp-etop">
                <button class="pp-ecaret" :class="{ open: !!detailRows[m.id] }" :aria-expanded="String(!!detailRows[m.id])"
                  :title="detailRows[m.id] ? 'Hide the full record for ' + shortName(m) : 'Show the full record for ' + shortName(m)"
                  @click="toggleDetail(m)"><i class="pp-caret" aria-hidden="true"></i></button>
                <div class="grow pp-ehit" role="button" :aria-expanded="String(!!detailRows[m.id])"
                  :title="detailRows[m.id] ? 'Hide the full record for ' + shortName(m) : 'Show the full record for ' + shortName(m)"
                  @click="toggleDetail(m)">
                  <div class="pp-name">{{ m.name }}</div>
                  <div class="pp-sub">{{ m.freq }} · {{ m.dose }} × {{ m.form }}<template v-if="m.pourable"> · {{ m.onhand }} on hand, needs {{ needed(m) }}<span v-if="isLow(m)" class="lowtag"> (short {{ Math.abs(remaining(m)) }})</span></template></div>
                </div>
                <select class="pp-stsel" :class="'s-' + effStatus(m)" :value="effStatus(m)" :disabled="!canChangeStatus(m)" :title="'Status of ' + shortName(m) + ' — choose to hold, mark new, change, complete or discontinue'" @change="chooseStatus(m, $event)"><option v-for="o in statusOptionsFor(m)" :key="o" :value="o" :disabled="o === 'Partial' || o === 'DC-pending'">{{ statusLabel(o) }}</option></select>
                <button class="pp-mini" :title="detailRows[m.id] ? 'Hide the full record' : 'Show the full record'" @click="toggleDetail(m)">{{ detailRows[m.id] ? 'Less' : 'More' }}</button>
                <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button>
              </div>

              <!-- the pouring controls stay on the compact row: this is the part used every week -->
              <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote" :class="'s-' + n.cls">{{ n.text }}</div>
              <div v-if="!m.pourable" class="pp-np">Not pourable ({{ m.form }}) — administer directly</div>
              <div v-else-if="isFullyLocked(m)" class="pp-locktag">Poured · {{ lockInfo(m).signer }} · {{ lockInfo(m).times.join(', ') }} <button class="pp-mini" @click="startRepour(m)">Re-pour</button></div>
              <template v-else>
                <div v-if="isPartlyLocked(m)" class="pp-locktag">{{ lockNote(m) }}</div>
                <div class="pp-etimes">
                  <div v-if="m.repour" class="pp-locktag repour">Re-pouring · replaces the pour signed by {{ lockInfo(m).signer }} <a href="#" @click.prevent="cancelRepour(m)">cancel</a></div>
                  <!-- the bottle check leads the pouring controls: it is the step before anything goes in a cup -->
                  <div v-if="canScan(m)" class="pp-scanline">
                    <button class="pp-scanbtn" :class="{ ok: scans[m.id] }" :title="scanTitle(m)" @click="scanMed(m)">{{ scans[m.id] ? '✓ Scanned' : 'Scan bottle' }}</button>
                    <button v-if="m.times.length" class="pp-pourbtn" :class="{ on: allPoured(m) }" :aria-pressed="String(allPoured(m))" :title="allPoured(m) ? 'Unmark — not in its cups yet' : 'Mark ' + shortName(m) + ' as in its cups at ' + m.times.join(', ')" @click="toggleAllPoured(m)">{{ allPoured(m) ? '✓ Poured' : 'Mark poured' }}</button>
                    <span class="pp-hint-t">{{ scans[m.id] ? 'Bottle matches this medication' : 'Confirm the bottle in your hand is this medication' }}</span>
                  </div>
                  <div v-if="stateLabel(m)" class="pp-tstate" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                  <!-- same controls as layout C: the chosen hours, and the others only once "Change times" is pressed -->
                  <div v-if="!m.skip" class="pp-etimechips">
                    <span class="pp-time" v-for="(t,i) in m.times" :key="t+i" :class="{ poured: isPoured(m, t), held: heldAllRange(m, t) }"><i v-if="isPoured(m, t)" class="ck">✓</i>{{ t }}<template v-if="heldAllRange(m, t)"> · held</template> <span class="x" role="button" :aria-label="'Move or discontinue ' + t" :title="'Move ' + t + ' to another hour, or discontinue it'" @click="removeTime(m,i)">×</span></span>
                    <button v-if="!m.picking" class="pp-addtime" :disabled="!canOpenPicker(m)" :title="pickerHint(m)" @click="openPicker(m)">{{ m.times.length ? 'Change times' : 'Choose times' }}</button>
                    <div v-else class="pp-picker">
                      <div class="pp-sub">Pick {{ maxTimes(m) }} of the selected hours<span v-if="!palette.length"> — select hours in the palette above first</span></div>
                      <div class="pp-pchips">
                        <button v-for="t in palette" :key="t" class="pp-pchip" :class="{ on: m.times.includes(t) }" :disabled="!m.times.includes(t) && m.times.length >= maxTimes(m)"
                          :title="m.times.includes(t) ? 'Remove ' + t : (m.times.length >= maxTimes(m) ? m.freq + ' allows ' + maxTimes(m) : 'Add ' + t)" @click="toggleMedTime(m, t)">{{ t }}</button>
                      </div>
                      <button class="pp-btn sm" @click="m.picking = false">Done</button>
                    </div>
                  </div>
                  <label class="pp-skip" :title="m.skip ? 'Include this medication in the pour again' : 'Leave this medication out of this pour'">
                    <input type="checkbox" :checked="m.skip" @change="toggleSkip(m)"> Not pouring this range
                  </label>
                </div>
              </template>

              <!-- C's record, on demand -->
              <div v-if="detailRows[m.id]" class="pp-edetail">
                <div class="kv"><b>Route / Form</b>{{ m.route }} · {{ m.form }}</div>
                <div class="kv"><b>Frequency</b>
                  <select class="pp-freq" :value="m.freq" :disabled="isFullyLocked(m) || !m.pourable || !canChangeStatus(m)" @change="requestFreqChange(m, $event)"><option v-for="f in FREQS" :key="f" :value="f">{{ f }}</option></select></div>
                <div class="kv"><b>Dose</b>{{ m.dose }} × {{ m.form }}</div>
                <div class="kv"><b>On hand</b><input v-if="m.pourable" class="pp-onhand" type="number" v-model.number="m.onhand" :disabled="isFullyLocked(m)"><span v-else>—</span></div>
                <div class="kv"><b>Needed / Remaining</b>
                  <span v-if="m.pourable">{{ needed(m) }} / <span :class="{ lowtag: isLow(m) }">{{ remaining(m) }}</span></span><span v-else>—</span></div>
                <div class="kv"><b>Supply</b>
                  <span v-if="m.pourable" class="pp-supply" :class="{ low: isLow(m) }"><i :style="{ width: supplyPct(m)+'%' }"></i></span><span v-else>—</span></div>
                <div class="kv" style="grid-column:1/-1"><b>Status</b>{{ statusLabel(effStatus(m)) }}<template v-if="lockInfo(m) && !m.repour"> · poured, signed by {{ lockInfo(m).signer }}</template></div>
                <PrePourMedRecord :med="m" style="grid-column:1/-1" />
              </div>
            </div>
            </template>
            <div v-if="!scheduledSorted.length" class="pp-sub" style="padding:10px 2px">No scheduled medications in this range.</div>
          </div>

          <div class="pp-prnblock">
            <div class="pp-section-h">PRN / As-Needed (not auto-poured)</div>
            <div class="pp-elist">
              <div class="pp-erow prn" v-for="m in prns" :key="m.id">
                <div class="pp-etop">
                  <span class="pp-ecaret spacer" aria-hidden="true"></span>
                  <div class="grow"><div class="pp-name">{{ m.name }}</div><div class="pp-sub">as directed · on demand, never pre-poured</div></div>
                  <span class="pp-chip s-PRN">As needed</span>
                  <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button>
                </div>
              </div>
              <div v-if="!prns.length" class="pp-sub" style="padding:10px 2px">No as-needed medications on file.</div>
            </div>
          </div>

          <!-- C's rail, flattened into one strip so the layout stays compact -->
          <div class="pp-esum">
            <div class="cell"><span>Range</span><b>{{ rangeLabel }}</b></div>
            <div class="cell"><span>Times</span><b>{{ palette.length ? palette.join(', ') : '—' }}</b></div>
            <div class="cell"><span>Ready to pour</span><b>{{ completeCandidates.length }} of {{ pourCandidates.length }}</b></div>
            <div class="cell" v-if="skippedMeds.length"><span>Not pouring</span><b>{{ skippedMeds.length }}</b></div>
            <div class="cell" v-if="lockedMeds.length"><span>Already poured</span><b>{{ lockedMeds.length }}</b></div>
            <div class="cell" :class="{ warn: lowMeds.length }"><span>Short on supply</span><b>{{ lowMeds.length }}</b></div>
          </div>

          <div class="pp-sign"><span class="pp-lbl">Sign-off</span>
            <button class="pp-btn sign" @click="openSignOff" :disabled="!canPour" :title="pourBlockReason || 'Review and sign this pour'">Pour &amp; Sign</button>
            <span class="pp-hint-t" :class="{ warn: !canPour }">{{ pourBlockReason || pourReadyText }}</span>
          </div>
        </div>

        <!-- ============ VARIANT C : per-med cards + rail ============ -->
        <div v-else>
          <div class="pp-layoutC">
            <div :class="{ 'pp-prnfirst': prnFirst }">
              <div class="pp-stack">
                <template v-for="g in medGroups" :key="g.key">
                  <div v-if="g.label" class="pp-grouphead">
                    <b>{{ g.label }}</b> <span class="pp-sub">· {{ g.meds.length }} med(s)</span>
                    <button v-if="g.hour && cupPicker !== g.hour && canAddTo(g.hour)" class="pp-addtime" :title="'Choose which medications are given at ' + g.hour" @click="cupPicker = g.hour">Add med to {{ g.hour }}</button>
                  </div>
                  <div v-if="g.hour && cupPicker === g.hour && canAddTo(g.hour)" class="pp-picker pp-grouppick">
                                      <button v-for="m in addableTo(g.hour)" :key="m.id" class="pp-pchip wide" :title="m._reason" @click="addToCup(m, g.hour)">
                      {{ shortName(m) }} <span class="pp-sub">· {{ m.times.length }} of {{ maxTimes(m) }}</span>
                    </button>
                                      <button class="pp-btn sm" @click="cupPicker = null">Done</button>
                  </div>
                  <div v-if="g.hour && !g.meds.length" class="pp-sub pp-groupempty">No medications at {{ g.hour }} yet.</div>
                <div class="pp-mcard" :class="['st-' + effStatus(m), { low: isLow(m), np: !m.pourable, locked: isFullyLocked(m), poured: allPoured(m) }]" v-for="m in g.meds" :key="g.key + ':' + m.id">
                  <header><span class="pp-name grow">{{ m.name }}</span>
                    <span v-if="isFullyLocked(m)" class="pp-locktag">Poured · {{ lockInfo(m).signer }}</span>
                    <select class="pp-stsel" :class="'s-' + effStatus(m)" :value="effStatus(m)" :disabled="!canChangeStatus(m)" :title="'Status of ' + shortName(m) + ' — choose to hold, mark new, change, complete or discontinue'" @change="chooseStatus(m, $event)"><option v-for="o in statusOptionsFor(m)" :key="o" :value="o" :disabled="o === 'Partial' || o === 'DC-pending'">{{ statusLabel(o) }}</option></select>
                    <button class="pp-mini" :aria-expanded="String(!!detailRows[m.id])" :title="detailRows[m.id] ? 'Hide the full record' : 'Show the medication, prescription, pharmacy and prescriber record'" @click="toggleDetail(m)">{{ detailRows[m.id] ? 'Less' : 'More' }}</button>
                    <button v-if="!isDcPending(m) && canDiscontinue" class="pp-mini dc" :title="'Discontinue ' + shortName(m)" @click="openDcSign(m)">Discontinue</button></header>
                  <div v-if="isPartlyLocked(m)" class="pp-locktag">{{ lockNote(m) }}</div>
                  <div v-for="n in medNotes(m)" :key="n.text" class="pp-mednote pp-mednote-c" :class="'s-' + n.cls">{{ n.text }}</div>
                  <div class="detail">
                    <div class="kv"><b>Route / Form</b>{{ m.route }} · {{ m.form }}</div>
                    <div class="kv"><b>Frequency</b>
                      <select class="pp-freq" :value="m.freq" :disabled="isFullyLocked(m) || !m.pourable || !canChangeStatus(m)" @change="requestFreqChange(m, $event)"><option v-for="f in FREQS" :key="f" :value="f">{{ f }}</option></select></div>
                    <div class="kv"><b>Dose</b>{{ m.dose }}</div>
                    <div class="kv"><b>On hand</b><input v-if="m.pourable" class="pp-onhand" type="number" v-model.number="m.onhand" :disabled="isFullyLocked(m)"><span v-else>—</span></div>
                    <div class="kv"><b>Needed / Remaining</b>
                      <span v-if="m.pourable">{{ needed(m) }} / <span :class="{ lowtag: isLow(m) }">{{ remaining(m) }}</span></span><span v-else>—</span></div>
                    <div class="kv"><b>Supply</b>
                      <span v-if="m.pourable" class="pp-supply" :class="{ low: isLow(m) }"><i :style="{ width: supplyPct(m)+'%' }"></i></span><span v-else>—</span></div>
                    <div class="kv" style="grid-column:1/-1"><b>Administration times</b>
                      <template v-if="!m.pourable"><span class="pp-np">Not pourable — administer directly</span></template>
                      <template v-else-if="isFullyLocked(m)"><span class="pp-time lock" v-for="t in lockInfo(m).times" :key="t">{{ t }}</span> <button class="pp-addtime" :title="'Pour ' + shortName(m) + ' again — replaces the pour signed by ' + lockInfo(m).signer" @click="startRepour(m)">Re-pour</button></template>
                      <template v-else>
                    <div v-if="m.repour" class="pp-locktag repour">Re-pouring · replaces the pour signed by {{ lockInfo(m).signer }} <a href="#" @click.prevent="cancelRepour(m)">cancel</a></div>
                    <!-- the bottle check leads the pouring controls: it is the step before anything goes in a cup -->
                    <div v-if="canScan(m)" class="pp-scanline">
                      <button class="pp-scanbtn" :class="{ ok: scans[m.id] }" :title="scanTitle(m)" @click="scanMed(m)">{{ scans[m.id] ? '✓ Scanned' : 'Scan bottle' }}</button>
                      <span class="pp-hint-t">{{ scans[m.id] ? 'Bottle matches this medication' : 'Confirm the bottle in your hand is this medication' }}</span>
                    </div>
                    <div v-if="stateLabel(m)" class="pp-tstate" :class="stateClass(m)">{{ stateLabel(m) }}</div>
                    <template v-if="!m.skip">
                      <span class="pp-time tap" v-for="(t,i) in m.times" :key="t+i" role="button" :class="{ poured: isPoured(m, t), held: heldAllRange(m, t) }" :aria-pressed="String(isPoured(m, t))" :title="isPoured(m, t) ? t + ' is in its cup — tap to unmark' : 'Tap once ' + t + ' is in its cup'" @click="togglePoured(m, t)"><i v-if="isPoured(m, t)" class="ck">✓</i>{{ t }}<template v-if="heldAllRange(m, t)"> · held</template> <span class="x" role="button" :aria-label="'Move or discontinue ' + t" :title="'Move ' + t + ' to another hour, or discontinue it'" @click.stop="removeTime(m,i)">×</span></span>
                      <button v-if="!m.picking" class="pp-addtime" :disabled="!canOpenPicker(m)" :title="pickerHint(m)" @click="openPicker(m)">{{ m.times.length ? 'Change times' : 'Choose times' }}</button>
                      <div v-else class="pp-picker">
                        <div class="pp-sub">Pick {{ maxTimes(m) }} of the selected hours<span v-if="!palette.length"> — select hours in the palette above first</span></div>
                        <div class="pp-pchips">
                          <button v-for="t in palette" :key="t" class="pp-pchip" :class="{ on: m.times.includes(t) }" :disabled="!m.times.includes(t) && m.times.length >= maxTimes(m)"
                            :title="m.times.includes(t) ? 'Remove ' + t : (m.times.length >= maxTimes(m) ? m.freq + ' allows ' + maxTimes(m) : 'Add ' + t)" @click="toggleMedTime(m, t)">{{ t }}</button>
                        </div>
                        <button class="pp-btn sm" @click="m.picking = false">Done</button>
                      </div>
                    </template>
                    <label class="pp-skip" :title="m.skip ? 'Include this medication in the pour again' : 'Leave this medication out of this pour'">
                      <input type="checkbox" :checked="m.skip" @change="toggleSkip(m)"> Not pouring this range
                    </label>
                      </template>
                    </div>
                  </div>
                  <PrePourMedRecord v-if="detailRows[m.id]" :med="m" class="pp-mcard-record" />
                </div>
                </template>
              </div>
              <div class="pp-prnblock">
                <div class="pp-section-h">PRN / As-Needed</div>
                <div class="pp-stack">
                  <div class="pp-mcard" v-for="m in prns" :key="m.id">
                    <header><span class="pp-name grow">{{ m.name }}</span><span class="pp-chip s-PRN">As needed</span></header>
                    <div class="detail"><div class="kv" style="grid-column:1/-1"><b>Handling</b>Administered on demand — not pre-poured.</div></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="pp-rail">
              <h3>Pour summary</h3>
              <div class="line"><span>Range</span><span>{{ rangeLabel }}</span></div>
              <div class="line"><span>Times</span><span>{{ palette.join(', ') }}</span></div>
              <div class="line"><span>Meds to pour</span><span>{{ completeCandidates.length }} of {{ pourCandidates.length }}</span></div>
              <div class="line" v-if="skippedMeds.length"><span>Not pouring</span><span>{{ skippedMeds.length }}</span></div>
              <div class="line"><span>Already poured in range</span><span>{{ lockedMeds.length }}</span></div>
              <div class="line warn"><span>Short on supply</span><span>{{ lowMeds.length }}</span></div>
              <div class="line"><span>Not pourable</span><span>{{ nonPourable.length }}</span></div>
              <div class="line warn" v-for="m in lowMeds" :key="m.id"><span>{{ shortName(m) }}</span><span>short {{ Math.abs(remaining(m)) }}</span></div>
              <button class="pp-btn sign" style="width:100%;margin-top:12px" @click="openSignOff" :disabled="!canPour" :title="pourBlockReason || 'Review and sign this pour'">Pour &amp; Sign</button>
              <div class="pp-hint-t" :class="{ warn: !canPour }" style="margin-top:8px">{{ pourBlockReason || pourReadyText }}</div>
            </div>
          </div>
        </div>
        <!-- ============ Awaiting a second signature (local record until the backend is wired) ============ -->
        <div v-if="pendingDcMeds.length" class="pp-dclist">
          <div class="pp-section-h">Discontinue pending</div>
          <div class="pp-dcrow pending" v-for="m in pendingDcMeds" :key="'p' + m.id">
            <div class="grow"><b>{{ m.name }}</b><div class="pp-sub">{{ m.dc.reason }}</div></div>
            <div class="pp-sub">Requested {{ fmtStamp(m.dc.at) }} by {{ m.dc.signerName || 'unknown user' }}<template v-if="m.dc.signerRole"> ({{ m.dc.signerRole }})</template> · still being poured and given until {{ counterpartOf(m.dc.signerRole) }} signs</div>
            <button v-if="canCountersign(m)" class="pp-btn sm sign" @click="openDcSign(m)">Sign off discontinue</button>
            <span v-else class="pp-tstate todo sm">Waiting on {{ counterpartOf(m.dc.signerRole) }}</span>
          </div>
        </div>

        <!-- ============ Discontinued (local record until the backend is wired) ============ -->
        <div v-if="discontinuedMeds.length" class="pp-dclist">
          <div class="pp-section-h">Discontinued</div>
          <div class="pp-dcrow" v-for="m in discontinuedMeds" :key="m.id">
            <div class="grow"><b>{{ m.name }}</b><div class="pp-sub">{{ m.dc.reason }}</div></div>
            <div class="pp-sub">Discontinued {{ fmtStamp(m.dc.at) }} · {{ m.dc.signer }} by {{ m.dc.signerName || 'unknown user' }}<template v-if="m.dc.signerRole"> ({{ m.dc.signerRole }})</template></div>
          </div>
        </div>
      </template>

      <!-- ====================== MODE: LOCKED POURS ====================== -->
      <template v-else-if="mode === 'locked'">
        <div v-if="!pours.length" class="pp-emptystate">
          <h3>No signed pours yet</h3>
          <p>Pour and sign on the Pour sheet. Signed days appear here and in Administer.</p>
          <button class="pp-btn" @click="mode = 'pour'">Go to Pour sheet</button>
        </div>
        <div v-else class="pp-stack">
          <div class="pp-pour" v-for="p in poursNewestFirst" :key="p.id">
            <header>
              <div><b>{{ fmtDay(p.days[0]) }}<template v-if="p.days.length > 1"> – {{ fmtDay(p.days[p.days.length-1]) }}</template></b>
                <span class="pp-sub"> · {{ p.days.length }} day(s) · {{ p.meds.length }} med(s)</span></div>
              <div class="pp-sub">Signed <b>{{ p.signer }}</b> by <b>{{ p.signerName || 'unknown user' }}</b><template v-if="p.signerRole"> ({{ p.signerRole }})</template><template v-if="p.signerId"> · ID {{ p.signerId }}</template> · {{ fmtStamp(p.signedAt) }}</div>
            </header>
            <div v-if="p.skipped && p.skipped.length" class="pp-sub" style="padding:8px 14px">Not poured (marked by the caregiver): {{ p.skipped.map(x => x.name).join(', ') }}</div>
            <div class="pp-pourgrid-wrap">
              <table class="pp-grid pp-pourgrid">
                <thead><tr><th>Medication</th><th v-for="d in p.days" :key="d">{{ fmtDay(d) }}</th></tr></thead>
                <tbody>
                  <tr v-for="pm in p.meds" :key="pm.id">
                    <td><div class="pp-name">{{ pm.name }}</div><div class="pp-sub">{{ pm.dose }} × {{ pm.form }} · {{ pm.times.join(', ') }}</div></td>
                    <td v-for="d in p.days" :key="d" class="cell">
                      <span v-for="t in pm.times" :key="t" class="pp-slot" :class="slotClass(slotKey(p.id, d, pm.id, t))" :title="slotTitle(slotKey(p.id, d, pm.id, t))">
                        {{ t }} <i>{{ slotMark(slotKey(p.id, d, pm.id, t)) }}</i>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <!-- ====================== MODE: ADMINISTER ====================== -->
      <template v-else>
        <div class="pp-controls">
          <div class="pp-row">
            <div class="pp-seg sm">
              <button :class="{ on: adminView === 'day' }" title="One day — today by default" @click="adminView = 'day'">Day</button>
              <button :class="{ on: adminView === 'range' }" title="History: every day in a range" @click="adminView = 'range'">History</button>
            </div>
            <template v-if="adminView === 'day'">
              <input type="date" class="pp-pill" v-model="adminDay" aria-label="Day">
              <span class="pp-pill muted">{{ fmtDay(adminDay, true) }}</span>
            </template>
            <template v-else>
              <label class="pp-lbl">From <input type="date" class="pp-pill" v-model="adminFrom"></label>
              <label class="pp-lbl">To <input type="date" class="pp-pill" v-model="adminTo"></label>
              <span class="pp-pill muted">{{ adminDays.length }} day(s) with pours</span>
            </template>
            <span class="pp-hint-t" v-if="adminView === 'day' && pouredDays.length">Days with pours: {{ pouredDays.map(d => fmtDay(d)).join(', ') }}</span>
          </div>
          <div v-if="adminView === 'range'" class="pp-stfilters pp-ofilter" role="group" aria-label="Filter history by outcome">
            <span class="pp-lbl">Show</span>
            <button v-for="f in OUTCOME_FILTERS" :key="f.key" class="pp-stfilter" :class="['of-' + f.key, { on: adminOutcome === f.key }]"
              :aria-pressed="String(adminOutcome === f.key)" :disabled="f.key !== 'all' && !outcomeCount(f.key)" @click="adminOutcome = f.key">
              {{ f.label }} <span class="cnt">{{ outcomeCount(f.key) }}</span>
            </button>
          </div>
        </div>
        <div v-if="!adminHasSlots" class="pp-emptystate">
          <h3 v-if="adminView === 'day'">No pre-poured medications for {{ fmtDay(adminDay, true) }}</h3>
          <h3 v-else-if="outcomeFiltering">Nothing “{{ outcomeLabel }}” between {{ fmtDay(adminRange[0]) }} and {{ fmtDay(adminRange[1]) }}</h3>
          <h3 v-else>No pours between {{ fmtDay(adminRange[0]) }} and {{ fmtDay(adminRange[1]) }}</h3>
          <template v-if="!outcomeFiltering">
            <p>Medications must be poured and signed off before they can be administered.</p>
            <button class="pp-btn" @click="mode = 'pour'">Go to Pour sheet</button>
          </template>
          <button v-else class="pp-btn" @click="adminOutcome = 'all'">Show everything</button>
        </div>
        <div v-else class="pp-stack">
          <template v-for="day in adminDays" :key="day">
          <div v-if="adminView === 'range'" class="pp-dayhead">{{ fmtDay(day, true) }}</div>
          <div v-if="visibleChanges(day).length" class="pp-changes">
            <div class="pp-changes-h">Changes</div>
            <div v-for="(c, i) in visibleChanges(day)" :key="c.at + i" class="pp-change">
              <span class="pp-stchip" :class="'s-' + c.status">{{ c.label || c.status }}</span>
              <b>{{ c.name }}</b> <span>{{ c.text }}</span><span v-if="c.reason"> — {{ c.reason }}</span>
              <span class="pp-sub">· {{ c.by || 'unknown' }} · {{ fmtStamp(c.at) }}</span>
            </div>
          </div>
          <div class="pp-timegroup" v-for="g in visibleGroupsFor(day)" :key="day + g.time" :class="{ closed: !isGroupOpen(g) }">
            <header role="button" tabindex="0" :aria-expanded="isGroupOpen(g)"
              :title="isGroupOpen(g) ? 'Hide the ' + g.time + ' medications' : 'Show the ' + g.time + ' medications'"
              @click="toggleGroup(g)" @keydown.enter.self.prevent="toggleGroup(g)" @keydown.space.self.prevent="toggleGroup(g)">
              <span class="pp-caret" aria-hidden="true"></span>
              <div class="grow"><b class="pp-tg-time">{{ g.time }}</b><span class="pp-sub"> · {{ g.slots.length }} med(s) · poured by {{ g.slots[0].signerName || g.slots[0].signer }}</span>
                <!-- once every dose is recorded the summary moves to the right-hand side, so it is not said twice -->
                <div v-if="pendingIn(g).length" class="pp-sub">{{ groupSummary(g) }}</div></div>
              <button v-if="pendingIn(g).length && windowOf(g).open" class="pp-btn sm light" :title="'Record giving every pre-poured ' + g.time + ' medication at once'" @click.stop="openGroupWitness(g)">
                Administer {{ pendingIn(g).length === g.slots.length ? 'all' : 'remaining' }} {{ pendingIn(g).length }} at {{ g.time }}
              </button>
              <span v-else-if="pendingIn(g).length && !(adminView === 'range' && isPastGroup(g))" class="pp-window" :title="'Pre-poured doses can be given within ' + windowHours + ' h of the scheduled time'">{{ windowOf(g).text }}</span>
              <span v-else-if="!pendingIn(g).length" class="pp-done">{{ groupSummary(g) }}</span>
            </header>
            <template v-if="isGroupOpen(g)">
            <div class="pp-slotrow" v-for="s in g.slots" :key="s.key" :class="['o-' + (admin[s.key]?.outcome || 'pending'), { held: s.held && !admin[s.key] }]">
              <div class="grow"><div class="pp-name">{{ s.name }}</div><div class="pp-sub">{{ s.dose }} × {{ s.form }}</div></div>
              <template v-if="admin[s.key]">
                <div class="pp-outcome">
                  <span class="mark">{{ OUTCOMES[admin[s.key].outcome].mark }}</span>
                  <b>{{ OUTCOMES[admin[s.key].outcome].label }}</b>
                  <span class="pp-sub">witness {{ admin[s.key].witness }} · {{ admin[s.key].actualTime }}</span>
                  <span class="pp-sub" v-if="admin[s.key].notes">· {{ admin[s.key].notes }}</span>
                </div>
                <button class="pp-btn ghost sm" @click="showSlot(s)">Details</button>
              </template>
              <span v-else-if="s.held" class="pp-tstate skip sm">{{ s.heldWhy || 'On hold' }} — do not give</span>
              <span v-else-if="isMissed(s)" class="pp-tstate miss sm">Not recorded</span>
              <span v-else class="pp-tstate todo sm">Pre-poured · not yet given</span>
            </div>
            </template>
          </div>
          </template>
        </div>
      </template>

      <!-- ====================== DISCONTINUE REQUEST / SIGN-OFF POPUP ======================
        Follows the Orders module: the reason field from DiscontinueModal.vue and the paired signature
        cards + signature pad from MedadminshortOrderTemplate.vue, drawn with pre-pour components. -->
      <div v-if="dcSign.open" class="pp-overlay" @click.self="closeDcSign">
        <div class="pp-modal" role="dialog" aria-modal="true">
          <h2>Discontinue {{ dcSign.med?.name }}<template v-if="dcSign.times"> — the {{ dcSign.times.join(', ') }} dose</template></h2>
          <p v-if="dcSign.notice" class="pp-banner">{{ dcSign.notice }}</p>
          <p v-if="dcSign.times" class="pp-banner lock">Only the {{ dcSign.times.join(', ') }} dose stops. The medication carries on at its other times.</p>
          <p class="pp-sub">Discontinuing is itself an order for {{ patientName }}. It needs a caregiver signature and a physician signature, and the medication keeps running until both are in place.</p>
          <div v-if="dcSignPendingCount" class="pp-banner lock">{{ dcSignPendingCount }} pre-poured dose(s) stay available until the second signature — they are withdrawn only when the discontinue completes.</div>

          <label class="pp-lbl" style="display:block;margin-top:10px">Reason <span class="pp-optional">(optional)</span></label>
          <textarea class="pp-pill wide" rows="3" v-model="dcSign.reason" :readonly="dcSign.stage === 'countersign'"
            placeholder="Why this medication is being discontinued"></textarea>

          <!-- One card is yours; the other is the counterpart's. Only yours is actionable, and it says so. -->
          <p class="pp-signote">{{ dcSignInstruction }}</p>
          <div class="pp-sigrow">
            <div v-for="role in ['caregiver','physician']" :key="role"
              class="pp-sigcard" :class="sigCardState(role)"
              @click="openSigPad(role)">
              <header>
                <span>{{ roleLabel(role) }}</span>
                <span class="pp-sigbadge" :class="sigCardState(role)">{{ sigBadge(role) }}</span>
              </header>
              <div v-if="dcSign[role].signature" class="pp-sigbody">
                <div class="pp-signame">{{ dcSign[role].signature }}</div>
                <div class="pp-sub">Signed {{ fmtStamp(dcSign[role].at) }}</div>
              </div>
              <div v-else-if="canSign(role)" class="pp-sigbody action">
                <button class="pp-btn sign sm" @click.stop="openSigPad(role)">Sign as {{ roleLabel(role).toLowerCase().replace(' signature','') }}</button>
              </div>
              <div v-else class="pp-sigbody empty">
                <span class="pp-sub">Not yours to sign — waiting on {{ role === 'caregiver' ? 'a caregiver' : 'a physician' }}</span>
              </div>
            </div>
          </div>
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="closeDcSign">Cancel</button>
            <button class="pp-btn sign" :disabled="!dcSignCanSubmit" @click="submitDcSign">{{ dcSignSubmitLabel }}</button>
          </div>
          <p v-if="!dcSignCanSubmit" class="pp-sub" style="text-align:right;margin-top:6px">{{ dcSignBlockedWhy }}</p>
        </div>
      </div>

      <!-- Signature pad, mirroring the Orders pad: full name in, preview, apply. -->
      <div v-if="sigPad.open" class="pp-overlay" @click.self="sigPad.open = false">
        <div class="pp-modal narrow">
          <h2>Enter your signature</h2>
          <label class="pp-lbl" style="display:block;margin-top:10px">Full name</label>
          <input class="pp-pill wide" v-model="sigPad.value" placeholder="Enter your full name" @keyup.enter="applySigPad">
          <div v-if="sigPad.value.trim()" class="pp-sigpreview">
            <div class="pp-sub">Signature preview</div>
            <div class="pp-signame">{{ sigPad.value.trim() }}</div>
          </div>
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="sigPad.open = false">Cancel</button>
            <button class="pp-btn sign" :disabled="!sigPad.value.trim()" @click="applySigPad">Apply signature</button>
          </div>
        </div>
      </div>

      <!-- ============ PATIENT SELF-MANAGED DISCONTINUE POPUP (PSP-8) ============
        Reserved for the patient path, which self-manages and does not take a second signature. Nothing in
        the caregiver flow opens it; caregivers go through the two-signature pop-up below. -->
      <div v-if="patientDc.open" class="pp-overlay" @click.self="patientDc.open = false">
        <div class="pp-modal">
          <h2>Discontinue {{ patientDc.med?.name }}</h2>
          <p class="pp-sub">Discontinuing is itself an order for {{ patientName }} — improvement, an ineffective treatment, a prescriber change, side effects, cost, or any other reason. It needs a provider signature and a nurse signature, and the medication keeps running until both are in place.</p>
          <div v-if="patientDcPendingCount" class="pp-banner lock">{{ patientDcPendingCount }} pre-poured dose(s) stay available until the second signature — they are withdrawn only when the discontinue completes.</div>
          <label class="pp-lbl" style="display:block;margin-top:10px">Reason <span class="pp-optional">(optional)</span></label>
          <textarea class="pp-pill wide" rows="3" v-model="patientDc.reason" placeholder="Why this medication is being discontinued"></textarea>
          <div class="pp-signer">
            <b>Discontinuing as</b> {{ staff.name || 'unknown user' }}<template v-if="staff.role"> · {{ staff.role }}</template><template v-if="staff.id"> · ID {{ staff.id }}</template>
            <div class="pp-sub">Self-managed medications stop on this signature alone — no countersignature is collected.</div>
          </div>
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="patientDc.open = false">Cancel</button>
            <button class="pp-btn sign" @click="confirmPatientDiscontinue">Discontinue</button>
          </div>
        </div>
      </div>

      <!-- ====================== STATUS POP-UP (Hold / New / Change / Completed) ======================
        Same questions as the stakeholder's prototype and the nursing module's HoldTimeSelector: all times
        or some, when, and why — every one required. Nothing changes until Submit. -->
      <div v-if="stDlg.open" class="pp-overlay" @click.self="stDlg.open = false">
        <div class="pp-modal" role="dialog" aria-modal="true" :aria-label="stDlg.status + ' ' + (stDlg.med ? stDlg.med.name : '')">
          <h2>{{ stDlg.status === 'Hold' ? 'Hold' : stDlg.status === 'Completed' ? 'Complete' : stDlg.status === 'New' ? 'Mark as new' : 'Change' }} {{ stDlg.med?.name }}</h2>
          <p class="pp-sub" v-if="stDlg.status === 'Hold'">A hold pauses the medication on the days you choose — it is not poured or given then — and it carries on afterwards. To stop it for good, discontinue instead.</p>
          <p class="pp-sub" v-else-if="stDlg.status === 'Completed'">The course is finished from this date: nothing is poured from then on.</p>
          <!-- a real patient's change goes to keyon (#336): a change order for the prescriber, or at once without an order -->
          <p class="pp-sub" v-else-if="stDlg.status === 'Change' && isRemoteSheet">{{ stDlg.med && stDlg.med.nonPrescription
            ? 'A change to this non-prescription medication. It applies today, recorded with its reason in History.'
            : 'A change to the order. It applies today as a change order for the prescriber to sign, recorded with its reason in History.' }}</p>
          <p class="pp-sub" v-else-if="stDlg.status === 'Change'">A change to the order. It is recorded with its date and reason; on the sample sheet it stays in this browser.</p>
          <p class="pp-sub" v-else>A medication newly added to this patient's regimen.</p>
          <div v-if="stDlg.extra && stDlg.extra.freqTo" class="pp-banner lock">Frequency: <b>{{ stDlg.extra.freqFrom }}</b> → <b>{{ stDlg.extra.freqTo }}</b></div>
          <div v-if="stDlgKeepCount" class="pp-sttimes pp-stkeep">
            <div class="pp-lbl">{{ stDlgKeepCount === 1 ? 'Which time stays?' : 'Which ' + stDlgKeepCount + ' times stay?' }}
              <span class="pp-hint-t">The {{ stDlg.med.times.length - stDlgKeepCount === 1 ? 'other stops' : 'others stop' }}.</span></div>
            <label v-for="t in stDlg.med.times" :key="t" class="pp-sttime" :class="{ on: stDlg.keep.includes(t) }">
              <input type="checkbox" :value="t" v-model="stDlg.keep" :disabled="!stDlg.keep.includes(t) && stDlg.keep.length >= stDlgKeepCount"> {{ t }}</label>
          </div>
          <p v-else-if="stDlgNewSlots" class="pp-sub">{{ stDlg.med.times.length ? 'The times already chosen stay; the' : 'The' }} {{ stDlgNewSlots === 1 ? 'new slot is' : stDlgNewSlots + ' new slots are' }} left empty for you to choose on the sheet.</p>

          <template v-if="stDlgTimes.length > 1">
            <div class="pp-stradios">
              <label><input type="radio" value="all" v-model="stDlg.scope"> {{ STATUS_VERB[stDlg.status] }} all times</label>
              <label><input type="radio" value="specific" v-model="stDlg.scope"> {{ STATUS_VERB[stDlg.status] }} specific times</label>
            </div>
            <div v-if="stDlg.scope === 'specific'" class="pp-sttimes">
              <label v-for="t in stDlgTimes" :key="t" class="pp-sttime" :class="{ on: stDlg.picked.includes(t) }"><input type="checkbox" :value="t" v-model="stDlg.picked"> {{ t }}</label>
            </div>
          </template>
          <p v-else-if="stDlgTimes.length === 1" class="pp-sub">Applies to the {{ stDlgTimes[0] }} dose.</p>

          <div class="pp-stdates">
            <label>{{ STATUS_ASKS[stDlg.status] === 'range' ? 'First day' : 'Date' }} *
              <input type="date" class="pp-pill" v-model="stDlg.start"></label>
            <label v-if="STATUS_ASKS[stDlg.status] === 'range'">Last day *
              <input type="date" class="pp-pill" v-model="stDlg.end" :min="stDlg.start"></label>
          </div>

          <div v-if="stDlg.status === 'Change' && stDlgScopeTimes.length" class="pp-stdoses">
            <div class="pp-lbl">Dose at each time</div>
            <label v-for="t in stDlgScopeTimes" :key="t">{{ t }}
              <input type="number" min="1" max="10" class="pp-pill" v-model.number="stDlg.doses[t]"> × {{ stDlg.med.form }}</label>
          </div>

          <label class="pp-lbl" style="display:block;margin-top:12px">Reason *</label>
          <textarea class="pp-pill wide" rows="2" v-model="stDlg.reason" :placeholder="stDlg.status === 'Hold' ? 'e.g. cannot swallow, procedure on Monday' : 'Why'"></textarea>
          <p v-if="stDlgWhy" class="pp-sub" style="margin-top:6px">{{ stDlgWhy }}</p>
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="stDlg.open = false">Cancel</button>
            <button class="pp-btn sign" :disabled="!!stDlgWhy" @click="submitStatus">Submit</button>
          </div>
        </div>
      </div>

      <!-- ====================== THE × ON A TIME: MOVE OR DISCONTINUE ======================
        Removing a dose without moving it changes the order, so the × asks which of the two is meant
        (review 2026-09-25). Moving needs no order; discontinuing goes to the two-signature flow. -->
      <div v-if="timeAct.open" class="pp-overlay" @click.self="timeAct.open = false">
        <div class="pp-modal narrow" role="dialog" aria-modal="true" :aria-label="'The ' + timeAct.time + ' dose of ' + (timeAct.med ? timeAct.med.name : '')">
          <h2>The {{ timeAct.time }} dose of {{ timeAct.med?.name }}</h2>
          <p class="pp-sub">Move it to another hour, or discontinue it. A dose cannot simply be dropped — that would change the order.</p>
          <div class="pp-timeact">
            <div class="pp-lbl">Change time</div>
            <div class="pp-pchips">
              <button v-for="h in palette.filter(x => !timeAct.med.times.includes(x))" :key="h" class="pp-pchip" :class="{ on: timeAct.to === h }" @click="timeAct.to = h">{{ h }}</button>
              <select class="pp-pill" v-model="timeAct.to" aria-label="Another hour">
                <option value="">Other hour…</option>
                <option v-for="h in timeActChoices.filter(x => !palette.includes(x))" :key="h" :value="h">{{ h }}</option>
              </select>
            </div>
            <button class="pp-btn" :disabled="!timeAct.to" @click="moveTime">Move to {{ timeAct.to || '…' }}</button>
          </div>
          <div class="pp-timeact dc">
            <div class="pp-lbl">Discontinue</div>
            <p class="pp-sub">Stops the {{ timeAct.time }} dose for good. Needs a caregiver and a physician signature, and creates an order for the provider.</p>
            <button class="pp-btn dcbtn" :disabled="!canDiscontinue" @click="discontinueTime">Discontinue the {{ timeAct.time }} dose</button>
          </div>
          <div class="pp-modal-actions"><button class="pp-btn ghost" @click="timeAct.open = false">Cancel</button></div>
        </div>
      </div>

      <!-- ====================== SIGN-OFF POPUP ====================== -->
      <div v-if="signOff.open" class="pp-overlay" @click.self="signOff.open = false">
        <div class="pp-modal">
          <h2>Pre-poured medications</h2>
          <p class="pp-sub">{{ rangeLabel }} · {{ palette.join(', ') }}</p>
          <div class="pp-modal-list">
            <div class="pp-modal-line skip" v-for="m in signOff.skipped" :key="'s' + m.id">
              <div class="grow"><b>{{ m.name }}</b><div class="pp-sub">{{ m.freq }}</div></div>
              <div class="num"><span class="pp-tstate skip sm">Not pouring this range</span></div>
            </div>
            <div class="pp-modal-line" v-for="c in signOff.items" :key="c.med.id" :class="{ skip: !c.days.length }">
              <div class="grow"><b>{{ c.med.name }}</b> <span v-if="c.med.suggested" class="pp-badge-auto">Auto</span> <span class="pp-scanstate" :class="{ ok: scans[c.med.id] }">{{ scans[c.med.id] ? '✓ Scanned' : 'Not scanned' }}</span> <span class="pp-scanstate" :class="{ ok: allPoured(c.med) }">{{ allPoured(c.med) ? '✓ Poured' : 'Not marked poured' }}</span><div class="pp-sub">{{ c.med.dose }} × {{ c.med.form }} · {{ c.med.times.join(', ') }}</div>
                <div v-if="c.stopped.length" class="pp-sub warn">Not poured — held or completed: {{ c.stopped.map(x => fmtDay(x.day) + ' ' + x.time).join(', ') }}</div>
                <div v-if="c.uncovered.length" class="pp-sub warn">Not poured — not enough on hand: {{ fmtDays(c.uncovered) }}</div></div>
              <div class="num">
                <template v-if="signOff.forceOverlap && c.overlap.length">{{ c.days.length + c.overlap.length }} day(s) · {{ c.unitsWithOverlap }} unit(s)</template>
                <template v-else-if="c.days.length">{{ c.days.length }} day(s) · {{ c.units }} unit(s)</template>
                <template v-else><span class="pp-hint-t">fully signed — skipped</span></template>
                <div v-if="c.overlap.length" class="pp-sub warn">{{ c.overlap.length }} day(s) overlap ({{ c.overlapSigner }})</div>
              </div>
            </div>
          </div>
          <div v-if="signOff.overlapTotal" class="pp-banner lock">
            <b>Re-pour.</b> {{ signOff.overlapTotal }} med(s) were already poured and signed in this range by {{ signOff.overlapSigners.join(', ') }}.
            <label class="chk"><input type="checkbox" v-model="signOff.forceOverlap"> Pour them again now (replaces the earlier pour; untick to leave them as they are)</label>
          </div>
          <!-- Scanning happens on each medication while pouring, not here; this only reports it. -->
          <div class="pp-sub pp-scanrow">{{ signOffScanned }} of {{ signOff.items.length }} medication(s) scanned against their bottle · {{ signOff.items.filter(c => allPoured(c.med)).length }} marked poured.</div>
          <!-- PPU-18 — the last point at which an automatic placement can be changed, so it is named
               here too rather than only on the sheet behind this pop-up. -->
          <div v-if="signOffAutoCount" class="pp-banner auto">
            <b>{{ signOffAutoCount }} of these {{ signOffAutoCount === 1 ? 'time was' : 'times were' }} filled in automatically</b>
            — marked <span class="pp-badge-auto">Auto</span> above. Signing accepts them as given at those hours.
            To change any, cancel and tap the time on that medication's row.
          </div>
          <div class="pp-sub" style="margin-top:8px">Tabs available will be reduced by the poured amount.</div>
          <div class="pp-signer">
            <b>Signing as</b> {{ staff.name || 'unknown user' }}<template v-if="staff.role"> · {{ staff.role }}</template><template v-if="staff.id"> · ID {{ staff.id }}</template>
            <div class="pp-sub">Recorded with the pour together with your typed signature and the time.</div>
          </div>
          <label class="pp-lbl" style="display:block;margin-top:14px">Caregiver signature *</label>
          <input class="pp-pill wide" v-model="signOff.signature" placeholder="Your name / initials" @keyup.enter="confirmSignOff">
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="signOff.open = false">Cancel</button>
            <button class="pp-btn sign" :disabled="!signOff.signature.trim() || !signOff.effectiveCount" @click="confirmSignOff">Sign &amp; lock {{ signOff.effectiveCount }} med(s)</button>
          </div>
        </div>
      </div>

      <!-- ====================== WITNESS POPUP (one time block at a time) ====================== -->
      <div v-if="witness.open" class="pp-overlay" @click.self="witness.open = false">
        <div class="pp-modal">
          <h2>Administer the {{ witness.time }} medications</h2>
          <p class="pp-sub">{{ fmtDay(witness.day, true) }} · {{ witness.slots.length }} pre-poured medication{{ witness.slots.length === 1 ? '' : 's' }}</p>
          <ul class="pp-wlist"><li v-for="s in witness.slots" :key="s.key">{{ s.name }} <span class="pp-sub">· {{ s.dose }} × {{ s.form }}</span></li></ul>
          <label class="pp-lbl" style="display:block;margin-top:10px">Witnessed by *</label>
          <input class="pp-pill wide" v-model="witness.name" placeholder="Enter witness name">
          <label class="pp-lbl" style="display:block;margin-top:10px">Actual time</label>
          <input type="time" class="pp-pill" v-model="witness.actualTime">
          <div class="pp-lbl" style="margin-top:12px">What happened *</div>
          <div class="pp-outcomes">
            <button class="pp-out taken" :class="{ on: witness.outcome === 'taken' }" title="Every medication in this block was taken" @click="witness.outcome = 'taken'">✔ All taken</button>
            <button class="pp-out except" :class="{ on: witness.outcome === 'except' }" title="Taken, except the ones you mark below" @click="witness.outcome = 'except'">✔ Except for…</button>
            <button class="pp-out refused" :class="{ on: witness.outcome === 'refused' }" title="The patient refused this block" @click="witness.outcome = 'refused'">✘ All refused</button>
          </div>
          <div v-if="witness.outcome === 'except'" class="pp-except">
            <label class="pp-lbl" style="display:block">Notes * <span class="pp-hint-t">(what was not taken, or what happened)</span></label>
            <textarea class="pp-pill wide" rows="2" v-model="witness.notes" placeholder="e.g. spat out the small blue pill"></textarea>
            <label class="pp-lbl" style="display:block;margin-top:10px">Medication not taken <span class="pp-hint-t">(optional — only if you can tell which one)</span></label>
            <select class="pp-pill wide" v-model="witness.exceptedKey">
              <option value="">Not sure / not identified</option>
              <option v-for="s in witness.slots" :key="s.key" :value="s.key">{{ s.name }}</option>
            </select>
            <div class="pp-sub" style="margin-top:6px">{{ witness.exceptedKey ? 'Only that medication is recorded as "Except for"; the others as taken.' : 'All ' + witness.slots.length + ' medications in this block are recorded as "Except for" with your note.' }}</div>
          </div>
          <div class="pp-modal-actions">
            <button class="pp-btn ghost" @click="witness.open = false">Cancel</button>
            <button class="pp-btn sign" :disabled="!witnessValid" :title="witnessValid ? '' : 'Witness name is required; Except for needs a note'" @click="confirmWitness">Record {{ witness.slots.length }} medication{{ witness.slots.length === 1 ? '' : 's' }}</button>
          </div>
        </div>
      </div>

      <!-- ====================== SLOT DETAILS (locked) ====================== -->
      <div v-if="detail.open" class="pp-overlay" @click.self="detail.open = false">
        <div class="pp-modal narrow">
          <h2>{{ detail.slot.name }}</h2>
          <p class="pp-sub">Scheduled {{ detail.slot.time }} · {{ fmtDay(detail.slot.day, true) }} · poured by {{ detail.slot.signerName || detail.slot.signer }}</p>
          <div class="pp-outcome big" :class="'o-' + detail.rec.outcome">
            <span class="mark">{{ OUTCOMES[detail.rec.outcome].mark }}</span> <b>{{ OUTCOMES[detail.rec.outcome].label }}</b>
          </div>
          <div class="line"><span>Witnessed by</span><b>{{ detail.rec.witness }}</b></div>
          <div class="line"><span>Actual time</span><b>{{ detail.rec.actualTime }}</b></div>
          <div class="line" v-if="detail.rec.notes"><span>Notes</span><b>{{ detail.rec.notes }}</b></div>
          <div class="line"><span>Recorded</span><b>{{ fmtStamp(detail.rec.at) }}</b></div>
          <div class="pp-modal-actions"><button class="pp-btn" @click="detail.open = false">OK</button></div>
        </div>
      </div>

      <!-- shared nursing add-medication form -->
      <AddMedicationForm
        :show="addMed.open"
        :pastProvloaded="addMed.provLoaded"
        :pastProvar="addMed.providers"
        :isEditMedication="false"
        :isAddNewMed="true"
        :ifStatusIsChange="false"
        :editFormdata="addMed.prefill"
        :lockAdministrationToOn="true"
        :isSaving="addMed.saving"
        :preferredTimes="paletteAs24h"
        :showPrescriptionType="true"
        cancelLabel="Return to scheduling"
        @close="closeAddMed"
        @save="saveNewMed"
        @loadprov="loadProviders"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import Swal from 'sweetalert2';
import { usePrivilegesStore } from '@/stores/privileges';
import { PRIVILEGES } from '@/constants/privileges';
import { CURR_API } from '@/constants/apis';
import { assertPrivilegeOrAlert } from '@/utils/privilegeGate';
import { PREPOUR_ADMIN_WINDOW_HOURS, PREPOUR_MAX_RANGE_DAYS } from '@/constants/prepour';
import { resolveKeyonPrivilegeIdentity } from '@/api/privilegesApi';
import AddMedicationForm from '@/components/private-layouts/currents/AddMedicationForm.vue';
import PrePourMedRecord from './PrePourMedRecord.vue';
import * as prepourApi from '@/api/prepourApi';
import { medRecordCard, isPrepourRecord, isNonPrescriptionRecord, derivedStatusOf, courseEndedOn } from '@/utils/prepourMedRecord';
import { holdsFromServer, changesFromEvents, fillTimes, keyonTimesOf, to24, keyonSheetHours, movedKeyonTimes } from '@/utils/prepourStatusRequests';

const store = useStore();
const route = useRoute();
const router = useRouter();
const privileges = usePrivilegesStore();

/**
 * The layout/times switcher is always on while this page is a prototype under review.
 *
 * It used to be gated on `process.env.NODE_ENV !== 'production'`, which meant a local
 * `npm run serve` only — every deployed environment is a production build, so on staging the strip
 * never rendered and reviewers had to be sent hand-written URLs, where a typo falls back to A in
 * silence. It exposes nothing privileged: the strip only rewrites query params anyone could type
 * by hand, plus a reset of this browser's own localStorage draft.
 *
 * This goes away with the prototype. When the chosen layout is settled, the losing variants and
 * this strip are deleted together.
 */
const canPrepour = computed(() => privileges.effectiveGranted(PRIVILEGES.MEDICATION_PREPOUR));
/* A discontinue needs a second signature from a physician, who pours nothing and so holds no
   medication_prepour — gating the whole page on 24 left the countersigner unable to reach the request
   waiting on them. Discontinuing (22) therefore opens the page read-only; pouring still needs 24. */
const canDiscontinue = computed(() => privileges.effectiveGranted(PRIVILEGES.MEDICATION_DISCONTINUE));
const canViewPrepour = computed(() => canPrepour.value || canDiscontinue.value);

/* ---- app context: patient ---- */
const allData = computed(() => store.getters?.['login/getAllData'] || {});
const patient = computed(() => allData.value?.patient || {});
const hasPatient = computed(() => !!(patient.value?.externId && patient.value?.externId2));
/* The patient's name and DOB come off the chart in view (allData.patient), never off login/getUser.
   Main's getUser was {...user, ...patient}, so the chart's fields won; the port's getUser is the
   signed-in staff member alone, and reading it first put the nurse's own DOB under the patient's
   name. With no chart open (sample mode) there is no DOB to show. */
const patientName = computed(() => {
  const p = patient.value;
  return [p.firstName || p.first_name || p.first, p.lastName || p.lastname || p.last]
    .filter(Boolean).join(' ') || 'No patient selected';
});
const patientDob = computed(() => patient.value?.dob || patient.value?.date_of_birth || '');
const patientKey = computed(() => hasPatient.value ? `${patient.value.externId2}-${patient.value.externId}` : 'sample');
/* the logged-in staff member (the person who pours / signs), not the patient being charted.
   Role comes from keyon GetAccountType (account_user.account_type) — allData carries no role field and
   `administrator` is the Postgres admin flag, not an account type (same source as useSchedulingAccess). */
const staffRole = ref('');
async function loadStaffRole(){
  staffRole.value = '';
  const { accountnumber, unique_ID } = resolveKeyonPrivilegeIdentity(allData.value);
  if (!accountnumber || !unique_ID) return;
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', { Account: { API_Meth: 'GetAccountType', accountnumber, subaccountnumber: unique_ID } });
    staffRole.value = String(res.data?.providerAssignment?.accounttype || '').trim();
  } catch (e) { staffRole.value = ''; }
}
const staff = computed(() => {
  const a = allData.value || {}; const m = a.manager || {}; const u = a.user || {};
  const name = m.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '';
  return { name, id: String(m.externid || m.externId || ''), account: String(m.externid2 || m.externId2 || ''), role: staffRole.value };
});
function signerLabel(p){
  const who = [p.signerName, p.signerRole].filter(Boolean).join(', ');
  return p.signer + (who ? ` (${who}${p.signerId ? ` · ID ${p.signerId}` : ''})` : '');
}

/* ---- frequency catalog (CONTEXT.md) + dose mapping ---- */
const FREQS = ['daily','2 times daily','3 times daily','4 times daily','every other day','at bedtime',
  'every hour','every 2 hours','every 3 hours','every 4 hours','every 6 hours','every 8 hours',
  'every 12 hours','every 24 hours','before every meal','after every meal','1 hour after every meal',
  'every morning','once a week','as directed'];
const DOSES = {'daily':1,'2 times daily':2,'3 times daily':3,'4 times daily':4,'every other day':1,
  'at bedtime':1,'every hour':24,'every 2 hours':12,'every 3 hours':8,'every 4 hours':6,'every 6 hours':4,
  'every 8 hours':3,'every 12 hours':2,'every 24 hours':1,'before every meal':3,'after every meal':3,
  '1 hour after every meal':3,'every morning':1,'once a week':1,'as directed':0};
function doseCount(freq){
  if (DOSES[freq] != null) return DOSES[freq];
  const m = /(\d+)\s*times?\s*(daily|a day)/i.exec(freq || '');
  return m ? Number(m[1]) : 1;
}
/* Doses a day for a frequency the catalogue knows, or null for one that does not say how many — free text
   such as "with breakfast and supper" — whose count is then the medication's own schedule. This is the
   server's rule (Pourability::dosesPerDay), which refuses any other count (prepour_times_mismatch); main's
   doseCount answers 1 for such a line, which held it to one time the server never accepts. */
function knownDoseCount(freq){
  const t = String(freq || '').trim();
  if (DOSES[t.toLowerCase()] != null) return DOSES[t.toLowerCase()];
  const m = /^(\d+)\s*times?\s*(daily|a day)$/i.exec(t);
  return m ? Number(m[1]) : null;
}

/* ---- pourability (keyed on dosage form / route) ---- */
const NON_POURABLE = /cream|ointment|lotion|gel|solution|suspension|inject|ampule|vial|syringe|patch|supposit|spray|drop|inhal|nebul|foam|paste|film|liquid|elixir|tincture|enema/i;
const NON_ORAL = /topical|transdermal|intraven|intramuscular|subcut|\biv\b|\bim\b|\bsq\b|nasal|optic|otic|rect|vagin|inhal|neb/i;
function pourableOf(form, rte){
  if (NON_POURABLE.test(form || '')) return false;
  if (NON_ORAL.test(rte || '')) return false;
  return true;
}
function isPrn(freq, prnFlag){
  if (prnFlag === 1 || prnFlag === '1' || prnFlag === true) return true;
  return /prn|as needed|as directed/i.test(freq || '');
}

/* ---- dates ---- */
function ymd(d){ const z = new Date(d); z.setMinutes(z.getMinutes() - z.getTimezoneOffset()); return z.toISOString().slice(0,10); }
function fmtDay(s, long){
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString(undefined, long ? { weekday:'long', month:'long', day:'numeric', year:'numeric' } : { month:'short', day:'numeric' });
}
function fmtStamp(iso){ return new Date(iso).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }); }
function addDays(s, n){ const d = new Date(s + 'T00:00:00'); d.setDate(d.getDate() + n); return ymd(d); }
/* a ticking clock: `today` rolls over at midnight and the administration window follows the wall clock */
const now = ref(new Date());
let clock = null;
const today = computed(() => ymd(now.value));

/* ---- range + time palette ---- */
/* Pouring covers a chosen range of days: pre-pouring an organizer days ahead is the point of the module.
   "Today only" is an ADMINISTRATION rule (stakeholder, 2026-09-15) and lives in the administration window
   below (PREPOUR_ADMIN_WINDOW_HOURS) - a dose may be given only near its own slot time, so past and future
   slots stay read-only. It was never a restriction on which days may be poured. */
// picked = [start, end] from the range picker (PoC "Select Date Range"); multi = [] of Dates (PoC "Select Non-Consecutive Days")
const DEFAULT_PALETTE = ['7am','11am','8pm'];
function defaultRange(){ return { picked: [new Date(), new Date(Date.now() + 6 * 864e5)], multi: [] }; }
const range = reactive(defaultRange());
const rangeDays = computed(() => {
  if (range.multi && range.multi.length) return [...new Set(range.multi.map(d => ymd(d)))].sort();
  const p = range.picked;
  if (!p || !p[0]) return [];
  const start = ymd(p[0]), end = ymd(p[1] || p[0]);
  const out = []; for (let d = start; d <= end && out.length < 60; d = addDays(d, 1)) out.push(d);
  return out;
});
/* PPU-8 — how far ahead a pour may reach. Bound to the pickers' :max-date so the days beyond it cannot
   be picked at all, and checked again below because the non-consecutive picker and a restored draft can
   both produce a day the picker never offered. Client-side only for now; see the constant's note. */
const maxPourDay = computed(() => addDays(today.value, PREPOUR_MAX_RANGE_DAYS - 1));
const maxPourDate = computed(() => new Date(maxPourDay.value + 'T00:00:00'));
const overMaxDays = computed(() => rangeDays.value.some(d => d > maxPourDay.value));
const rangeLabel = computed(() => {
  const d = rangeDays.value;
  if (!d.length) return 'no days selected';
  if (range.multi && range.multi.length) return d.map(x => fmtDay(x)).join(', ');
  return `${fmtDay(d[0])} – ${fmtDay(d[d.length-1])}`;
});
const blocks = [
  { t:'6am - 12pm', hrs:['6am','7am','8am','9am','10am','11am'] },
  { t:'12pm - 6pm', hrs:['12pm','1pm','2pm','3pm','4pm','5pm'] },
  { t:'6pm - 12am', hrs:['6pm','7pm','8pm','9pm','10pm','11pm'] },
  { t:'12am - 6am', hrs:['12am','1am','2am','3am','4am','5am'] },
];
const HOUR_ORDER = blocks.flatMap(b => b.hrs);
/* Where a time sorts in the sheet's day, 6am first and 5am last. A palette hour sorts as its tile does; a scheduled time
   that is not on the hour (a 9:30pm dose, PP-OM-01) sorts by its minutes in the same day — HOUR_ORDER.indexOf put it
   first, before 6am. */
function hourRank(t){
  const hm = to24(t);
  if (!hm) return Number.MAX_SAFE_INTEGER;
  return (Number(hm.slice(0, 2)) * 60 + Number(hm.slice(3, 5)) + 1440 - 360) % 1440;
}
const byHour = (a, b) => hourRank(a) - hourRank(b);
const NIGHT = blocks[3].hrs;
const palette = ref([...DEFAULT_PALETTE]);

/* PPU-2 — the times panel is furniture for setup, not something to live with. Once the hours are
   chosen it collapses to a summary line and reopens on a tap.
   It is never auto-hidden on a count of hours: the review raised "what if the user wants a fourth
   time and the panel has already gone", and the answer taken was that only the user closes it. */
const timesOpen = ref(!palette.value.length);
function toggleTimesPanel(){ timesOpen.value = !timesOpen.value; }
watch(() => palette.value.length, (n, was) => {
  if (!n) { timesOpen.value = true; return; }
  // A new hour gives medications somewhere to go: Auto rows re-spread and short rows are topped up.
  // Removing one only re-spreads Auto rows; the caregiver's own rows just lose that hour (togglePalette).
  if (draftReady) suggestPlacements({ topUp: n > (was || 0) });
});

/* ---- variant switching ----
   Declared here rather than further down because the sort control below watches `variant` to work out
   which orderings the layout on screen can honour. */
const VNAMES = { A:'Spreadsheet grid', B:'Time-cup board', C:'Per-med cards + rail', D:'Compact checklist', E:'Compact + details' };
const KEYS = ['A','B','C','D','E'];
const variant = computed(() => KEYS.includes(route.query.variant) ? route.query.variant : 'A');
function setVariant(v){ router.replace({ query: { ...route.query, variant: v } }); }
function cycle(d){ const i = KEYS.indexOf(variant.value); setVariant(KEYS[(i + d + KEYS.length) % KEYS.length]); }

/* PPU-5 — the review found two habits and both are real. Joe fills one medication across all its
   slots and moves on; Laura fills everything due at 7am, then everything due at 8pm. "prn" does not
   reorder the schedule at all — it lifts the as-needed list above it, which is the other thing a
   caregiver goes looking for. */
const SORTS = [
  { key: 'time', label: 'By time' },
  { key: 'med', label: 'By medication' },
  { key: 'prn', label: 'By PRN' },
  { key: 'rx', label: 'By prescription' },
];
/* Not every sort means something in every layout. The time-cup board IS the time ordering — its cups
   are the hours — so offering "By time" there is a button that visibly does nothing, which is exactly
   what the review caught. The control therefore offers only what the layout on screen can act on. */
const SORTS_BY_VARIANT = { B: ['med'] };
const availableSorts = computed(() => {
  const allowed = SORTS_BY_VARIANT[variant.value];
  return allowed ? SORTS.filter(o => allowed.includes(o.key)) : SORTS;
});
const sortBy = ref('time');
/* Switching to a layout that cannot honour the current sort falls back to its first option rather
   than leaving a selection highlighted that is not in effect. */
watch(availableSorts, opts => { if (!opts.some(o => o.key === sortBy.value)) sortBy.value = opts[0].key; });
const prnFirst = computed(() => sortBy.value === 'prn');

/* An hour as the pour sheet writes it ("7am", "12pm") turned into the "HH:MM" the medication form's
   time inputs speak. Returns '' for anything unparseable rather than a bad time. */
function hourTo24(h){
  // to24's rule, which reads a scheduled "9:30pm" with its minutes as well as a palette hour
  return /(am|pm)$/i.test(String(h || '').trim()) ? to24(h) : '';
}
function hourFrom24(t){
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || '').trim());
  if (!m) return '';
  const n = Number(m[1]);
  if (n > 23 || Number(m[2]) > 59) return '';
  const h12 = n % 12 === 0 ? 12 : n % 12;
  const label = `${h12}${n < 12 ? 'am' : 'pm'}`;
  return HOUR_ORDER.includes(label) ? label : '';
}
/* PPU-1 — what the Add Medication form receives as its default administration times. */
const paletteAs24h = computed(() => palette.value.map(hourTo24).filter(Boolean));

/* mobile date control */
const datesOpen = ref(false);
const dateMode = ref('range');
watch(() => range.multi && range.multi.length, n => { if (n) dateMode.value = 'multi'; });
/* quick-pick time presets: the hour for each is adjustable, the palette stays the source of truth */
const PRESETS = [
  { key:'morning', label:'Morning', hours:['6am','7am','8am','9am','10am'] },
  { key:'midday',  label:'Midday',  hours:['11am','12pm','1pm','2pm'] },
  { key:'evening', label:'Evening', hours:['4pm','5pm','6pm','7pm'] },
  { key:'bedtime', label:'Bedtime', hours:['8pm','9pm','10pm','11pm'] },
];
const presetHour = reactive({ morning:'8am', midday:'12pm', evening:'6pm', bedtime:'9pm' });
function togglePreset(k){ togglePalette(presetHour[k]); }
function setPresetHour(k, h){
  const old = presetHour[k];
  if (palette.value.includes(old)) { togglePalette(old); presetHour[k] = h; if (!palette.value.includes(h)) togglePalette(h); }
  else presetHour[k] = h;
}
const customHour = ref('');
function addCustomHour(){ if (customHour.value && !palette.value.includes(customHour.value)) togglePalette(customHour.value); customHour.value = ''; }
function togglePalette(h){
  const i = palette.value.indexOf(h);
  // an hour a medication is due at stays: dropping it would drop that dose from the pour
  const due = i >= 0 ? meds.value.filter(m => isKeyonTimed(m) && m.times.includes(h) && !isFullyLocked(m)) : [];
  if (due.length) {
    Swal.fire({ icon: 'info', title: `${h} stays`, text: `${due.map(shortName).join(', ')} ${due.length === 1 ? 'is' : 'are'} due at ${h}. Move ${due.length === 1 ? 'its' : 'their'} time first.` });
    return;
  }
  if (i >= 0) {
    palette.value.splice(i,1);
    // subtractive rule: an hour removed from the palette leaves every unlocked med; nothing is auto-added
    meds.value.forEach(m => { if (!isFullyLocked(m)) { const j = m.times.indexOf(h); if (j >= 0) m.times.splice(j, 1); } });
  } else palette.value.push(h);
  palette.value.sort(byHour);
}

/* ---- meds from app context (localStorage 'medications'), else sample ---- */
const SAMPLE = [
  { name:'Metformin HCl 850 mg', form:'Tablet', route:'Oral', status:'Active', freq:'2 times daily', dose:1, onhand:10 },
  { name:'Olmesartan Medoxomil 20 mg', form:'Tablet', route:'Oral', status:'Active', freq:'daily', dose:1, onhand:88 },
  { name:'Aspirin EC 81 mg', form:'Tablet', route:'Oral', status:'Active', freq:'daily', dose:1, onhand:48 },
  { name:'Furosemide 40 mg', form:'Tablet', route:'Oral', status:'Change', freq:'2 times daily', dose:2, onhand:0 },
  { name:'Clobetasol Propionate 0.05%', form:'Cream', route:'Topical', status:'Active', freq:'2 times daily', dose:1, onhand:60 },
  { name:'Acetaminophen 500 mg', form:'Tablet', route:'Oral', status:'Active', freq:'as directed', dose:1, onhand:24 },
];
const usingSample = ref(false);
const loading = ref(false);
const loadError = ref('');
const meds = ref([]);
const pourMarks = reactive({});   // pour marks (see "pour marks" below): medId -> { time: true }

/* The medication status vocabulary. Anything outside it is not a medication status and must not be
   shown as one — see medStatusOf below. */
const STATUS_MAP = { 'active':'Active','active-pending':'Active','discontinue':'Discontinue','discontinued':'Discontinue',
  'hold':'Hold','new':'New','pending':'Pending','change':'Change','completed':'Completed','partial':'Partial' };
function normStatus(s){ return STATUS_MAP[String(s || '').toLowerCase()] || null; }
/**
 * What the status chip shows for one medication.
 *
 * keyon returns `status: 'scheduled'` for every med on an administration schedule, which says nothing
 * about the medication, so the order's status was used instead. But `orderstatus` carries ORDER
 * lifecycle words — it comes back as 'Archived' for ordinary active meds, meaning the order record was
 * archived, not the medication. That was rendering as an "Archived" chip on medications being poured
 * right now, which reads as though they had been withdrawn.
 *
 * So the order's status is consulted only when it is itself a medication status. Anything else falls
 * back to Active, which is what these rows are: loadMeds already drops discontinued, inactive and
 * completed records before they reach here.
 */
function medStatusOf(r){
  const own = String(r.status || '');
  if (/scheduled|pending/i.test(own) && r.orderstatus) {
    const fromOrder = normStatus(r.orderstatus);
    if (fromOrder) return fromOrder;
  }
  return normStatus(own) || 'Active';
}
function guessForm(r){
  const explicit = r.dosageForm || r.dosage_form || r.viaType || '';
  if (explicit && !/^\d+$/.test(String(explicit))) return explicit;
  const n = String(r.medName || r.medname || '');
  const hit = /cream|ointment|lotion|gel|solution|suspension|inject|patch|supposit|spray|drops?|inhaler|nebul|capsule|caplet|tablet|tab\b/i.exec(n);
  return hit ? hit[0].replace(/^\w/, c => c.toUpperCase()) : (r.med_doseuom || 'Tablet');
}
function normFreq(f){
  const t = String(f || '').trim();
  const abbr = { qd:'daily', daily:'daily', bid:'2 times daily', tid:'3 times daily', qid:'4 times daily', qod:'every other day',
    qhs:'at bedtime', hs:'at bedtime', qam:'every morning', q4h:'every 4 hours', q6h:'every 6 hours', q8h:'every 8 hours', q12h:'every 12 hours',
    weekly:'once a week', prn:'as directed' };
  if (abbr[t.toLowerCase()]) return abbr[t.toLowerCase()];
  const m = /^(\d)\s*times?\s*(daily|a day)$/i.exec(t);
  if (m) return m[1] === '1' ? 'daily' : `${m[1]} times daily`;
  return FREQS.includes(t) ? t : (t || 'daily');
}
const COUNT_UOM = /^(tab|tablet|tablets|cap|capsule|capsules|each|ea|unit|units|pill|pills|count)$/i;
function tabletsPerDose(r){
  if (r.dose != null) return Number(r.dose) || 1;                       // sample roster
  const uom = String(r.med_doseuom || r.unitType || '').trim();
  return COUNT_UOM.test(uom) ? (Number(r.med_amount) || 1) : 1;
}
function mapRecord(r, idx){
  const form = guessForm(r);
  const rte  = r.route || r.med_route || 'Oral';
  const freq = normFreq(r.frequency || r.med_frequency);
  // normFreq shows a medication with no frequency as daily; the server counts its schedule instead (maxTimes).
  const freqUnstated = String(r.frequency || r.med_frequency || '').trim() === '';
  const prn  = isPrn(freq, r.prn);
  const pourable = pourableOf(form, rte);
  const model = {
    id: String(r.medId || r.medentryid || ('m'+idx)),
    name: r.medName || r.medname || r.shorthand || ('Medication '+(idx+1)),
    form, route: rte, status: derivedStatusOf(r) || medStatusOf(r),
    // med_amount is the dose "Amount" in med_doseuom. Only when that unit is a count (tablet/capsule/each) is it a
    // tablet count; a strength (mg/mcg/%) says nothing about tablets per administration, so the sheet assumes 1.
    // Backend gap: no tablets-per-dose field exists (see prepour-backend-gaps doc). tabletqnty is the bottle total.
    freq, freqUnstated, dose: tabletsPerDose(r),
    ndc: String(r.ndcNumber || r.ndcnumber || '').trim(),
    onhand: Number(r.available != null && r.available !== '' ? r.available : (r.total != null ? r.total : 0)) || 0,
    // times start empty here; suggestPlacements fills them from the frequency once hours are chosen,
    // and marks them `suggested` so the sheet can say they were not the caregiver's own choice yet
    prn, pourable, times: [], skip: false, picking: false, repour: false, dc: null, suggested: false,
    // The medication's own dose times, in the sheet's hour words. A pour is given at these times and only these
    // (PP-OM-01): the server refuses any other hour and counts every one of them, so when the medication has any they
    // are its hours - placeKeyonTimes puts them on the row and in the palette, whatever the frequency says. A 7am cup
    // for an 08:00 dose was a second dose beside the scheduled one.
    keyonHours: keyonSheetHours(r),
    nonPrescription: isNonPrescriptionRecord(r),
    ...STATUS_DEFAULTS(),
    // keyon's own record: a hold or change sent to nursing's endpoints names this row, its order and prescriber
    keyon: r,
    // what MORE shows
    record: {
      ndc: r.ndcNumber || r.ndcnumber || '', rxNorm: r.rxNorms || r.rxnorns || '', route: rte, form,
      quantity: r.tabletQuantity || r.tabletqnty || r.total || '', refills: r.refills ?? '',
      ...medRecordCard(r),
    },
  };
  return model;
}
/* local overrides until the backend is wired: on-hand edits, discontinue records and status records */
function applyLocalOverrides(list){
  const pk = patientKey.value;
  const ov = prepourApi.getOnHand(pk), dcs = prepourApi.getDiscontinues(pk), sts = prepourApi.getStatuses(pk);
  list.forEach(m => {
    if (ov[m.id] != null) m.onhand = ov[m.id];
    const dc = dcs[m.id];
    // A discontinue of one dose (the × on a time) leaves the medication running at its other times.
    // Records written before the two-signature flow existed carry no state and are already complete.
    if (dc && dc.times && dc.times.length) {
      if (dc.state === 'pending') { m.dc = dc; m.status = 'DC-pending'; } else m.dcTimes = [...dc.times];
    } else if (dc) { m.dc = dc; m.status = dc.state === 'pending' ? 'DC-pending' : 'Discontinue'; }
    if (sts[m.id]) Object.assign(m, { statusRec: sts[m.id].rec || null, holds: sts[m.id].holds || [], timeDoses: sts[m.id].timeDoses || {} });
  });
  return list;
}
function sampleMeds(){
  usingSample.value = true;
  return applyLocalOverrides(SAMPLE.map((s, i) => {
    const prn = isPrn(s.freq); const pourable = pourableOf(s.form, s.route);
    return { id:'s'+i, ...s, prn, pourable, times: [], skip: false, picking: false, repour: false, dc: null, suggested: false,
      ...STATUS_DEFAULTS(), record: { route: s.route, form: s.form } };
  }));
}
async function loadMeds(){
  draftReady = false;
  if (!canViewPrepour.value) { meds.value = []; return; }   // fail closed: no fetch (and no ePHI audit row) without a privilege
  if (!hasPatient.value) { meds.value = sampleMeds(); restoreDraft(); return; }
  usingSample.value = false; loading.value = true; loadError.value = '';
  const content = { MedicationAdmin: {
    API_Meth: 'GetPatientMeds', pid: patient.value.externId, accountId: patient.value.externId2,
    providerid: '123456789', proflicensenumber: '609382', provsubaccount: allData.value?.manager?.externid || '' } };
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', content);
    const recs = Array.isArray(res.data?.records) ? res.data.records : [];
    const active = recs.filter(r => isPrepourRecord(r) && !/discontinue|inactive|completed/i.test(String(r.status || '')));
    meds.value = applyLocalOverrides(active.map(mapRecord));
    await refreshKeyonStatus();
    restoreDraft();
  } catch (e) {
    loadError.value = 'Could not load medications: ' + (e?.response?.data?.message || e.message || 'unknown error');
    meds.value = [];
  } finally { loading.value = false; }
}

/* A real patient's holds and History are keyon's (#336): holds from the slots nursing holds
   (GetPrePourHolds), so a hold placed in Medication Administration shows here too, and History from
   med_status_events (GetMedStatusEvents), which the endpoints write whoever acts. */
async function refreshKeyonStatus(){
  if (!prepourApi.isRemote(patientKey.value)) return;
  try {
    const [holds, events] = await Promise.all([prepourApi.fetchHolds(pourCtx.value), prepourApi.fetchStatusEvents(pourCtx.value)]);
    const byMed = holdsFromServer(holds, meds.value);
    meds.value.forEach(m => { m.holds = byMed[m.id] || []; });
    // keyon's History, with the discontinues this browser still records (prepourApi.getChanges)
    changes.value = [...changesFromEvents(events), ...prepourApi.getChanges(patientKey.value)]
      .sort((a, b) => new Date(a.at) - new Date(b.at));
  } catch (e) {
    loadError.value = 'Could not load the holds and History: ' + e.message;
  }
}

/* ---- browser-only persistence: the unsigned draft (per patient). Records that belong to the patient
   go through @/api/prepourApi, so every layout reads and writes them the same way. ---- */
function lsKey(k){ return `ppProto:${k}:${patientKey.value}`; }
function lsGet(k, dflt){ try { const v = localStorage.getItem(lsKey(k)); return v ? JSON.parse(v) : dflt; } catch (e) { return dflt; } }
function lsSet(k, v){ try { localStorage.setItem(lsKey(k), JSON.stringify(v)); } catch (e) { /* quota / private mode */ } }

/* ---- signed pours (the LOCK) ---- */
// pour = { id, days:[ymd], signer, signedAt, meds:[{ id, name, dose, form, times:[] }] }
const pours = ref([]);
// pours signed before the re-pour fix could repeat a day in `days`; refreshPours collapses them on the way in
/* Who is asking and for whom, for the pre-pour records keyon keeps (prepourApi). */
const pourCtx = computed(() => ({ account: patient.value.externId2, patient: patient.value.externId, actorId: staff.value.id }));
const poursError = ref('');
/* A real patient's pours come from keyon; the sample sheet's from this browser. */
async function refreshPours(){
  poursError.value = '';
  try {
    const list = await prepourApi.fetchPours(patientKey.value, pourCtx.value);
    pours.value = prepourApi.isRemote(patientKey.value) ? list : list.map(p => ({ ...p, days: [...new Set(p.days)].sort() }));
    admin.value = await prepourApi.fetchAdministrations(patientKey.value, pourCtx.value, pours.value);
  } catch (e) {
    pours.value = []; admin.value = {};
    poursError.value = 'Could not load the signed pours: ' + e.message;
  }
  if (pouredDays.value.length && !pouredDays.value.includes(today.value)) adminDay.value = pouredDays.value[0];
}
const poursNewestFirst = computed(() => [...pours.value].sort((a, b) => b.signedAt.localeCompare(a.signedAt)));
function lockedDaysFor(medId){
  const out = {};
  pours.value.forEach(p => { if (p.meds.some(pm => pm.id === medId)) p.days.forEach(d => { out[d] = p; }); });
  return out; // day -> pour
}
/* the signed pours covering this med inside the selected range: { pour, signer, times, days, through } */
function lockInfo(m){
  if (!m.pourable || m.prn) return null;
  const locked = lockedDaysFor(m.id);
  const days = rangeDays.value.filter(d => locked[d]);
  if (!days.length) return null;
  const through = days[days.length - 1], p = locked[through];
  const pm = p.meds.find(x => x.id === m.id);
  return { pour: p, signer: signerLabel(p), times: pm ? pm.times : [], days, through };
}
/* Days already signed are read-only; a re-pour deliberately takes them back and clears them at sign-off
   through the overlap-confirm. Unsigned days in the range stay pourable either way. */
function editableDays(m){
  const info = lockInfo(m);
  if (!info) return rangeDays.value;
  if (m.repour) return rangeDays.value;
  const locked = lockedDaysFor(m.id);
  return rangeDays.value.filter(d => !locked[d]);
}
function overlapDays(m){
  if (!m.repour) return [];
  const locked = lockedDaysFor(m.id);
  return rangeDays.value.filter(d => locked[d]);
}
// locked = every day in the range is already signed, and the med has not been reopened with "Re-pour"
function isFullyLocked(m){ return !!lockInfo(m) && !m.repour && rangeDays.value.length > 0 && editableDays(m).length === 0; }
const lockedMeds = computed(() => scheduled.value.filter(m => isFullyLocked(m)));
/* Some, not all, of the range is already signed for this med. The sheet only pours the other days,
   so it has to say which days are taken — otherwise the row looks like a fresh pour of the whole range. */
function isPartlyLocked(m){ return !!lockInfo(m) && !m.repour && !isFullyLocked(m); }
const partlyLockedMeds = computed(() => scheduled.value.filter(m => isPartlyLocked(m)));
function lockNote(m){
  const info = lockInfo(m);
  return 'Already pre-poured for ' + info.days.map(d => fmtDay(d)).join(', ') + ' · signed by ' + info.signer
    + ' — this pour covers ' + editableDays(m).map(d => fmtDay(d)).join(', ') + ' only';
}
function startRepour(m){ m.repour = true; m.times = []; m.skip = false; m.picking = false; m.suggested = false; suggestPlacements(); }
function cancelRepour(m){ m.repour = false; m.times = []; m.picking = false; m.suggested = false; }

/* ---- supply math (local only) ---- */
function needed(m){ return isFullyLocked(m) ? 0 : unitsOver(m, editableDays(m)); }
/* Units a med needs on a day: its dose at each chosen time, less doses held or completed that day. Times
   not chosen yet still count at the standard dose, so a short row still shows what it will need. */
function unitsOn(m, day){
  const n = maxTimes(m), dose = m.dose || 1;
  let u = 0;
  m.times.forEach(t => { if (!isStoppedSlot(m, day, t)) u += doseAt(m, t); });
  if (m.times.length < n && !isStoppedSlot(m, day, null)) u += (n - m.times.length) * dose;
  return u;
}
function unitsOver(m, days){ return days.reduce((n, d) => n + unitsOn(m, d), 0); }
function remaining(m){ return (m.onhand || 0) - needed(m); }
function isLow(m){ return m.pourable && !m.prn && remaining(m) < 0; }
function supplyPct(m){ return Math.max(0, Math.min(100, Math.round((m.onhand || 0) / Math.max(needed(m),1) * 100))); }

const isDcPending = m => m.dc?.state === 'pending';
const isDcDone = m => !!m.dc && !isDcPending(m);
/* Until the second signature lands the medication is untouched, so it stays on the pour sheet. */
const scheduled = computed(() => meds.value.filter(m => !m.prn && !isDcDone(m)));
const prns = computed(() => meds.value.filter(m => m.prn && !isDcDone(m)));
const pendingDcMeds = computed(() => meds.value.filter(isDcPending));
const discontinuedMeds = computed(() => meds.value.filter(isDcDone));
/* PPU-5 — the order the pour sheet renders in. Sorting only reorders rows; it never changes what is
   poured, what is assigned, or whether a medication is complete. "By time" puts the earliest cup
   first and leaves meds with no times yet at the end, where they still need attention. */
function firstTimeIndex(m){
  if (!m.times || !m.times.length) return Number.MAX_SAFE_INTEGER;
  return Math.min(...m.times.map(hourRank));
}
function byName(a, b){ return a.name.localeCompare(b.name); }
/* The single ordering rule, so every layout sorts the same way rather than each having its own idea.
   "By PRN" does not reorder the schedule — it lifts the as-needed block — so it falls through to the
   time ordering here. */
function sortMeds(list){
  const out = [...list];
  if (sortBy.value === 'med') return out.sort(byName);
  return out.sort((a, b) => (firstTimeIndex(a) - firstTimeIndex(b)) || byName(a, b));
}
const scheduledSorted = computed(() => sortMeds(scheduled.value));
/* The list layouts (A, C, D, E) render these groups rather than one flat list. Nothing is ever left
   out — a grouping only decides where each medication sits:
   - By time: one group per hour, earliest first, each holding the meds poured at that hour, so a
     caregiver can work cup by cup and add meds to an hour from its header. A 2-times-daily med sits
     in both of its hours; both rows are the same record. Meds with no hour yet come first, since
     they are the work left; skipped and not-pourable meds close the list.
   - By medication: one A–Z group.
   - By PRN: the as-needed block is lifted above the schedule (.pp-prnfirst), and the schedule keeps
     its own heading so the two read as two groups.
   B, the cup board, is already grouped by hour and does not use this. */
const medGroups = computed(() => {
  const list = scheduled.value.filter(matchesQuery);
  if (sortBy.value === 'med') return [{ key: 'all', label: 'All medications · A–Z', meds: [...list].sort(byName) }];
  if (sortBy.value === 'prn') return [{ key: 'sched', label: 'Scheduled', meds: scheduledSorted.value }];
  // By prescription: medications added without one (no order) apart from the prescribed, each A–Z
  if (sortBy.value === 'rx') return [
    { key: 'rx', label: 'Prescription', meds: list.filter(m => !m.nonPrescription).sort(byName) },
    { key: 'nonrx', label: 'Non-prescription', meds: list.filter(m => m.nonPrescription).sort(byName) },
  ].filter(g => g.meds.length);
  const hoursOf = m => (isFullyLocked(m) ? lockInfo(m).times : m.times);
  const active = list.filter(m => m.pourable && !m.skip);
  const hours = [...new Set([...palette.value, ...active.flatMap(hoursOf)])]
    .sort(byHour);
  const groups = [];
  const unplaced = active.filter(m => !hoursOf(m).length).sort(byName);
  if (unplaced.length) groups.push({ key: 'none', label: 'No time yet', meds: unplaced });
  hours.forEach(h => groups.push({ key: 'h:' + h, label: h, hour: h, meds: active.filter(m => hoursOf(m).includes(h)).sort(byName) }));
  const skipped = list.filter(m => m.pourable && m.skip).sort(byName);
  if (skipped.length) groups.push({ key: 'skip', label: 'Not pouring this range', meds: skipped });
  const np = list.filter(m => !m.pourable).sort(byName);
  if (np.length) groups.push({ key: 'np', label: 'Not pourable — administer directly', meds: np });
  return groups;
});
const nonPourable = computed(() => scheduled.value.filter(m => !m.pourable));
const lowMeds = computed(() => scheduled.value.filter(isLow));
/* who is in this pour: pourable, not locked, not skipped */
const pourCandidates = computed(() => scheduled.value.filter(m => m.pourable && !isFullyLocked(m) && !m.skip && !isFullyStopped(m)));
const skippedMeds = computed(() => scheduled.value.filter(m => m.pourable && !isFullyLocked(m) && m.skip));
const placeable = computed(() => sortMeds(scheduled.value.filter(m => m.pourable && !isFullyLocked(m))));
function isComplete(m){ return m.times.length >= maxTimes(m); }
const completeCandidates = computed(() => pourCandidates.value.filter(isComplete));
const incompleteCandidates = computed(() => pourCandidates.value.filter(m => !isComplete(m)));
const hasPastDays = computed(() => rangeDays.value.some(d => d < today.value));
const canPour = computed(() => canPrepour.value && rangeDays.value.length > 0 && !hasPastDays.value && !overMaxDays.value && palette.value.length > 0 && completeCandidates.value.length > 0 && incompleteCandidates.value.length === 0);
const pourBlockReason = computed(() => {
  if (!canPrepour.value) return 'You can sign discontinues here, but pouring needs the Pre-Pour privilege.';
  if (!rangeDays.value.length) return 'Select the days to pour first.';
  if (hasPastDays.value) return 'The selected days include dates in the past. Pick today or later.';
  if (overMaxDays.value) return `Pre-pouring reaches at most ${PREPOUR_MAX_RANGE_DAYS} days ahead (through ${fmtDay(maxPourDay.value)}). Shorten the range.`;
  if (!palette.value.length) return 'Select the administration hours first.';
  if (!pourCandidates.value.length) return skippedMeds.value.length ? 'Every medication is marked "Not pouring this range".' : 'No pourable medications in this range.';
  const n = incompleteCandidates.value.length;
  if (n) return `${n} medication${n === 1 ? '' : 's'} still need${n === 1 ? 's' : ''} times: ${incompleteCandidates.value.map(shortName).join(', ')}.`;
  return '';
});
const pourReadyText = computed(() => `${completeCandidates.value.length} med(s) ready to pour` + (skippedMeds.value.length ? ` · ${skippedMeds.value.length} not pouring` : ''));
function stateLabel(m){
  if (m.skip) return 'Not pouring this range';
  if (isFullyStopped(m)) return 'Nothing to pour — held or completed for this whole range';
  const n = maxTimes(m), have = m.times.length;
  // an auto-filled row never claims to be "complete" — it is waiting to be agreed with
  if (m.suggested && have) return `Auto-filled · ${m.times.join(', ')} · check before signing`;
  if (have >= n) return '';   // the chips already show the times; a complete row needs no status line
  if (!have) return `Choose ${n} time${n === 1 ? '' : 's'}`;
  return `${have} of ${n} times chosen · ${n - have} more needed`;
}
function stateClass(m){ return m.skip || isFullyStopped(m) ? 'skip' : (m.suggested && m.times.length ? 'sugg' : (isComplete(m) ? 'ok' : 'todo')); }
/* Inside a cup every medication shares the hour, so the time ordering has nothing left to say and the
   medication ordering does the work. Both go through sortMeds so the board follows the same control
   as the list layouts. */
function cupMeds(t){ return sortMeds(scheduled.value.filter(m => m.pourable && !m.skip && m.times.includes(t))); }
function cupChoices(t){
  return placeable.value.filter(m => !m.skip && !m.times.includes(t)).map(m => {
    const full = m.times.length >= maxTimes(m);
    return Object.assign(Object.create(m), { _disabled: full, _reason: full ? `${m.freq} allows ${maxTimes(m)} cup${maxTimes(m) === 1 ? '' : 's'} — remove it from another cup first` : `Add ${shortName(m)} to the ${t} cup` });
  });
}
/* offered only while some medication can still take this hour — otherwise the button leads nowhere */
function canAddTo(t){ return cupChoices(t).some(m => !m._disabled); }
// only what can actually be added: a med already at its frequency's limit is not an option, it is noise
function addableTo(t){ return cupChoices(t).filter(m => !m._disabled); }
function addToCup(m, t){
  if (m.times.length >= maxTimes(m) || m.times.includes(t)) return;
  claimRow(m); m.times.push(t); sortTimes(m);
  if (!canAddTo(t)) cupPicker.value = null;
}
const cupPicker = ref(null);

/* Variant E — which rows have their full record open. Per medication, because the whole point is that
   a caregiver opens the one they have a question about and leaves the rest compact. Not persisted:
   it is a reading state, not part of the pour. */
const detailRows = reactive({});
function toggleDetail(m){ detailRows[m.id] = !detailRows[m.id]; }
const allDetailsOpen = computed(() => scheduled.value.length > 0 && scheduled.value.every(m => detailRows[m.id]));
function expandAllDetails(){ scheduled.value.forEach(m => { detailRows[m.id] = true; }); }
function collapseAllDetails(){ Object.keys(detailRows).forEach(k => { detailRows[k] = false; }); }
function shortName(m){ return m.name.split(' ').slice(0,2).join(' '); }

/* ---- automatic placement (PPU-18) ----
   "Why aren't they in there already?" — the review watched a nurse set up her hours and then place
   every medication into every slot by hand, when the sheet already knew how often each one was due.
   Joe's own words a moment later: adding a med "tells you that it's one a day so you don't get any
   more". The frequency is known and the count is already enforced; only the clicking was manual.

   So the sheet places them. It is a SUGGESTION and says so everywhere it appears: when a medication is
   given is a clinical decision, and sign-off is where the caregiver takes it. Nothing here is silent. */

/* Spread n doses across the chosen hours so they land apart rather than stacking at the start. */
function spreadAcross(pool, n){
  if (n >= pool.length) return [...pool];
  const out = [];
  for (let i = 0; i < n; i++){
    const h = pool[Math.round(i * (pool.length - 1) / (n - 1))];
    if (!out.includes(h)) out.push(h);
  }
  for (const h of pool){ if (out.length >= n) break; if (!out.includes(h)) out.push(h); }
  return out.sort(byHour);
}
/* Which of the selected hours this medication would be given at. Frequencies that name a time of day
   are honoured — "every morning" is a clinical instruction, not a scheduling preference — and anything
   that only says how often is spread across the chosen hours. */
function suggestTimesFor(m){
  const pool = palette.value, n = maxTimes(m);
  if (!pool.length || !n) return [];
  if (n === 1) {
    const f = String(m.freq || '').toLowerCase();
    if (/bedtime|evening|night/.test(f)) return [pool[pool.length - 1]];
    if (/morning/.test(f)) return [pool[0]];
    return [pool[0]];
  }
  return spreadAcross(pool, n);
}
/* Place medications on the selected hours.
   - An empty row, or one still marked Auto, is (re)spread across every selected hour: it is a machine
     guess, so when a second hour appears a 2-times-daily med moves from "6am" to "6am, 6pm".
   - A row the caregiver set or that was carried over is never rearranged. With `topUp` — an hour was
     just added — one still short of its frequency gains hours, and only gains: its own hours stay put.
     It is then marked Auto, because part of it is now a machine choice that needs checking. */
function suggestPlacements({ topUp = false } = {}){
  placeKeyonTimes();
  if (!autoFill.value || !palette.value.length) return;
  meds.value.forEach(m => {
    if (m.prn || !m.pourable || m.skip || isDcDone(m) || isFullyLocked(m) || isKeyonTimed(m)) return;
    const n = maxTimes(m);
    if (!m.times.length || m.suggested) {
      const t = suggestTimesFor(m);
      if (!t.length) return;
      m.times = t;
      m.suggested = true;
      return;
    }
    if (!topUp || m.times.length >= n) return;
    const extra = [...spreadAcross(palette.value, n), ...palette.value].filter((h, i, all) => h && all.indexOf(h) === i && !m.times.includes(h));
    const add = extra.slice(0, n - m.times.length);
    if (!add.length) return;
    m.times.push(...add); sortTimes(m);
    m.suggested = true;
  });
}
/* A real medication is poured and given at its own scheduled times, whatever the draft, the last pour or the frequency
   would place: the server refuses a dose at any other hour (PourPlan, prepour_time_not_scheduled; main's
   lib/PrePourSchedule.php). Placing medications on the pour hours by frequency alone poured a dose due at 08:00 at 7am
   past its hold, and giving it at 8pm wrote a new 20:00 time while 19:00 stayed due. Its hours join the palette so each
   has a cup; a time moves only through the × (the medication's schedule). These are not a machine guess, so the row
   is never marked Auto. */
function isKeyonTimed(m){ return !!(m.keyonHours && m.keyonHours.length); }
function placeKeyonTimes(){
  const missing = [];
  meds.value.forEach(m => {
    if (!isKeyonTimed(m) || m.prn || !m.pourable || m.skip || isDcDone(m) || isFullyLocked(m)) return;
    m.times = m.keyonHours.filter(t => !(m.dcTimes || []).includes(t)).sort(byHour);
    m.suggested = false; m.picking = false;
    m.times.forEach(t => { if (!palette.value.includes(t) && !missing.includes(t)) missing.push(t); });
  });
  if (!missing.length) return;
  palette.value.push(...missing);
  palette.value.sort(byHour);
}
/* The moment a caregiver touches a row it stops being a suggestion and becomes their decision. */
function claimRow(m){ m.suggested = false; }
const suggestedMeds = computed(() => scheduled.value.filter(m => m.suggested && !m.skip && m.times.length));

/* ---- pour sheet actions ---- */
/* the frequency decides how many administration times a med may have per day */
function maxTimes(m){
  const dcTimes = m.dcTimes || [];
  // A medication with a schedule is poured at every one of its scheduled times, as the server counts them
  // (PourPlan, PP-OM-01): a cup is one scheduled dose, whatever the frequency says. A dose stopped on the MAR has
  // already left the schedule, so counting it off the frequency as well took it twice. A dose discontinued here (kept
  // in this browser, D2) is still on the schedule: it goes to the server as a held slot, not poured (locallyStoppedTimes).
  if (isKeyonTimed(m)) return Math.max(1, m.keyonHours.filter(t => !dcTimes.includes(t)).length);
  const known = m.freqUnstated ? null : knownDoseCount(m.freq);
  return Math.max(1, (known || 1) - dcTimes.length);
}
function sortTimes(m){ m.times.sort(byHour); }
function canOpenPicker(m){ return m.pourable && !isFullyLocked(m) && !m.skip && palette.value.length > 0 && !isKeyonTimed(m); }
function pickerHint(m){
  if (isKeyonTimed(m)) return 'Poured at its times in Medication Administration. Use × on a time to move it.';
  if (!palette.value.length) return 'Select hours in the palette above first';
  return m.times.length ? 'Change which of the selected hours this medication uses' : `Choose ${maxTimes(m)} of the selected hours for this medication`;
}
function openPicker(m){ meds.value.forEach(x => { x.picking = false; }); m.picking = true; }
function toggleMedTime(m, t){
  claimRow(m);
  const i = m.times.indexOf(t);
  if (i >= 0) { m.times.splice(i, 1); return; }
  if (m.times.length >= maxTimes(m)) return;
  m.times.push(t); sortTimes(m);
}
/* A frequency change keeps the times the caregiver already has. Going up leaves the added slot(s) empty
   for the caregiver to choose, whatever the auto-fill switch says — "it shouldn't have a time in it"
   (review 2026-09-25). Going down keeps only the times picked in the Change pop-up: which dose stops is
   an order decision, not the page's. */
function applyFreqChange(m, to, keep){
  m.freq = to; m.freqUnstated = false; m.picking = false; claimRow(m);
  if (!keep) return;
  m.times.filter(t => !keep.includes(t)).forEach(t => { if (pourMarks[m.id]) delete pourMarks[m.id][t]; });
  m.times = m.times.filter(t => keep.includes(t));
}
/* A frequency change is a change to the order, so it goes through the Change pop-up (date, which times,
   reason) and only applies on Submit. The select is put back until then. */
function requestFreqChange(m, e){
  const to = e.target.value, from = m.freq;
  e.target.value = from;
  if (to === from) return;
  openStatusDialog(m, 'Change', { freqFrom: from, freqTo: to });
}
/* The × on a time no longer removes it: dropping a dose without moving it changes the order
   (review 2026-09-25). It asks whether to move the dose to another hour or discontinue it. */
function removeTime(m, i){ if (i >= 0 && m.times[i]) askRemoveTime(m, m.times[i]); }
function toggleSkip(m){ claimRow(m); m.skip = !m.skip; if (m.skip) { m.times = []; m.picking = false; } else placeKeyonTimes(); }

/* ---- unsigned draft: survives navigation / reload, cleared on sign-off ---- */
const draftRestored = ref(false);
/* PPU-7 — set when this sheet was filled in from the last signed pour rather than by the caregiver.
   It drives a banner, because a carried-over pour is emphatically NOT a signed one: the review was
   explicit that the caregiver re-confirms and signs every period before anything may be given. */
const carriedOver = ref(null);
let draftReady = false;
function draftSnapshot(){
  const times = {}, skips = {}, repours = {}, suggested = {};
  meds.value.forEach(m => { if (m.times.length) times[m.id] = [...m.times]; if (m.skip) skips[m.id] = true; if (m.repour) repours[m.id] = true; if (m.suggested) suggested[m.id] = true; });
  return { picked: range.picked ? range.picked.map(d => d ? new Date(d).toISOString() : null) : null,
    multi: (range.multi || []).map(d => new Date(d).toISOString()), palette: [...palette.value], times, skips, repours, suggested,
    poured: JSON.parse(JSON.stringify(pourMarks)) };
}
function saveDraft(){ if (draftReady) lsSet('draft', draftSnapshot()); }
function restoreDraft(){
  const d = lsGet('draft', null);
  draftRestored.value = false;
  // always start from defaults so a previous patient's range/palette never bleed into this patient's draft
  Object.assign(range, defaultRange()); palette.value = [...DEFAULT_PALETTE];
  if (d) {
    // a draft can be reopened days later: drop days that are already in the past
    if (d.picked && d.picked[0]) {
      const start = ymd(d.picked[0]), end = ymd(d.picked[1] || d.picked[0]);
      if (end >= today.value) range.picked = [new Date((start < today.value ? today.value : start) + 'T00:00:00'), new Date(end + 'T00:00:00')];
    }
    if (Array.isArray(d.multi)) range.multi = d.multi.map(x => new Date(x)).filter(x => ymd(x) >= today.value);
    if (Array.isArray(d.palette)) palette.value = [...d.palette];
    meds.value.forEach(m => {
      if (d.repours && d.repours[m.id] && lockInfo(m)) m.repour = true;
      if (d.times && d.times[m.id] && !isFullyLocked(m)) m.times = d.times[m.id].filter(t => palette.value.includes(t)).slice(0, maxTimes(m));
      if (d.skips && d.skips[m.id]) m.skip = true;
      // a suggestion is still a suggestion after a reload; it only stops being one when touched
      if (d.suggested && d.suggested[m.id]) m.suggested = true;
    });
    if (d.poured) Object.assign(pourMarks, d.poured);
    draftRestored.value = Object.keys(d.times || {}).length > 0 || Object.keys(d.skips || {}).length > 0 || Object.keys(d.repours || {}).length > 0;
  }
  if (!draftRestored.value) applyTemplate();
  suggestPlacements();          // anything still empty gets placed; nothing already set is touched
  draftReady = true;
}
/* Fill an empty sheet from the last signed pour. Only ever runs when the caregiver has no draft of
   their own in progress, so it can never overwrite work someone is in the middle of. Medications
   added or removed since last time simply have no entry and are left for the caregiver, which is
   what "unless there's a med added or a med taken away" asks for. */
// "Start from scratch" is a decision about this sheet, so a later range change must not undo it
let templateDismissed = false;
function applyTemplate(){
  carriedOver.value = null; templateDismissed = false;
  const t = prepourApi.templateFor(patientKey.value, pours.value);
  if (!t || !Array.isArray(t.palette) || !t.palette.length) return;
  palette.value = [...t.palette];
  const skips = new Set(t.skips || []);
  meds.value.forEach(m => { if (skips.has(m.id) && !isFullyLocked(m) && !isDcDone(m) && m.pourable && !m.prn) m.skip = true; });
  fillFromTemplate(t);
}
/* Put the last signed pour's hours on every row that has none of the caregiver's own yet. Called again
   whenever the range changes: a med fully locked under the default range (the pour just signed) had
   nothing to fill on load, and only becomes pourable once a later range is picked. A machine suggestion
   gives way to the carried-over hours; a row the caregiver has set, skipped or reopened is never touched. */
function fillFromTemplate(t = prepourApi.templateFor(patientKey.value, pours.value)){
  if (!t || !t.times || templateDismissed) return;
  const filled = new Set(carriedOver.value ? carriedOver.value.ids : []);
  meds.value.forEach(m => {
    if (isFullyLocked(m) || isDcDone(m) || !m.pourable || m.prn || m.skip || m.repour) return;
    if (m.times.length && !m.suggested) return;
    const times = (t.times[m.id] || []).filter(x => palette.value.includes(x) && !(m.dcTimes || []).includes(x)).slice(0, maxTimes(m));
    if (!times.length) return;
    m.times = times;
    m.suggested = false;        // carried over from a pour someone signed — not a machine guess
    filled.add(m.id);
  });
  if (!filled.size) return;
  // meds on the sheet now that were not in the last pour — the caregiver has to place these themselves
  const fresh = scheduled.value.filter(m => m.pourable && !m.skip && !m.times.length && !isFullyLocked(m)).length;
  if (!carriedOver.value) timesOpen.value = false;
  carriedOver.value = { at: t.savedAt, by: t.signedBy || '', ids: [...filled], matched: filled.size, fresh };
}
watch(() => rangeDays.value.join(','), () => { if (draftReady) { fillFromTemplate(); suggestPlacements(); } });
function discardTemplate(){
  carriedOver.value = null; templateDismissed = true;
  meds.value.forEach(m => { m.times = []; m.skip = false; m.picking = false; m.suggested = false; });
  suggestPlacements();
}
function discardDraft(){
  localStorage.removeItem(lsKey('draft'));
  meds.value.forEach(m => { m.times = []; m.skip = false; m.picking = false; m.repour = false; m.suggested = false; });
  draftRestored.value = false;
  carriedOver.value = null;
  suggestPlacements();
}
watch([() => meds.value.map(m => [m.times.join(','), m.skip, m.repour, m.suggested]), palette, () => range.picked, () => range.multi, pourMarks], saveDraft, { deep: true });
/* ---- Add Meds: shared nursing form -> keyon InsertAdminMecationInfo (prepour = 1) ---- */
const addMed = reactive({ open: false, saving: false, provLoaded: false, providers: [], prefill: null });
/* The hours a just-saved medication was given in the Add Medication form, waiting for it to come back
   from the reload so they can be put on its row. */
const pendingSeed = ref(null);
/**
 * PPU-1, the half the review actually asked for: "when I add the med and I put in Lasix for 7am, if I
 * clock that time, it's going to show up here automatically."
 *
 * Every medication arrives from keyon with no times — the sheet deliberately makes the caregiver choose
 * each range rather than assuming last time's answer. But a medication added seconds ago on this screen
 * is the one case where the answer is already known, and making the caregiver re-enter the hours they
 * just typed is the disconnect the review was about.
 *
 * Hours the form used that the sheet does not know about join the palette first, so the med has cups to
 * land in. Anything beyond what its frequency allows is dropped rather than silently over-filling it.
 */
function applyPendingSeed(){
  const seed = pendingSeed.value;
  pendingSeed.value = null;
  if (!seed || !seed.times.length || !seed.name) return;
  seed.times.filter(h => !palette.value.includes(h)).forEach(togglePalette);
  const key = seed.name.toLowerCase();
  const m = meds.value.find(x => String(x.name || '').trim().toLowerCase() === key);
  if (!m || m.prn || !m.pourable || isFullyLocked(m)) return;
  m.times = seed.times.filter(t => palette.value.includes(t)).slice(0, maxTimes(m));
  m.suggested = false;
  sortTimes(m);
}
function openAddMed(){
  if (!hasPatient.value) { Swal.fire({ icon: 'info', title: 'Select a patient first', text: 'Add Meds needs a patient in the session.' }); return; }
  if (!assertPrivilegeOrAlert(PRIVILEGES.MEDICATION_ADD_UNORDERED, 'You do not have permission to add medications.')) return;
  // keyon row names — the form's prefill watcher only reads those (startdate/prepour/medadmin)
  addMed.prefill = { prepour: 1, medadmin: 1, startdate: today.value };
  addMed.open = true;
  // the hours are what the form is about to default to, so show what it will use
  if (palette.value.length) timesOpen.value = false;
}
function closeAddMed(){ addMed.open = false; addMed.prefill = null; }   // null -> object again on next open re-triggers the prefill
async function loadProviders(){
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', { Provider: { API_Meth: 'GetAllProviders', patientid: patient.value.externId, accountnumber: patient.value.externId2 } });
    addMed.providers = res.data?.provider || [];
  } catch (e) { addMed.providers = []; }
  addMed.provLoaded = true;
}
function flag(v){ return v === true || v === 1 || v === '1' ? 1 : 0; }
async function saveNewMed(formData, timeInputs){
  if (addMed.saving) return;
  addMed.saving = true;
  Swal.fire({ title: 'Saving medication...', allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() });
  const now = new Date();
  const times = (timeInputs && timeInputs.length ? timeInputs : String(formData.administrationTimes || '').split(','))
    .map(t => String(t || '').trim()).filter(Boolean);
  const content = { MedicationAdmin: {
    API_Meth: 'InsertAdminMecationInfo',
    // The local day, beside the local time (`toISOString()` is the UTC day:
    // an evening order was dated tomorrow at tonight's time).
    orderDate: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`,
    orderTime: now.toTimeString().split(' ')[0],
    accountnumber: patient.value.externId2, patientid: patient.value.externId,
    npinumber: formData.providerNpi || formData.providernpi || allData.value?.manager?.externid || '',
    proflicensenumber: '123123123', ordernumber: '36',
    // keyon creates no order for a non-prescription medication (lib/NonPrescriptionMedication.php)
    nonprescription: formData.nonprescription ? 1 : 0,
    name: formData.medicationName || '', dates: {},
    tabsAvailable: formData.tabsAvailable || 0, totalTabs: formData.quantity,
    frequency: formData.frequency || '', dosage: formData.dosage || '',
    administrationTimes: formData.administrationTimes || '', scheduletimes: formData.administrationTimes,
    slotedtimes: times.map(t => ({ time: t, status: 'pending', dosage: parseInt(formData.dosage, 10) || 1 })),
    route: formData.route || '', dosageForm: formData.dosageForm || '', diagnosis: formData.diagnosis || '',
    prn: formData.prn, newMedication: formData.newMedication || false,
    // Pre-Pour slots live on the same records Medication Administration reads (medadmin = 1), so both flags are always on
    administration: 1, prepour: 1, nebulizer: flag(formData.nebulizer),
    datescriptFilled: formData.filledDate, startDate: formData.startDate, endDate: formData.endDate,
    primeprovName: formData.providerName, primeprovEmail: formData.providerEmail, primeprovAddress: formData.providerAddress,
    primeprovPhone: formData.providerOffice, primeprovFax: formData.providerFax, primeprovNpi: formData.providerNpi,
    providerDea: formData.providerDea || '', providerCell: formData.providerCell || '',
    pharmacy: formData.pharmacyName || '', pharmacyNpi: formData.pharmacyNpi || '', pharmacyAddress: formData.pharmacyAddress || '',
    pharmacyPhone: formData.pharmacyOffice || '', pharmacyCell: formData.pharmacyCell || '', pharmacyEmail: formData.pharmacyEmail || '',
    pharmacyDea: formData.pharmacyDea || '', prescriberInfo: formData.prescriberInfo || '', prescriberDeaNpi: formData.prescriberDeaNpi || '',
    rxNumber: formData.rxnorns || '', ndcnumber: formData.ndcnumber, refills: formData.refills || 0,
    refillReminderDate: formData.refillReminderDate, expirationDate: formData.expirationDate || '', instructions: formData.instructions || '',
    status: 'active', medsetting: '', discontinuedDate: undefined, discontinuedTimes: {},
    via: formData.via || 0, fluidType: formData.fluidType, startTime: formData.startTime, endTime: formData.endTime,
    howLong: formData.howLong || 0, totalVolume: formData.totalVolume, totalVolumeUnit: formData.totalVolumeUnit, rate: formData.rate,
    provaccountnumber: allData.value?.manager?.externid2, provsubaccountnumber: allData.value?.manager?.externid,
    units: formData.unitType || '', source: 'Manual', link: '',
  } };
  try {
    const res = await axios.post(CURR_API + '/tswebhook.php', content);
    const d = res.data || {};
    if (d.count >= 1) {
      Swal.fire({ icon: 'warning', title: 'Duplicate medication', text: `${formData.medicationName} is already on this patient's list.` });
    } else if (d.message === 'Medication Added Successfully') {
      /* PPU-1, the other direction. A caregiver may start from the medications rather than from the
         hours — "I might start off by adding medications… then they're going to reflect out here."
         Held until after loadMeds, because restoreDraft rebuilds the palette from the saved draft and
         would otherwise overwrite anything set before it. */
      pendingSeed.value = {
        name: String(formData.medicationName || '').trim(),
        times: [...new Set(times.map(hourFrom24).filter(Boolean))],
      };
      await Swal.fire({ icon: 'success', title: 'Medication added', confirmButtonText: 'Ok' });
      closeAddMed();
      await loadMeds();
      applyPendingSeed();
    } else {
      Swal.fire({ icon: 'error', title: 'Error saving medication', text: d.results || d.errorMsg || 'Unexpected response from the server.' });
    }
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'Error saving medication', text: e?.response?.data?.message || e.message });
  } finally { addMed.saving = false; }
}

/* ---- sign-off popup ---- */
const signOff = reactive({ open: false, signature: '', forceOverlap: false, items: [], skipped: [], overlapTotal: 0, overlapSigners: [], effectiveCount: 0 });
function openSignOff(){
  if (!canPour.value) { alert(pourBlockReason.value); return; }
  signOff.skipped = skippedMeds.value.map(m => ({ id: m.id, name: m.name, freq: m.freq }));
  const items = completeCandidates.value.map(m => {
    // a re-pour's editable days are the whole range, overlap included; the overlap is added back only
    // when the caregiver confirms it, so it must not also sit in `days` or those days are poured twice
    // only the days the bottle covers are poured (decided 2026-10-05); the rest stay unpoured, named below
    const uncovered = supplyDays(m).unavailable;
    const overlap = overlapDays(m).filter(d => !uncovered.includes(d));
    const days = editableDays(m).filter(d => !overlap.includes(d) && !uncovered.includes(d));
    const locked = lockedDaysFor(m.id);
    const overlapSigner = overlap.length ? signerLabel(locked[overlap[overlap.length-1]]) : '';
    return { med: m, days, overlap, overlapSigner, uncovered, units: unitsOver(m, days),
      unitsWithOverlap: unitsOver(m, [...days, ...overlap]), stopped: stoppedSlotsIn(m, [...days, ...overlap]) };
  });
  const signers = [...new Set(items.flatMap(c => c.overlap.length ? [c.overlapSigner] : []))];
  Object.assign(signOff, { open: true, signature: '', forceOverlap: items.some(c => c.overlap.length), items,
    overlapTotal: items.reduce((n, c) => n + c.overlap.length, 0), overlapSigners: signers });
  recountSignOff();
}
/* ---- barcode check, per medication ----
   While pouring, the caregiver scans the bottle in their hand to prove it is the medication on this row
   — the one entered through Add Medication, identified by its NDC. A USB or Bluetooth scanner types
   the code and presses Enter, so a focused text input is all the hardware needs.
   Sign-off reports it, a real patient's pour keeps the time each bottle was checked (MED-11), and it is
   cleared once the pour is signed. */
const scans = reactive({});
function canScan(m){ return m.pourable && !m.prn && !isFullyLocked(m) && !m.skip; }
function scanTitle(m){
  if (scans[m.id]) return 'Scanned ' + fmtStamp(scans[m.id].at) + ' — scan again to re-check';
  return m.ndc ? 'Scan the ' + shortName(m) + ' bottle to confirm it matches NDC ' + m.ndc : 'No NDC on record for ' + shortName(m);
}
/* A bottle's barcode is usually a UPC-A: "3", the 10-digit NDC, a check digit. Records hold the NDC in
   10- or 11-digit form; the 11-digit form pads one segment with a zero (4-4-2, 5-3-2 or 5-4-1 → 5-4-2),
   so an 11-digit record matches a 10-digit code with that zero removed. */
function ndcMatches(code, ndc){
  let c = String(code || '').replace(/\D/g, '');
  const n = String(ndc || '').replace(/\D/g, '');
  if (!c || !n) return false;
  if (c.length === 12 && c[0] === '3') c = c.slice(1, 11);
  if (c === n) return true;
  if (n.length === 11 && c.length === 10) return [0, 5, 9].some(i => n[i] === '0' && n.slice(0, i) + n.slice(i + 1) === c);
  if (c.length === 11 && n.length === 10) return ndcMatches(n, c);
  return false;
}
async function scanMed(m){
  if (!m.ndc) {
    Swal.fire({ icon: 'warning', title: 'No NDC on record', text: `${m.name} has no NDC number saved, so the bottle cannot be checked against it. Add the NDC to the medication first.` });
    return;
  }
  const res = await Swal.fire({
    title: 'Scan the ' + shortName(m) + ' bottle',
    text: 'Point the scanner at the barcode on the bottle, or type the NDC.',
    input: 'text', inputPlaceholder: 'Barcode / NDC', inputAttributes: { autocomplete: 'off', autocapitalize: 'off' },
    showCancelButton: true, confirmButtonText: 'Check', focusConfirm: false,
  });
  if (!res.isConfirmed) return;
  if (ndcMatches(res.value, m.ndc)) {
    scans[m.id] = { at: new Date().toISOString() };
    Swal.fire({ icon: 'success', title: 'Bottle matches', text: `${m.name} · NDC ${m.ndc}`, timer: 1400, showConfirmButton: false });
  } else {
    delete scans[m.id];
    Swal.fire({ icon: 'error', title: 'This is not ' + shortName(m), text: `The scanned code does not match NDC ${m.ndc}. Check the bottle before pouring it.` });
  }
}
const signOffScanned = computed(() => signOff.items.filter(c => scans[c.med.id]).length);
function recountSignOff(){
  signOff.effectiveCount = signOff.items.filter(c => c.days.length || (signOff.forceOverlap && c.overlap.length)).length;
}
const signOffAutoCount = computed(() => signOff.items.filter(c => c.med.suggested).length);
watch(() => signOff.forceOverlap, recountSignOff);
/* Remove one med's given days from every signed pour (a re-pour supersedes the earlier slots for those
   med-days). Pours share a day list across their meds, so a partially affected pour is split in two. */
function supersedeMedDays(medId, days){
  const gone = new Set(days);
  const next = [];
  pours.value.forEach(p => {
    if (!p.meds.some(pm => pm.id === medId) || !p.days.some(d => gone.has(d))) { next.push(p); return; }
    const keptDays = p.days.filter(d => !gone.has(d));
    const hitDays = p.days.filter(d => gone.has(d));
    const otherMeds = p.meds.filter(pm => pm.id !== medId);
    if (keptDays.length) next.push({ ...p, days: keptDays });
    if (otherMeds.length) next.push({ ...p, id: p.id + '-' + medId, days: hitDays, meds: otherMeds });
  });
  pours.value = next;
}
const signing = ref(false);
async function confirmSignOff(){
  const signer = signOff.signature.trim();
  if (!signer || !signOff.effectiveCount || signing.value) return;
  if (prepourApi.isRemote(patientKey.value)) { await signPourInKeyon(signer); return; }
  const pourMeds = [];
  const ov = prepourApi.getOnHand(patientKey.value);
  signOff.items.forEach(c => {
    const pourDays = signOff.forceOverlap ? [...new Set([...c.days, ...c.overlap])].sort() : c.days;
    if (!pourDays.length) return;
    if (signOff.forceOverlap && c.overlap.length) supersedeMedDays(c.med.id, c.overlap);
    // the earlier pour's pills for these days are discarded; the re-pour takes new pills from the bottle
    const units = unitsOver(c.med, pourDays);
    c.med.onhand = (c.med.onhand || 0) - units;           // Model B: decrement at pour time
    ov[c.med.id] = c.med.onhand;
    // held or completed doses are not poured; they stay on the record so Administer can say why
    pourMeds.push({ id: c.med.id, name: c.med.name, dose: c.med.dose, form: c.med.form, times: [...c.med.times], days: pourDays,
      held: stoppedSlotsIn(c.med, pourDays).map(x => x.day + '|' + x.time), doses: { ...(c.med.timeDoses || {}) } });
  });
  prepourApi.saveOnHand(patientKey.value, ov);
  // one pour per distinct day-set so the lock grid stays rectangular
  const groups = {};
  pourMeds.forEach(pm => { const k = pm.days.join('|'); (groups[k] = groups[k] || []).push(pm); });
  Object.entries(groups).forEach(([k, list]) => {
    pours.value.push({ id: 'p' + Date.now() + Math.random().toString(36).slice(2,6), days: k.split('|'), signer,
      signerName: staff.value.name, signerId: staff.value.id, signerAccount: staff.value.account, signerRole: staff.value.role,
      signedAt: new Date().toISOString(), meds: list.map(({ days: _d, ...rest }) => rest),
      skipped: signOff.skipped.map(x => ({ id: x.id, name: x.name })) });
  });
  prepourApi.savePours(patientKey.value, pours.value);
  /* PPU-7 — remember the shape of this pour so the next range starts from it. A caregiver pours the
     same organizer every week or fortnight and should not rebuild it each time: "they don't need to
     go through this whole process once a week".
     Only the shape is kept — hours, which medication takes which of them, and what was left out.
     Nothing about it is signed, and it is applied as editable defaults, never as a completed pour. */
  prepourApi.saveTemplate(patientKey.value, {
    palette: [...palette.value],
    times: pourMeds.reduce((acc, pm) => { acc[pm.id] = [...pm.times]; return acc; }, {}),
    skips: signOff.skipped.map(x => x.id),
    savedAt: new Date().toISOString(),
    signedBy: staff.value.name || signer,
  });
  finishSignOff();
}
/* the draft is done: clear choices so the next pour starts explicit again */
function finishSignOff(){
  signOff.open = false;
  meds.value.forEach(m => { m.times = []; m.skip = false; m.picking = false; m.repour = false; m.suggested = false; });
  localStorage.removeItem(lsKey('draft')); draftRestored.value = false;
  Object.keys(scans).forEach(k => { delete scans[k]; });
  Object.keys(pourMarks).forEach(k => { delete pourMarks[k]; });
  mode.value = 'locked';
}
/* A real patient's pour is signed in keyon (PourAndSign, keyon/lib/PrePourSign.php): it stores the slots,
   supersedes what a re-pour takes back and takes the tablets from the bottle once. The page then reads the
   pours and the medications back, so what it shows is what keyon kept. A refusal leaves the sheet as it was. */
/* A dose discontinued on this sheet (kept in this browser, D2) is still on the medication's schedule, and the server
   pours every scheduled time (prepour_times_mismatch otherwise). It goes as a held slot: on record, nothing poured. */
function locallyStoppedTimes(m){
  return (m.dcTimes || []).filter(t => (m.keyonHours || []).includes(t) && !m.times.includes(t));
}
async function signPourInKeyon(signer){
  const meds = [];
  signOff.items.forEach(c => {
    const pourDays = signOff.forceOverlap ? [...new Set([...c.days, ...c.overlap])].sort() : c.days;
    if (!pourDays.length) return;
    const stopped = locallyStoppedTimes(c.med);
    meds.push({ medentryid: c.med.id, name: c.med.name, form: c.med.form, dose: c.med.dose, days: pourDays,
      times: [...c.med.times, ...stopped].sort(byHour), doses: { ...(c.med.timeDoses || {}) },
      held: [...stoppedSlotsIn(c.med, pourDays).map(x => x.day + '|' + x.time), ...pourDays.flatMap(d => stopped.map(t => d + '|' + t))],
      supersede: signOff.forceOverlap ? [...c.overlap] : [],
      // the bottle checked against its NDC before pouring, kept with the pour (MED-11); main reported it only
      ...(scans[c.med.id] ? { scanned_at: scans[c.med.id].at } : {}) });
  });
  signing.value = true;
  try {
    await prepourApi.signPour(pourCtx.value, { signature: signer, palette: [...palette.value],
      skipped: signOff.skipped.map(x => ({ id: x.id, name: x.name })), meds });
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'The pour was not signed', text: e.message });
    return;
  } finally {
    signing.value = false;
  }
  await refreshPours();
  await loadMeds();
  finishSignOff();
}

/* ---- administration slots + witness ---- */
const OUTCOMES = {
  taken:   { label: 'Taken',      mark: '✔' },
  except:  { label: 'Except for', mark: '✔' },
  refused: { label: 'Refused',    mark: '✘' },
};
const admin = ref({}); // slotKey -> { outcome, witness, notes, actualTime, at }
function slotKey(pourId, day, medId, time){ return `${pourId}|${day}|${medId}|${time}`; }
const allSlots = computed(() => {
  const out = [];
  pours.value.forEach(p => p.days.forEach(day => p.meds.forEach(pm => pm.times.forEach(time => {
    // held when it was poured around a hold, or put on hold after the pour was signed
    const med = meds.value.find(x => x.id === pm.id);
    const held = (pm.held || []).includes(day + '|' + time) || (med ? isStoppedSlot(med, day, time) : false);
    out.push({ key: slotKey(p.id, day, pm.id, time), pourId: p.id, day, time, medId: pm.id, name: pm.name,
      itemId: pm.items ? pm.items[day + '|' + time] : undefined,   // the keyon slot, for a real patient
      dose: (pm.doses && pm.doses[time]) || pm.dose, form: pm.form, signer: p.signer, signerName: p.signerName,
      held, heldWhy: held && med ? (stopReason(med, day, time) || ((med.dcTimes || []).includes(time) ? 'Discontinued' : '')) : '' });
  }))));
  return out;
});
const pendingSlotCount = computed(() => allSlots.value.filter(s => !admin.value[s.key] && !s.held).length);
const pouredDays = computed(() => [...new Set(allSlots.value.map(s => s.day))].sort());
const adminDay = ref(today.value);
/* History: "if I go back three weeks, how do I see what was taken?" (review 2026-09-25). A range shows
   every poured day in it, most recent first, in the same time-block layout as a single day. */
const adminView = ref('day');                  // 'day' | 'range'
const adminFrom = ref(addDays(today.value, -6));
const adminTo = ref(today.value);
const adminRange = computed(() => adminFrom.value <= adminTo.value ? [adminFrom.value, adminTo.value] : [adminTo.value, adminFrom.value]);
const inAdminRange = d => d >= adminRange.value[0] && d <= adminRange.value[1];
/* A day with changes but no pour is still listed: a hold that started on it is part of its history. */
const adminDays = computed(() => {
  if (adminView.value === 'day') return [adminDay.value];
  return [...new Set([...pouredDays.value, ...changes.value.map(c => c.day)])].filter(inAdminRange).sort().reverse()
    .filter(d => visibleGroupsFor(d).length || visibleChanges(d).length);
});

/* History by outcome: taken / except / refused as recorded, held, and "Not recorded" for a dose whose
   administration window closed with nothing written down — looking back, "not yet given" would read as
   if it could still be given. */
const OUTCOME_FILTERS = [
  { key: 'all', label: 'All' }, { key: 'taken', label: 'Taken' }, { key: 'except', label: 'Except for' },
  { key: 'refused', label: 'Refused' }, { key: 'missed', label: 'Not recorded' }, { key: 'held', label: 'Held' },
  { key: 'changed', label: 'Changed' },
];
const adminOutcome = ref('all');
function isPastSlot(day, time){ return slotDate(day, time).getTime() + windowHours * 3600e3 < now.value.getTime(); }
function isPastGroup(g){ return isPastSlot(g.slots[0].day, g.time); }
function isMissed(s){ return !admin.value[s.key] && !s.held && isPastSlot(s.day, s.time); }
function slotOutcome(s){ const r = admin.value[s.key]; return r ? r.outcome : s.held ? 'held' : isMissed(s) ? 'missed' : 'due'; }
const outcomeFiltering = computed(() => adminView.value === 'range' && adminOutcome.value !== 'all');
function visibleGroupsFor(day){
  if (!outcomeFiltering.value) return slotsByTimeFor(day);
  if (adminOutcome.value === 'changed') return [];
  return slotsByTimeFor(day).map(g => ({ ...g, slots: g.slots.filter(s => slotOutcome(s) === adminOutcome.value) })).filter(g => g.slots.length);
}
function changesOn(day){ return changes.value.filter(c => c.day === day).reverse(); }
function visibleChanges(day){ return !outcomeFiltering.value || adminOutcome.value === 'changed' ? changesOn(day) : []; }
function outcomeCount(k){
  const slots = allSlots.value.filter(s => inAdminRange(s.day));
  const changed = changes.value.filter(c => inAdminRange(c.day)).length;
  if (k === 'all') return slots.length + changed;
  return k === 'changed' ? changed : slots.filter(s => slotOutcome(s) === k).length;
}
const outcomeLabel = computed(() => (OUTCOME_FILTERS.find(f => f.key === adminOutcome.value) || {}).label);
function slotsByTimeFor(day){
  const groups = [];
  allSlots.value.filter(s => s.day === day)
    .sort((a, b) => hourRank(a.time) - hourRank(b.time) || a.name.localeCompare(b.name))
    .forEach(s => { let g = groups.find(x => x.time === s.time); if (!g) { g = { time: s.time, slots: [] }; groups.push(g); } g.slots.push(s); });
  return groups;
}
const adminHasSlots = computed(() => adminDays.value.some(d => visibleGroupsFor(d).length || visibleChanges(d).length));
/* The Locked pours grid reads held state from the same slots Administer uses: drawing a held dose like a
   poured one invited filling a cup that must stay empty (baseline round 2026-10-05). */
const slotsByKey = computed(() => new Map(allSlots.value.map(s => [s.key, s])));
function heldSlot(k){ const s = slotsByKey.value.get(k); return !admin.value[k] && s && s.held ? s : null; }
function slotClass(k){ const r = admin.value[k]; return r ? 'o-' + r.outcome : heldSlot(k) ? 'o-held' : 'o-pending'; }
function slotMark(k){ const r = admin.value[k]; return r ? OUTCOMES[r.outcome].mark : heldSlot(k) ? 'held' : ''; }
function slotTitle(k){
  const r = admin.value[k], h = heldSlot(k);
  if (r) return `${OUTCOMES[r.outcome].label} · witness ${r.witness} · ${r.actualTime}`;
  return h ? `${h.heldWhy || 'On hold'} — not poured, do not give` : 'Pre-poured, not yet administered';
}

/* administration window: a pre-poured dose may be given only within PREPOUR_ADMIN_WINDOW_HOURS of its slot time,
   before or after. Past days and future days are therefore read-only. */
const windowHours = PREPOUR_ADMIN_WINDOW_HOURS;
function slotDate(day, time){
  // to24 reads a scheduled "9:30pm" with its minutes; read as an hour only, it was midnight, and the cup's window
  // said "Window closed" all day
  const hm = to24(time);
  const d = new Date(day + 'T00:00:00');
  if (hm) d.setHours(Number(hm.slice(0, 2)), Number(hm.slice(3, 5)), 0, 0);
  return d;
}
function fmtMins(n){ const h = Math.floor(n / 60), r = n % 60; return h ? `${h} h${r ? ` ${r} min` : ''}` : `${r} min`; }
function windowOf(g){
  const diff = Math.round((slotDate(g.slots[0].day, g.time) - now.value) / 60000);
  const lim = windowHours * 60;
  if (Math.abs(diff) <= lim) return { open: true, text: '' };
  return diff > 0 ? { open: false, text: `Too early — opens in ${fmtMins(diff - lim)}` }
                  : { open: false, text: `Window closed ${fmtMins(-diff - lim)} ago` };
}

/* A time block the caregiver has finished starts folded, so the day reads as what is still to give;
   one with anything pending starts open. Once the caregiver folds or opens a block, that choice holds
   for the block — until the day changes. */
const groupOpen = reactive({});
function groupKey(g){ return g.slots[0].day + '|' + g.time; }
// under a History outcome filter every block starts open: the filter was chosen to see those rows
function isGroupOpen(g){ const k = groupKey(g); return k in groupOpen ? groupOpen[k] : (outcomeFiltering.value || pendingIn(g).length > 0); }
function toggleGroup(g){ groupOpen[groupKey(g)] = !isGroupOpen(g); }

/* administration is recorded per TIME BLOCK: the caregiver gives "all the 8am meds", not one pill at a time */
function pendingIn(g){ return g.slots.filter(s => !admin.value[s.key] && !s.held); }
function groupSummary(g){
  const held = g.slots.filter(s => s.held && !admin.value[s.key]).length;
  const done = g.slots.length - pendingIn(g).length - held;
  const none = isPastGroup(g) ? 'Not recorded' : 'Not yet given';
  if (!done && held) return held === g.slots.length ? 'Held — not given' : `${none} · ${held} held`;
  if (!done) return none;
  const counts = {}; g.slots.forEach(s => { const r = admin.value[s.key]; if (r) counts[r.outcome] = (counts[r.outcome] || 0) + 1; });
  const parts = Object.entries(counts).map(([k, n]) => `${n} ${OUTCOMES[k].label.toLowerCase()}`);
  return (done === g.slots.length ? 'Given · ' : `${done} of ${g.slots.length} given · `) + parts.join(', ');
}
const witness = reactive({ open: false, time: '', day: '', slots: [], name: '', outcome: 'taken', exceptedKey: '', notes: '', actualTime: '' });
const witnessValid = computed(() => witness.name.trim() && (witness.outcome !== 'except' || witness.notes.trim()));
function nowHm(){ const d = new Date(); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; }
function openGroupWitness(g){
  if (!windowOf(g).open) return;
  Object.assign(witness, { open: true, time: g.time, day: g.slots[0].day, slots: pendingIn(g), name: '', outcome: 'taken', exceptedKey: '', notes: '', actualTime: nowHm() });
}
async function confirmWitness(){
  if (!witnessValid.value) return;
  const at = new Date().toISOString(), who = witness.name.trim();
  const next = { ...admin.value };
  const recorded = [];
  witness.slots.forEach(s => {
    // "Except for" without an identified med applies to the whole block: nobody can say which pill was skipped
    const excepted = witness.outcome === 'except' && (!witness.exceptedKey || witness.exceptedKey === s.key);
    const outcome = witness.outcome === 'refused' ? 'refused' : (excepted ? 'except' : 'taken');
    next[s.key] = { outcome, witness: who, notes: excepted ? witness.notes.trim() : '', actualTime: witness.actualTime, at, block: witness.time };
    recorded.push({ itemid: s.itemId, outcome, notes: next[s.key].notes });
  });
  /* A real patient's doses are recorded in keyon, which checks the day, the window and that each is given
     once; the page then shows what keyon kept. A refusal leaves every dose as it was. */
  if (prepourApi.isRemote(patientKey.value)) {
    try {
      await prepourApi.recordAdministration(pourCtx.value, { witness: who, actualTime: witness.actualTime, slots: recorded });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Not recorded', text: e.message });
      return;
    }
    admin.value = await prepourApi.fetchAdministrations(patientKey.value, pourCtx.value, pours.value).catch(() => next);
    witness.open = false;
    return;
  }
  admin.value = next;
  prepourApi.saveAdministrations(patientKey.value, admin.value);
  witness.open = false;
}
const detail = reactive({ open: false, slot: null, rec: null });
function showSlot(s){ Object.assign(detail, { open: true, slot: s, rec: admin.value[s.key] }); }

/* ---- discontinue (= inactivate): the opposite of signing a med off ---- */
/* Patient self-managed path (PSP-8): one signer, no countersignature. */
const patientDc = reactive({ open: false, med: null, reason: '' });
const patientDcPendingCount = computed(() => patientDc.med ? allSlots.value.filter(s => s.medId === patientDc.med.id && !admin.value[s.key]).length : 0);
function openPatientDiscontinue(m){ Object.assign(patientDc, { open: true, med: m, reason: '' }); }
function confirmPatientDiscontinue(){
  if (!patientDc.med) return;
  const rec = { reason: patientDc.reason.trim(), state: 'complete', signer: staff.value.name || 'patient',
    signerName: staff.value.name, signerId: staff.value.id, signerRole: staff.value.role, at: new Date().toISOString() };
  prepourApi.saveDiscontinue(patientKey.value, patientDc.med.id, rec);
  withdrawPendingSlots(patientDc.med.id);
  Object.assign(patientDc.med, { dc: rec, status: 'Discontinue', times: [], skip: false, picking: false, repour: false });
  patientDc.open = false;
}

/* Caregiver / physician path: the two-signature pop-up. */
const dcSign = reactive({ open: false, med: null, times: null, reason: '', notice: '', stage: 'request', requestedBy: 'caregiver',
  caregiver: { signature: '', at: '', id: '', role: '' }, physician: { signature: '', at: '', id: '', role: '' } });
const sigPad = reactive({ open: false, role: '', value: '' });
const dcSignPendingCount = computed(() => dcSign.med ? allSlots.value.filter(s => s.medId === dcSign.med.id && !admin.value[s.key]).length : 0);
/* Orders only opens its pad once VerifyAccountSignature confirms the role; here the signed-in role stands in.
   An account that is neither (an Administrator, or a role lookup that came back empty) can sign either card so
   a single demo login can walk both halves. The backend applies the real check once this is wired up. */
const roleUnverified = computed(() => !isNurseRole(staff.value.role) && !isProviderRole(staff.value.role));
function canSign(role){
  if (dcSign[role].signature) return false;
  if (roleUnverified.value) return true;
  return role === 'caregiver' ? isNurseRole(staff.value.role) : isProviderRole(staff.value.role);
}
function roleLabel(role){ return role === 'caregiver' ? 'Caregiver signature' : 'Physician signature'; }
/* The card this user signs. An unverified role (Administrator, or a lookup that came back empty) falls back
   to the requester's card so a single demo login can still walk the flow. */
const myRole = computed(() => {
  if (isNurseRole(staff.value.role)) return 'caregiver';
  if (isProviderRole(staff.value.role)) return 'physician';
  return dcSign.stage === 'countersign' ? otherRole(dcSign.requestedBy) : 'caregiver';
});
function otherRole(role){ return role === 'physician' ? 'caregiver' : 'physician'; }
/* signed | mine (yours to sign now) | waiting (theirs) — drives the card, the badge and the colour. */
function sigCardState(role){
  if (dcSign[role].signature) return 'signed';
  return canSign(role) ? 'mine' : 'waiting';
}
function sigBadge(role){
  const st = sigCardState(role);
  return st === 'signed' ? 'Signed' : st === 'mine' ? 'Your signature' : 'Awaiting';
}
const dcSignInstruction = computed(() => {
  const mine = myRole.value;
  if (dcSign[mine].signature) return `Signed as ${roleLabel(mine).toLowerCase()}. ${otherRole(mine) === 'physician' ? 'A physician' : 'A caregiver'} still has to sign before the medication stops.`;
  return `You are signing as the ${mine}. Sign your card below — ${otherRole(mine) === 'physician' ? 'a physician' : 'a caregiver'} signs the other one.`;
});
/* Submit needs this user's own signature, whichever side they are on: a physician can raise a discontinue
   and wait on a caregiver just as a caregiver can raise one and wait on a physician. */
const dcSignCanSubmit = computed(() => !!dcSign[myRole.value].signature);
const dcSignSubmitLabel = computed(() => {
  if (dcSign.caregiver.signature && dcSign.physician.signature) return 'Complete discontinue';
  return `Send for ${otherRole(myRole.value)} signature`;
});
const dcSignBlockedWhy = computed(() => {
  if (!dcSign[myRole.value].signature) return `Sign the ${myRole.value} card above to send this on.`;
  return '';
});
function openDcSign(m, opts = {}){
  const rec = isDcPending(m) ? m.dc : null;
  const by = rec ? (rec.requestedBy || 'caregiver') : (isProviderRole(staff.value.role) ? 'physician' : 'caregiver');
  const signed = rec ? { signature: rec.requesterSignature || rec.caregiverSignature || rec.signerName || '', at: rec.at, id: rec.signerId || '', role: rec.signerRole || '' }
                     : { signature: '', at: '', id: '', role: '' };
  const blank = { signature: '', at: '', id: '', role: '' };
  Object.assign(dcSign, {
    open: true, med: m, reason: rec ? rec.reason : (opts.reason || ''), notice: opts.notice || '',
    stage: rec ? 'countersign' : 'request', requestedBy: by,
    times: rec ? (rec.times || null) : (opts.times || null),
    caregiver: by === 'caregiver' ? signed : { ...blank },
    physician: by === 'physician' ? signed : { ...blank },
  });
}
function closeDcSign(){ dcSign.open = false; sigPad.open = false; }
function openSigPad(role){ if (!canSign(role)) return; Object.assign(sigPad, { open: true, role, value: staff.value.name || '' }); }
function applySigPad(){
  const v = sigPad.value.trim();
  if (!v || !sigPad.role) return;
  Object.assign(dcSign[sigPad.role], { signature: v, at: new Date().toISOString(), id: staff.value.id, role: staff.value.role });
  sigPad.open = false;
}
/* Local only — see the note above on issues #311 and #312. */
function submitDcSign(){
  if (!dcSignCanSubmit.value || !dcSign.med) return;
  const by = dcSign.requestedBy, other = otherRole(by);
  const complete = !!(dcSign.caregiver.signature && dcSign.physician.signature);
  const rec = {
    reason: dcSign.reason.trim(), state: complete ? 'complete' : 'pending', requestedBy: by,
    caregiverSignature: dcSign.caregiver.signature, caregiverAt: dcSign.caregiver.at,
    physicianSignature: dcSign.physician.signature, physicianAt: dcSign.physician.at,
    requesterSignature: dcSign[by].signature,
    signer: dcSign[by].signature, signerName: dcSign[by].signature,
    signerId: dcSign[by].id, signerRole: dcSign[by].role || (by === 'physician' ? 'Provider' : 'Registered Nurse'),
    at: dcSign[by].at || new Date().toISOString(),
    countersignedBy: complete ? dcSign[other].signature : undefined,
    completedAt: complete ? dcSign[other].at : undefined,
    times: dcSign.times || undefined,
  };
  prepourApi.saveDiscontinue(patientKey.value, dcSign.med.id, rec);
  // Nothing is withdrawn until the discontinue is fully signed.
  if (complete) withdrawPendingSlots(dcSign.med.id, dcSign.times);
  const m = dcSign.med, times = dcSign.times;
  logChange(m, complete ? 'Discontinue' : 'DC-pending',
    complete ? `Discontinued ${timesText(times)}` : `Discontinue ${timesText(times)} requested — waits for a second signature`, rec.reason);
  if (complete && times) {
    // one dose discontinued: the medication runs on at its other times
    Object.assign(m, { dc: null, status: m.statusRec ? m.status : 'Active', dcTimes: [...(m.dcTimes || []), ...times], times: m.times.filter(t => !times.includes(t)) });
  } else {
    Object.assign(m, complete
      ? { dc: rec, status: 'Discontinue', times: [], skip: false, picking: false, repour: false }
      : { dc: rec, status: 'DC-pending' });
  }
  closeDcSign();
}
/* Orders accepts a discontinue reason of 10 characters or more (DiscontinueModal.vue) and shows this error
   in between. Same rule and same wording here so the two modules cannot drift on validation. */
/* Every order needs one provider signature and one nurse signature, so the initiator's role decides who is
   still owed. Role text comes from keyon GetAccountType; anything unrecognised is left to the backend. */
const isNurseRole = r => /nurse/i.test(r || '') && !/practitioner/i.test(r || '');
const isProviderRole = r => /provider|physician|practitioner/i.test(r || '');
function counterpartOf(role){
  if (isNurseRole(role)) return 'a provider';
  if (isProviderRole(role)) return 'a nurse';
  return 'the second signer';
}
const dcCounterpartLabel = computed(() => {
  const c = counterpartOf(staff.value.role);
  return c === 'the second signer' ? 'The second signer' : c[0].toUpperCase() + c.slice(1);
});
/* The counterpart signs, never the person who asked. */
function canCountersign(m){
  if (!isDcPending(m) || !canDiscontinue.value) return false;
  if (roleUnverified.value) return true;
  if (m.dc.signerId && m.dc.signerId === staff.value.id) return false;
  const mine = staff.value.role;
  if (isNurseRole(m.dc.signerRole)) return isProviderRole(mine);
  if (isProviderRole(m.dc.signerRole)) return isNurseRole(mine);
  return false;
}
function statusLabel(s){ return s === 'DC-pending' ? 'Discontinue pending' : s; }
/* withdraw the med's pre-poured slots that were never recorded as given; recorded ones stay on the pour */
function withdrawPendingSlots(medId, onlyTimes = null){
  pours.value = pours.value.map(p => {
    if (!p.meds.some(pm => pm.id === medId)) return p;
    const keep = t => (onlyTimes && !onlyTimes.includes(t)) || p.days.some(d => admin.value[slotKey(p.id, d, medId, t)]);
    const meds = p.meds
      .map(pm => pm.id !== medId ? pm : { ...pm, times: pm.times.filter(keep) })
      .filter(pm => pm.times.length);
    return { ...p, meds };
  }).filter(p => p.meds.length);
  prepourApi.savePours(patientKey.value, pours.value);
}
/* Local only, on purpose. Calling keyon's DiscontinueOrder today archives the original order on this first
   call and creates a discontinue order no second signature can complete, which is the behaviour issues #311
   and #312 ask about. Until those are answered the prototype keeps these records in localStorage; swapping
   submitDcSign's body for the Orders call is the whole of the wiring work.
   Discontinuing also needs an NPI-bearing signer (stakeholder, 2026-09-15); the prototype has no NPI source
   for the logged-in user, so the backend enforces that when this stops being local-only. */

/* ---- medication status: Hold / New / Change / Pending / Completed (review 2026-09-25) ----
   The stakeholder's own prototype and the nursing module both change a medication's status from its
   row, and each status asks when it applies, to which times, and why. Hold is the one the review
   insisted on: "not today or not tomorrow" — a pause over dates, possibly for one time only, which is
   not a discontinue. Held and completed doses are not poured, and Administer shows them as held.
   Local (key 'status') until the backend is wired. The dialog's payload mirrors the nursing module's
   HoldTimeSelector ({ statusOption, dateRange, times, holdType, reason }), and new_medications already
   has medHoldDates / medStatusTimes / medChangeType / medChangeReason to receive it. */
function STATUS_DEFAULTS(){ return { statusRec: null, holds: [], timeDoses: {}, dcTimes: null }; }
const STATUS_OPTIONS = ['Active', 'Hold', 'New', 'Change', 'Pending', 'Completed', 'Discontinue'];
// statuses with a pop-up; Active and Pending apply at once, Discontinue has its own two-signature flow
const STATUS_ASKS = { Hold: 'range', New: 'date', Change: 'date', Completed: 'date' };
const STATUS_VERB = { Hold: 'Hold', New: 'New for', Change: 'Change', Completed: 'Complete' };

function saveStatus(m){
  prepourApi.saveStatus(patientKey.value, m.id, { rec: m.statusRec, holds: m.holds, timeDoses: m.timeDoses });
}
/* statusRec holds only the latest status, so each change is also appended here: History has to answer
   "what was changed" for a past day (review 2026-09-25). One entry per change, filed under the day it was
   made. Local (key 'changes') until the backend is wired — medChangeType / medChangeReason /
   medStatusTimes are its columns. */
const changes = ref([]);
function logChange(m, status, text, reason = '', label = ''){
  const at = new Date().toISOString();
  changes.value = [...changes.value, { medId: m.id, name: m.name, status, label, text, reason, by: staff.value.name || '', at, day: today.value }];
  prepourApi.saveChanges(patientKey.value, changes.value);
}
/* Does a hold/status entry cover this dose? `time` null means a dose not placed on a time yet, which
   only an all-times entry can cover. */
function covers(e, day, time){
  if (day < e.start || day > e.end) return false;
  const all = !e.times || !e.times.length;
  return time == null ? all : (all || e.times.includes(time));
}
function holdOn(m, day, time){ return (m.holds || []).find(h => covers(h, day, time)) || null; }
function completedOn(m, day, time){
  const r = m.statusRec;
  return r && r.status === 'Completed' && covers({ ...r, end: '9999-12-31' }, day, time) ? r : null;
}
/* a day after the course's end date is stopped too - GetPatientMeds keeps carrying today's slots for
   a course that ended, so it was offered and poured */
function courseEnded(m, day){ return courseEndedOn(m.record && m.record.endDate, day); }
function isStoppedSlot(m, day, time){ return !!(holdOn(m, day, time) || completedOn(m, day, time) || courseEnded(m, day)); }
function stopReason(m, day, time){
  const h = holdOn(m, day, time);
  if (h) return 'On hold' + (h.reason ? ' — ' + h.reason : '');
  const c = completedOn(m, day, time);
  if (c) return 'Completed' + (c.reason ? ' — ' + c.reason : '');
  return courseEnded(m, day) ? 'Course ended' : '';
}
function stoppedSlotsIn(m, days){
  const out = [];
  days.forEach(d => m.times.forEach(t => { if (isStoppedSlot(m, d, t)) out.push({ day: d, time: t }); }));
  return out;
}
/* every dose in every day of the range is held or completed: nothing to pour, so it is not a candidate */
function isFullyStopped(m){
  const days = editableDays(m);
  return rangeDays.value.length > 0 && days.length > 0 && days.every(d => isStoppedSlot(m, d, null) && m.times.every(t => isStoppedSlot(m, d, t)));
}
function doseAt(m, t){ return (m.timeDoses && m.timeDoses[t]) || m.dose || 1; }
function heldInRange(m){ return (m.holds || []).filter(h => rangeDays.value.some(d => d >= h.start && d <= h.end)); }

/* The status a row shows. Discontinue wins; a hold anywhere in the range shows as Hold; then whatever
   the caregiver set; then Partial when the bottle runs out inside the range, as in the stakeholder's
   prototype; then the medication's own status. */
function effStatus(m){
  if (m.status === 'DC-pending' || m.status === 'Discontinue') return m.status;
  if (heldInRange(m).length) return 'Hold';
  if (m.statusRec && m.statusRec.status) return m.statusRec.status;
  if (supplyDays(m).unavailable.length) return 'Partial';
  return m.status || 'Active';
}
const isRemoteSheet = computed(() => prepourApi.isRemote(patientKey.value));
/* For a real patient New and Pending are keyon's (derivedStatusOf): shown as the status, never offered as a choice. */
const DERIVED_STATUSES = ['New', 'Pending'];
function statusOptionsFor(m){
  const eff = effStatus(m);
  const opts = STATUS_OPTIONS.filter(o => (o !== 'Discontinue' || canDiscontinue.value)
    && !(prepourApi.isRemote(patientKey.value) && DERIVED_STATUSES.includes(o)));
  return opts.includes(eff) ? opts : [eff, ...opts];
}
function canChangeStatus(m){ return canPrepour.value && !isDcPending(m) && !isDcDone(m); }

const stDlg = reactive({ open: false, med: null, status: '', extra: null, scope: 'all', picked: [], keep: [], start: '', end: '', reason: '', doses: {} });
/* The select always shows the real status: a choice only takes effect on Submit, and Cancel leaves the
   row as it was (the stakeholder's prototype left the cancelled option showing). */
function chooseStatus(m, e){
  const to = e.target.value;
  e.target.value = effStatus(m);
  if (to === 'Discontinue') { openDcSign(m); return; }
  if (to === 'Active') { setActive(m); return; }
  if (to === 'Pending') {
    m.statusRec = { status: 'Pending', at: new Date().toISOString(), by: staff.value.name || '' };
    saveStatus(m); logChange(m, 'Pending', 'Marked pending'); return;
  }
  if (STATUS_ASKS[to]) openStatusDialog(m, to);
}
function statusTimesFor(m){ return m.times.length ? [...m.times] : (lockInfo(m) ? [...lockInfo(m).times] : []); }
function openStatusDialog(m, status, extra = null){
  const first = rangeDays.value.find(d => d >= today.value) || today.value;
  Object.assign(stDlg, { open: true, med: m, status, extra, scope: 'all', picked: [], keep: [], start: first,
    end: status === 'Hold' ? first : '', reason: extra && extra.freqTo ? `Frequency changed from ${extra.freqFrom} to ${extra.freqTo}` : '',
    doses: Object.fromEntries(statusTimesFor(m).map(t => [t, doseAt(m, t)])) });
}
const stDlgTimes = computed(() => stDlg.med ? statusTimesFor(stDlg.med) : []);
const stDlgScopeTimes = computed(() => stDlg.scope === 'specific' ? stDlgTimes.value.filter(t => stDlg.picked.includes(t)) : stDlgTimes.value);
/* Doses per day after the frequency change in the pop-up (0 when it is not a frequency change). */
const stDlgFreqN = computed(() => {
  const m = stDlg.med, x = stDlg.extra;
  return m && x && x.freqTo ? Math.max(1, (doseCount(x.freqTo) || 1) - (m.dcTimes ? m.dcTimes.length : 0)) : 0;
});
// how many of the row's times survive a change that lowers the count (0 = nothing to pick)
const stDlgKeepCount = computed(() => stDlg.med && stDlg.med.times.length > stDlgFreqN.value ? stDlgFreqN.value : 0);
const stDlgNewSlots = computed(() => stDlg.med && stDlgFreqN.value ? Math.max(0, stDlgFreqN.value - stDlg.med.times.length) : 0);
const stDlgWhy = computed(() => {
  if (!stDlg.start) return STATUS_ASKS[stDlg.status] === 'range' ? 'Choose the first day of the hold.' : 'Choose the date.';
  if (STATUS_ASKS[stDlg.status] === 'range' && !stDlg.end) return 'Choose the last day of the hold.';
  if (STATUS_ASKS[stDlg.status] === 'range' && stDlg.end < stDlg.start) return 'The last day is before the first.';
  if (stDlg.scope === 'specific' && !stDlg.picked.length) return 'Tick at least one time.';
  const k = stDlgKeepCount.value;
  if (k && stDlg.keep.length !== k) return k === 1 ? 'Tick the time that stays.' : `Tick the ${k} times that stay.`;
  if (!stDlg.reason.trim()) return 'A reason is required.';
  // keyon applies a change at once, as nursing's change order does
  if (prepourApi.isRemote(patientKey.value) && stDlg.status === 'Change' && stDlg.start !== today.value) return 'A change takes effect today: choose today.';
  return '';
});
async function submitStatus(){
  const m = stDlg.med;
  if (!m || stDlgWhy.value) return;
  if (prepourApi.isRemote(patientKey.value) && stDlg.status === 'Completed') { await completeInKeyon(m); return; }
  if (prepourApi.isRemote(patientKey.value) && (stDlg.status === 'Hold' || stDlg.status === 'Change')) { await statusInKeyon(m); return; }
  // same shape as HoldTimeSelector's submit, so the backend wiring can take it as is
  const payload = {
    statusOption: stDlg.status, holdType: stDlg.scope,
    dateRange: [stDlg.start, STATUS_ASKS[stDlg.status] === 'range' ? stDlg.end : stDlg.start],
    times: stDlg.scope === 'specific' ? [...stDlg.picked] : null, reason: stDlg.reason.trim(),
  };
  const by = staff.value.name || '', at = new Date().toISOString();
  const [start, end] = payload.dateRange;
  if (payload.statusOption === 'Hold') {
    m.holds = [...(m.holds || []), { start, end, times: payload.times, reason: payload.reason, at, by }];
  } else {
    m.statusRec = { status: payload.statusOption, start, times: payload.times, reason: payload.reason, at, by,
      ...(stDlg.extra && stDlg.extra.freqTo ? { freqFrom: stDlg.extra.freqFrom, freqTo: stDlg.extra.freqTo } : {}) };
  }
  let text = payload.statusOption === 'Hold' ? `On hold ${fmtSpan(start, end)} · ${timesText(payload.times)}`
                                            : `From ${fmtDay(start)} · ${timesText(payload.times)}`;
  if (payload.statusOption === 'Change') {
    const doses = {};
    stDlgScopeTimes.value.forEach(t => { const n = Number(stDlg.doses[t]); if (n > 0 && n !== (m.dose || 1)) doses[t] = n; });
    m.timeDoses = { ...(m.timeDoses || {}), ...doses };
    if (Object.keys(doses).length) text += ' · dose ' + Object.entries(doses).map(([t, n]) => `${n} at ${t}`).join(', ');
    if (stDlg.extra && stDlg.extra.freqTo) {
      const keep = stDlgKeepCount.value ? [...stDlg.keep] : null;
      text += ` · ${stDlg.extra.freqFrom} → ${stDlg.extra.freqTo}${keep ? ' · keeps ' + keep.join(', ') : ''}`;
      applyFreqChange(m, stDlg.extra.freqTo, keep);
    }
  }
  saveStatus(m);
  logChange(m, payload.statusOption, text, payload.reason);
  stDlg.open = false;
}
/* A real patient's Completed is keyon's to allow (the end-date rule, keyon/lib/MedicationCompletion.php).
   Stopping a course before its end, or one with no end date, is a discontinue: keyon answers
   needs_discontinue and the caregiver is taken there with the reason they already gave. */
async function completeInKeyon(m){
  const reason = stDlg.reason.trim(), date = stDlg.start;
  try {
    await prepourApi.completeMedication(pourCtx.value, { medentryid: m.id, date, reason });
  } catch (e) {
    if (e.code === 'needs_discontinue') { stDlg.open = false; openDcSign(m, { reason, notice: e.message }); return; }
    Swal.fire({ icon: 'error', title: 'Not completed', text: e.message });
    return;
  }
  stDlg.open = false;
  logChange(m, 'Completed', `Completed from ${fmtDay(date)}`, reason);
  await loadMeds();
  await refreshPours();
}
/* A real patient's hold and change go to nursing's endpoints (#336): HoldMedication and
   ChangeOrderForMedLogTimes. keyon writes the History entry; the page reads the medications, holds and
   History back. A prescription change is a change order the prescriber signs; a non-prescription one
   applies at once. */
async function statusInKeyon(m){
  const reason = stDlg.reason.trim();
  const hold = stDlg.status === 'Hold';
  try {
    if (hold) {
      await prepourApi.holdMedication(pourCtx.value, m, { start: stDlg.start, end: stDlg.end || stDlg.start,
        times: stDlg.scope === 'specific' ? [...stDlg.picked] : null, reason });
    } else {
      const change = keyonChangeOf(m);
      if (change.refuse) { Swal.fire({ icon: 'info', title: 'Nothing sent', text: change.refuse }); return; }
      await prepourApi.changeMedication(pourCtx.value, m, { ...change, reason });
    }
  } catch (e) {
    Swal.fire({ icon: 'error', title: hold ? 'Not held' : 'Not changed', text: e.message });
    return;
  }
  stDlg.open = false;
  await loadMeds();
  await refreshPours();
}
/* The change keyon records: one dose for every time (its order has one), the frequency, and as many times as
   the frequency gives - the sheet's (or the ones kept in the pop-up), topped up with standard hours for a raised
   frequency, whose new doses the caregiver places on the sheet. The dialog's dose is tablets; keyon's is the
   order's amount, which is tablets only when its unit is a count. */
function keyonChangeOf(m){
  const x = stDlg.extra, freqChange = !!(x && x.freqTo);
  const picked = [...new Set(stDlgScopeTimes.value.map(t => Number(stDlg.doses[t]) || m.dose || 1))];
  if (picked.length > 1) return { refuse: 'This medication has one dose for every time. Give every time the same dose.' };
  const tablets = picked.length ? picked[0] : (m.dose || 1);
  const doseChange = tablets !== (m.dose || 1);
  if (!freqChange && !doseChange) return { refuse: 'Nothing changed: choose a new dose or frequency.' };
  const r = m.keyon || {};
  const countUnit = COUNT_UOM.test(String(r.med_doseuom || '').trim());
  if (doseChange && !countUnit) return { refuse: `This dose is ordered in ${r.med_doseuom || 'another unit'}, not tablets. Change it in Medication Administration.` };
  const n = freqChange ? stDlgFreqN.value : Math.max(1, m.times.length || keyonTimesOf(r).length || doseCount(m.freq));
  const kept = freqChange && stDlgKeepCount.value ? [...stDlg.keep] : (m.times.length ? m.times : keyonTimesOf(r));
  return { dose: doseChange ? tablets : (r.med_amount || tablets), freq: freqChange ? x.freqTo : m.freq, times: fillTimes(kept.map(to24), n) };
}
/* Releasing a real patient's hold (ClearCurrentMedicationHold): its held times from today on are due again. */
async function releaseInKeyon(m){
  if (!activeHolds(m).length) return;
  try {
    await prepourApi.releaseHold(pourCtx.value, m);
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'Not released', text: e.message });
    return;
  }
  await loadMeds();
  await refreshPours();
}
/* Back to Active: the status note clears and any hold that has not ended yet is released. */
function setActive(m){
  if (prepourApi.isRemote(patientKey.value)) { releaseInKeyon(m); return; }
  const released = (m.holds || []).filter(h => h.end >= today.value).length;
  m.statusRec = null;
  m.holds = (m.holds || []).filter(h => h.end < today.value);
  saveStatus(m);
  logChange(m, 'Active', 'Back to Active' + (released ? ` · ${released} hold(s) released` : ''));
}
function heldAllRange(m, t){ const days = editableDays(m); return days.length > 0 && days.every(d => isStoppedSlot(m, d, t)); }
function releaseHold(m, h){
  if (prepourApi.isRemote(patientKey.value)) {
    // keyon releases a medication's holds from today on together, so releasing one of several asks first
    const others = activeHolds(m).filter(x => x !== h);
    if (!others.length) { releaseInKeyon(m); return; }
    Promise.resolve(Swal.fire({ icon: 'warning', title: 'Release every hold?', showCancelButton: true,
      text: `Releasing a hold on ${shortName(m)} releases all its holds from today on, including ${others.map(x => fmtSpan(x.start, x.end)).join(', ')}.`,
      confirmButtonText: 'Release all', cancelButtonText: 'Keep them' }))
      .then(r => { if (r && r.isConfirmed) releaseInKeyon(m); });
    return;
  }
  m.holds = (m.holds || []).filter(x => x !== h); saveStatus(m);
  logChange(m, 'Active', `Hold ${fmtSpan(h.start, h.end)} released`, '', 'Released');
}

/* Which days the bottle covers. Days are filled in order, a whole day at a time — a day's cups are
   either poured or not — and the rest are "Unavailable", as the stakeholder's prototype shows them. */
function supplyDays(m){
  const covered = [], unavailable = [];
  if (!m.pourable || m.prn || isFullyLocked(m) || m.skip) return { covered, unavailable };
  let left = m.onhand || 0;
  editableDays(m).forEach(d => {
    const u = unitsOn(m, d);
    if (!u) return;
    if (left >= u) { left -= u; covered.push(d); } else { left = 0; unavailable.push(d); }
  });
  return { covered, unavailable };
}
function fmtSpan(a, b){ return !b || a === b ? fmtDay(a) : fmtDay(a) + ' – ' + fmtDay(b); }
/* consecutive days as ranges: "Sep 26 – Sep 28, Oct 1" */
function fmtDays(list){
  const runs = [];
  list.forEach(d => { const r = runs[runs.length - 1]; if (r && addDays(r[1], 1) === d) r[1] = d; else runs.push([d, d]); });
  return runs.map(([a, b]) => fmtSpan(a, b)).join(', ');
}
function timesText(t){ return t && t.length ? 'at ' + t.join(', ') : 'all times'; }
/* One line per thing the caregiver should know before pouring this med: holds, a status with its date,
   discontinued doses, and where the supply runs out. Every layout shows the same lines. */
function medNotes(m){
  const out = [];
  heldInRange(m).forEach(h => out.push({ cls: 'Hold', text: `On hold ${fmtSpan(h.start, h.end)} · ${timesText(h.times)}${h.reason ? ' — ' + h.reason : ''}` }));
  const r = m.statusRec;
  if (r && STATUS_ASKS[r.status]) {
    out.push({ cls: r.status, text: `${r.status} from ${fmtDay(r.start)} · ${timesText(r.times)}${r.freqTo ? ` · ${r.freqFrom} → ${r.freqTo}` : ''}${r.reason ? ' — ' + r.reason : ''}` });
  }
  if (m.timeDoses && Object.keys(m.timeDoses).length) {
    out.push({ cls: 'Change', text: 'Dose by time: ' + Object.entries(m.timeDoses).map(([t, n]) => `${n} at ${t}`).join(', ') });
  }
  if (m.dcTimes && m.dcTimes.length) out.push({ cls: 'Discontinue', text: `Discontinued ${timesText(m.dcTimes)}` });
  if (isDcPending(m) && m.dc.times) out.push({ cls: 'DC-pending', text: `Discontinue ${timesText(m.dc.times)} waits for a second signature` });
  const sup = supplyDays(m);
  if (sup.unavailable.length) {
    out.push({ cls: 'Partial', text: sup.covered.length
      ? `Supply covers ${fmtDays(sup.covered)} · unavailable ${fmtDays(sup.unavailable)}`
      : `Not enough on hand — unavailable ${fmtDays(sup.unavailable)}` });
  }
  return out;
}

/* ---- status filters (review 2026-09-25) ----
   "You can see at the top when something was discontinued or held or is new or pending" — the nursing
   module's filters, here as a bar over the sheet. A filter opens a list of those medications with their
   dates and reasons; it does not hide anything on the sheet. */
const STATUS_FILTERS = [
  { key: 'New', label: 'New', match: m => effStatus(m) === 'New' },
  { key: 'Hold', label: 'Held', match: m => (m.holds || []).some(h => h.end >= today.value) },
  { key: 'Discontinue', label: 'Discontinued', match: m => isDcPending(m) || isDcDone(m) || !!(m.dcTimes && m.dcTimes.length) },
  { key: 'Pending', label: 'Pending', match: m => effStatus(m) === 'Pending' },
  { key: 'Change', label: 'Change', match: m => effStatus(m) === 'Change' },
  { key: 'Completed', label: 'Completed', match: m => effStatus(m) === 'Completed' },
  { key: 'Partial', label: 'Partial', match: m => effStatus(m) === 'Partial' },
  { key: 'PRN', label: 'PRN', match: m => m.prn },
  { key: 'NonRx', label: 'Non-Rx', match: m => m.nonPrescription },
];
const statusFilter = ref(null);
function filterCount(f){ return meds.value.filter(f.match).length; }
function toggleStatusFilter(k){ statusFilter.value = statusFilter.value === k ? null : k; }
const statusFilterDef = computed(() => STATUS_FILTERS.find(f => f.key === statusFilter.value) || null);
const statusFilterMeds = computed(() => statusFilterDef.value ? meds.value.filter(statusFilterDef.value.match) : []);
function filterLines(m){
  if (statusFilter.value === 'Discontinue') {
    if (m.dc) return [`${isDcPending(m) ? 'Requested' : 'Discontinued'} ${fmtStamp(m.dc.at)}${m.dc.times ? ' · ' + timesText(m.dc.times) : ''}${m.dc.reason ? ' — ' + m.dc.reason : ''}`];
    return [`Discontinued ${timesText(m.dcTimes)}`];
  }
  if (statusFilter.value === 'PRN') return [`${m.freq} · on demand, never pre-poured`];
  if (statusFilter.value === 'NonRx') return ['Added without a prescription · no order'];
  if (statusFilter.value === 'Hold') return [];     // listed with a Release button each
  return medNotes(m).filter(n => n.cls === statusFilter.value || statusFilter.value === 'Partial').map(n => n.text);
}
function activeHolds(m){ return (m.holds || []).filter(h => h.end >= today.value); }

/* ---- pour marks (review 2026-09-25) ----
   Caregivers are interrupted mid-pour; each dose already in its cup is marked so they can pick up where
   they left off without tipping the cups out to check. Two ways to mark, to compare in practice:
   tapping the time itself (layouts A, C, D) or a Mark poured button (B, E). Kept with the draft and
   cleared on sign-off. */
// pourMarks (medId -> { time: true }) is declared with the page state near the top: the draft watcher reads it
const MARK_BY_TAP = ['A', 'C', 'D'];
const markByTap = computed(() => MARK_BY_TAP.includes(variant.value));
function isPoured(m, t){ return !!(pourMarks[m.id] && pourMarks[m.id][t]); }
function togglePoured(m, t){ const cur = pourMarks[m.id] || {}; pourMarks[m.id] = { ...cur, [t]: !cur[t] }; }
function allPoured(m){ return m.times.length > 0 && m.times.every(t => isPoured(m, t)); }
function toggleAllPoured(m){ const on = !allPoured(m); pourMarks[m.id] = Object.fromEntries(m.times.map(t => [t, on])); }
const pouredCount = computed(() => pourCandidates.value.filter(allPoured).length);

/* ---- auto-fill switch ----
   On: hours are filled in for each medication from its frequency, marked Auto, as before. Off: nothing
   is placed for the caregiver and every time is chosen by hand — the stakeholder's rule for new slots
   ("it shouldn't have a time in it"). Remembered per browser so a reviewer can compare the two. */
function readAutoFill(){ try { return localStorage.getItem('ppProto:autofill') !== '0'; } catch (e) { return true; } }
const autoFill = ref(readAutoFill());
watch(autoFill, on => {
  try { localStorage.setItem('ppProto:autofill', on ? '1' : '0'); } catch (e) { /* private mode */ }
  if (on) { suggestPlacements(); return; }
  // off: take back only what the machine placed; the caregiver's own choices stay
  meds.value.forEach(m => { if (m.suggested) { m.times = []; m.suggested = false; } });
});

/* ---- the × on a time: move the dose, or discontinue it ---- */
const timeAct = reactive({ open: false, med: null, time: '', to: '' });
function askRemoveTime(m, t){ Object.assign(timeAct, { open: true, med: m, time: t, to: '' }); }
const timeActChoices = computed(() => timeAct.med ? HOUR_ORDER.filter(h => !timeAct.med.times.includes(h)) : []);
function moveTime(){
  const m = timeAct.med, from = timeAct.time, to = timeAct.to;
  if (!m || !to) return;
  if (prepourApi.isRemote(patientKey.value)) { moveTimeInKeyon(m, from, to); return; }
  applyMove(m, from, to);
  logChange(m, 'Change', `The ${from} dose moved to ${to}`, '', 'Time moved');
  timeAct.open = false;
}
function applyMove(m, from, to){
  const i = m.times.indexOf(from);
  if (i >= 0) m.times.splice(i, 1, to);
  claimRow(m); sortTimes(m);
  if (pourMarks[m.id]) delete pourMarks[m.id][from];
  if (!palette.value.includes(to)) togglePalette(to);     // a new hour gets its own cup
}
/* A real patient's time move goes to keyon as nursing's "just update times" (InsertUpdateMedLogTimes): the
   sheet's hours are the medication's times. A held medication is refused - release the hold first. */
async function moveTimeInKeyon(m, from, to){
  if (activeHolds(m).some(h => h.start <= today.value)) {
    Swal.fire({ icon: 'info', title: 'On hold', text: 'Release the hold first, then move the time.' });
    return;
  }
  // keyon's own times with this one moved: the sheet's could be behind keyon (a frequency change adds times), and
  // sending those replaced keyon's schedule - a move after raising a medication to twice daily dropped its 20:00 dose
  const times = isKeyonTimed(m) ? movedKeyonTimes(m.keyon, from, to) : m.times.map(t => (t === from ? to : t));
  if (!times) {
    Swal.fire({ icon: 'info', title: 'Times changed', text: `${shortName(m)} is no longer due at ${from}. Reload the page and try again.` });
    return;
  }
  try {
    await prepourApi.moveTimes(pourCtx.value, m, { times, reason: `The ${from} dose moved to ${to}` });
  } catch (e) {
    Swal.fire({ icon: 'error', title: 'Not moved', text: e.message });
    return;
  }
  timeAct.open = false;
  if (pourMarks[m.id]) delete pourMarks[m.id][from];
  if (!isKeyonTimed(m)) { applyMove(m, from, to); await refreshKeyonStatus(); return; }
  await loadMeds();      // its new times are keyon's
}
function discontinueTime(){ const m = timeAct.med, t = timeAct.time; timeAct.open = false; openDcSign(m, { times: [t] }); }

/* ---- search (from the stakeholder's prototype) ---- */
const medQuery = ref('');
function matchesQuery(m){
  const q = medQuery.value.trim().toLowerCase();
  return !q || m.name.toLowerCase().includes(q) || String((m.record && m.record.diagnosis) || '').toLowerCase().includes(q);
}

/* ---- modes + reset ---- */
const mode = ref('pour');
function resetLocal(){
  if (!confirm('Clear local pours, administrations and on-hand overrides for this patient?')) return;
  prepourApi.clearLocal(patientKey.value); localStorage.removeItem(lsKey('draft'));
  admin.value = {}; changes.value = []; refreshPours(); loadMeds();
}

/* time-selector style: ?times=blocks|chips|tiles */
const TIMES_UI = ['blocks','chips','tiles','list','presets'];
const timesUi = computed(() => TIMES_UI.includes(route.query.times) ? route.query.times : 'blocks');
function setTimes(t){ router.replace({ query: { ...route.query, times: t } }); }
function onKey(e){
  const t = e.target.tagName;
  if (t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || e.target.isContentEditable) return;
  if (signOff.open || witness.open || detail.open || patientDc.open || dcSign.open || sigPad.open || mode.value !== 'pour') return;
  if (e.key === 'ArrowLeft') cycle(-1);
  if (e.key === 'ArrowRight') cycle(1);
}

onMounted(() => {
  refreshPours();   // and the administrations recorded against them
  changes.value = prepourApi.getChanges(patientKey.value);
  loadMeds(); loadStaffRole();
  clock = setInterval(() => { now.value = new Date(); }, 30000);
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => { clearInterval(clock); window.removeEventListener('keydown', onKey); });
watch(today, () => { adminDay.value = today.value; loadMeds(); });   // midnight: reload so past days drop out of the draft range and Administer follows the new day
watch(patientKey, () => { refreshPours(); changes.value = prepourApi.getChanges(patientKey.value); loadMeds(); loadStaffRole(); });
watch(canViewPrepour, granted => { if (granted) loadMeds(); else meds.value = []; });   // privileges load after mount
</script>

<style scoped>
.pp-proto{ --teal:#2f6b6e; --line:#dbe1e4; --muted:#5c666c; --low-b:#c0392b; --low-band:#fff4f4;
  --warn:#8a5a00; --warnbg:#fff6e5; --ok-b:#2e7d4f; --ok-bg:#e6f2ea; --exc:#c77700; --exc-bg:#fff1dc; --ref-bg:#fbe3e1;
  --st-active:#e6f2ea; --st-hold:#fbf1c9; --st-dc:#f7d9d9; --st-new:#dbe4f5; --st-pending:#ecdcf3;
  --st-change:#efd9bf; --st-completed:#d6f5d6; --st-partial:#f8d9e6;
  /* Scan bottle had the same teal as the page's main buttons (review 2026-09-25): it gets its own hue. */
  --scan:#3a5a9b; --poured:#2f8a5f; --poured-bg:#eaf6ef;
  max-width:1240px; margin:0 auto; padding:16px 20px 40px; color:#1e2529;
  font:16px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif; }
.pp-proto *, .pp-proto *::before, .pp-proto *::after{ box-sizing:border-box; }
.pp-noaccess{ background:var(--low-band); border:1px solid #e6b0aa; color:var(--low-b); border-radius:10px; padding:16px; }
/* Medications tab bar — mirrors CurrentMed.vue so the Prepour tab feels native */
.medications-header{ margin:4px 0 20px; }
.medications-title{ text-align:center; margin:0 0 15px; font-size:2.5em; color:var(--teal); }
.medications-nav{ display:flex; justify-content:center; align-items:center; gap:10px; flex-wrap:wrap; }
.nav-separator{ margin:0 5px; }
.nav-link{ text-decoration:none; font-size:2em; padding:5px 10px; color:var(--teal); }
.nav-link:hover{ text-decoration:underline; }
.nav-link.active{ font-weight:bold; color:#1e2529; pointer-events:none; }
.pp-head{ display:flex; align-items:center; gap:16px; padding:6px 0 14px; flex-wrap:wrap; }
.pp-pt{ font-weight:700; font-size:20px; } .pp-dob{ color:var(--muted); margin-left:10px; }
.pp-modes{ display:flex; background:#fff; border:1px solid var(--line); border-radius:10px; padding:4px; margin-bottom:16px; gap:4px; }
.pp-mode{ padding:10px 20px; font:inherit; font-size:16px; font-weight:600; border:none; border-bottom:3px solid transparent;
  background:transparent; color:var(--muted); cursor:pointer; border-radius:8px; }
.pp-mode:hover{ background:#eef4f4; }
.pp-mode.active{ color:#fff; background:var(--teal); border-bottom-color:var(--teal); }
.pp-mode .cnt{ display:inline-block; min-width:22px; padding:1px 7px; border-radius:6px; background:#e6ebec; color:#3a464c; font-size:13px; margin-left:6px; text-align:center; }
/* Names the layout on screen. `margin-left:auto` parks it at the far end of the tab strip so it
   reads as a label about the page rather than a fourth tab. */
.pp-variant-badge{ margin-left:auto; align-self:center; padding:4px 10px; border-radius:6px;
  background:#eef4f4; color:#3a464c; font-size:13px; font-weight:600; white-space:nowrap; }
.pp-mode.active .cnt{ background:rgba(255,255,255,.25); color:#fff; }
.pp-devbar{ display:flex; flex-wrap:wrap; align-items:center; gap:6px; background:#111a1c; color:#cfd8da; border-radius:6px; padding:8px 10px; margin-bottom:14px; font-size:13px; }
.pp-devbar span{ font-weight:700; text-transform:uppercase; letter-spacing:.05em; font-size:11px; margin:0 4px 0 6px; }
.pp-devbar button{ background:#2b3a3d; color:#fff; border:none; border-radius:4px; padding:5px 10px; font:inherit; font-size:13px; cursor:pointer; }
.pp-devbar button.on{ background:#7fe0d6; color:#0d2a2c; font-weight:700; }
.pp-devbar button.reset{ margin-left:auto; background:#5a2b2b; }
.pp-banner{ background:var(--warnbg); border:1px solid #f0d9a8; color:var(--warn); border-radius:8px; padding:10px 14px; margin-bottom:12px; font-size:15px; }
.pp-banner.lock{ background:#eef4f4; border-color:#cfe0e0; color:#245457; }
.pp-banner .chk{ display:block; margin-top:6px; font-weight:600; }
.pp-controls{ background:#fff; border:1px solid var(--line); border-radius:10px; padding:14px 16px; margin-bottom:16px; }
.pp-row{ display:flex; flex-wrap:wrap; gap:12px; align-items:center; }
.pp-lbl{ font-weight:600; font-size:15px; color:var(--muted); }
.pp-lbl.chk{ display:flex; align-items:center; gap:6px; }
.pp-pill{ border:1px solid var(--line); border-radius:8px; padding:9px 12px; background:#fff; font:inherit; font-size:15px; }
.pp-pill.muted{ color:var(--muted); background:#f6f8f8; }
.pp-pill.wide{ width:100%; box-sizing:border-box; }
.pp-dp{ min-width:230px; flex:1 1 230px; max-width:320px; }
/* Sized to what it actually holds — "09/23/2026 - 09/24/2026" and no more — instead of stretching to
   fill the row. Two of these plus a restated summary pill were taking the whole width. */
.pp-dp.narrow{ min-width:0; flex:0 0 auto; width:236px; max-width:100%; }
.pp-dp.narrow :deep(.dp__input){ padding:8px 10px 8px 34px; font-size:14px; }
.pp-daycount{ font-size:13px; font-weight:600; color:var(--muted); white-space:nowrap; }
.pp-dp :deep(.dp__input){ font:inherit; font-size:15px; padding:9px 12px 9px 38px; border-radius:8px; border-color:var(--line); }
.pp-daypick{ display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.pp-day{ border:1px solid var(--line); border-radius:999px; padding:4px 10px; font-size:12px; cursor:pointer; display:flex; align-items:center; gap:5px; }
.pp-day.on{ background:var(--teal); color:#fff; border-color:var(--teal); }
.pp-btn{ background:var(--teal); color:#fff; border:none; border-radius:8px; padding:10px 18px; cursor:pointer; font:inherit; font-size:15px; font-weight:600; }
.pp-btn:disabled{ opacity:.45; cursor:not-allowed; }
.pp-btn.ghost{ background:#fff; color:var(--teal); border:1px solid var(--teal); }
.pp-btn.sign{ background:var(--ok-b); }
.pp-btn.sm{ padding:8px 14px; font-size:14px; }
.pp-palette{ display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-top:12px; }
.pp-block{ background:var(--teal); color:#fff; border-radius:10px; padding:12px 14px; }
.pp-block h4{ font-size:15px; text-align:center; margin:0 0 10px; }
.pp-hours{ display:grid; grid-template-columns:repeat(3,1fr); gap:8px 10px; }
.pp-hour{ display:flex; align-items:center; gap:7px; font-size:15px; cursor:pointer; }
.pp-hour input{ width:18px; height:18px; margin:0; }
/* time selector: chips */
.pp-chipsel{ display:flex; flex-direction:column; gap:10px; margin-top:12px; }
.pp-chiprow{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
.pp-chiplbl{ font-size:13px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.05em; width:92px; flex:0 0 92px; }
.pp-tchip{ border:1px solid #b9cfcf; background:#fff; color:#245457; border-radius:999px; padding:8px 14px; font:inherit; font-size:15px; cursor:pointer; min-width:64px; }
.pp-tchip.on{ background:var(--teal); border-color:var(--teal); color:#fff; font-weight:700; }
/* time selector: tiles */
.pp-tiles-wrap{ margin-top:12px; }
.pp-tiles{ display:grid; grid-template-columns:repeat(6,1fr); gap:8px; }
.pp-tile{ border:1px solid var(--line); background:#f6f8f8; color:#3a464c; border-radius:10px; padding:14px 4px; font:inherit; font-size:16px; font-weight:600; cursor:pointer; }
.pp-tile.night{ background:#eef1f3; color:var(--muted); }
.pp-tile.on{ background:var(--teal); border-color:var(--teal); color:#fff; }
.pp-tiles-sum{ margin-top:10px; font-size:15px; color:#245457; }
.pp-chip{ display:inline-block; padding:3px 11px; border-radius:999px; font-size:14px; font-weight:600; white-space:nowrap; }
.s-Active{ background:var(--st-active); color:#256b45; } .s-Hold{ background:var(--st-hold); color:#7a5c00; }
.s-Discontinue{ background:var(--st-dc); color:#a3312a; }
.s-DC-pending{ background:var(--st-hold); color:#7a5c00; } .s-New{ background:var(--st-new); color:#2c4a86; }
.s-Pending{ background:var(--st-pending); color:#7a3f96; } .s-Change{ background:var(--st-change); color:#8a531a; }
.s-Completed{ background:var(--st-completed); color:#1f7a1f; } .s-Partial{ background:var(--st-partial); color:#a83267; }
/* "As needed", spelled out. PRN is the clinical abbreviation and the section headers already use it;
   the chip on the row says what it means, so the indication is explicit wherever the med appears. */
.s-PRN{ background:#e8eef7; color:#3a5580; }
/* PPU-18 — automatic placements read as provisional, not done: amber rather than the green of a
   choice the caregiver made. */
.pp-banner.auto{ background:#fff8ec; border-color:#f0dcb8; color:#7a5312; }
.pp-badge-auto{ display:inline-block; padding:1px 7px; border-radius:999px; background:#fbeed6; color:#8a5a12;
  font-size:11px; font-weight:700; letter-spacing:.02em; vertical-align:middle; }
.pp-tstate.sugg{ color:#8a5a12; }
.pp-erow.sugg{ border-left-color:#e0a83c; }
.pp-scanrow{ display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin-top:12px; padding:10px 12px;
  background:#f7fafa; border:1px dashed #cfdcdc; border-radius:8px; }
.pp-time{ display:inline-flex; align-items:center; gap:6px; background:#eef4f4; border:1px solid #cfe0e0; color:#245457; border-radius:999px; padding:4px 11px; font-size:14px; margin:2px 3px; }
.pp-optional{ font-weight:400; color:var(--muted); }
.pp-grid td.na{ color:var(--muted); text-align:center; }
.pp-time.lock{ background:#f1f3f4; border-style:dashed; color:var(--muted); }
.pp-time .x{ color:#7aa; cursor:pointer; font-weight:700; font-size:16px; }
.pp-addtime{ border:1px dashed #9bbcbc; background:#fff; color:#357; border-radius:999px; padding:4px 11px; font-size:14px; cursor:pointer; font-family:inherit; }
.pp-np{ color:var(--warn); background:var(--warnbg); border:1px solid #f0d9a8; border-radius:6px; padding:5px 9px; font-size:14px; display:inline-block; }
.pp-locktag{ font-size:13px; color:#245457; background:#eef4f4; border-radius:6px; padding:2px 7px; display:inline-block; margin-top:4px; }
.lowtag{ color:var(--low-b); font-weight:700; }
.pp-grid{ width:100%; border-collapse:collapse; background:#fff; border:1px solid var(--line); border-radius:10px; overflow:hidden; }
.pp-grid th, .pp-grid td{ padding:11px 12px; border-bottom:1px solid var(--line); text-align:left; vertical-align:middle; font-size:15px; }
.pp-grid th{ background:#eef2f3; font-weight:700; color:#3a464c; }
.pp-grid tr.low{ background:var(--low-band); } .pp-grid tr.np{ background:#f1f3f4; color:var(--muted); }
.pp-grid tr.locked{ background:#f6f8f8; }
.pp-grid tr.locked input, .pp-grid tr.locked select{ background:#f1f3f4; color:var(--muted); }
.pp-name{ font-weight:700; font-size:16px; } .pp-sub{ color:var(--muted); font-size:14px; } .pp-sub.warn{ color:var(--warn); }
.num{ text-align:right; font-variant-numeric:tabular-nums; }
.pp-onhand{ width:76px; border:1px solid var(--line); border-radius:6px; padding:6px 8px; text-align:right; font:inherit; font-size:15px; }
.pp-freq{ border:1px solid var(--line); border-radius:6px; padding:6px 8px; max-width:180px; font:inherit; font-size:15px; }
.pp-section-h{ font-weight:700; color:var(--muted); text-transform:uppercase; font-size:13px; letter-spacing:.06em; margin:18px 0 8px; }
.pp-sign{ background:#fff; border:1px solid var(--line); border-radius:10px; padding:12px 16px; margin-top:16px; display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
.pp-hint-t{ color:var(--muted); font-size:14px; }
.pp-emptystate{ background:#fff; border:1px dashed #c7cdd0; border-radius:12px; padding:32px; text-align:center; color:var(--muted); }
.pp-emptystate h3{ margin:0 0 6px; color:#3a464c; } .pp-emptystate p{ margin:0 0 14px; }
/* board */
.pp-board{ display:flex; gap:14px; overflow-x:auto; padding-bottom:8px; }
.pp-cup{ flex:0 0 340px; background:#fff; border:1px solid var(--line); border-radius:12px; display:flex; flex-direction:column; }
.pp-cup > header{ background:var(--teal); color:#fff; font-size:19px; padding:12px 14px; border-radius:12px 12px 0 0; font-weight:700; text-align:center; }
.pp-cup .body{ padding:10px; display:flex; flex-direction:column; gap:8px; min-height:80px; }
.pp-cup .foot{ padding:8px 10px; border-top:1px dashed var(--line); color:var(--muted); font-size:14px; text-align:center; }
.pp-empty{ color:var(--muted); font-size:14px; text-align:center; padding:12px; }
.pp-medcard{ border:1px solid var(--line); border-radius:10px; padding:12px 14px; }
.pp-medcard .pp-name{ font-size:18px; }
.pp-medcard.low{ border-color:#e6b0aa; background:var(--low-band); }
.pp-medcard.locked{ background:#f6f8f8; border-style:dashed; }
.pp-medcard .mc-top{ display:flex; justify-content:space-between; gap:8px; align-items:center; }
.pp-medcard .mc-dose{ font-size:15px; color:var(--muted); margin-top:2px; }
.pp-npcol{ flex:0 0 270px; background:#f1f3f4; border:1px dashed #c7cdd0; border-radius:12px; padding:12px; }
/* layout C */
.pp-layoutC{ display:grid; grid-template-columns:1fr 320px; gap:16px; align-items:start; }
.pp-stack{ display:flex; flex-direction:column; gap:12px; }
.pp-mcard{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
.pp-mcard.low{ border-color:#e6b0aa; } .pp-mcard.np{ opacity:.75; } .pp-mcard.locked{ background:#f6f8f8; border-style:dashed; }
.pp-mcard > header{ display:flex; align-items:center; gap:12px; padding:12px 14px; border-bottom:1px solid var(--line); }
.pp-mcard .grow, .grow{ flex:1; }
.pp-mcard .detail{ padding:12px 14px; display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.kv{ font-size:15px; } .kv b{ display:block; color:var(--muted); font-weight:600; margin-bottom:3px; font-size:13px; }
.pp-supply{ display:inline-block; height:8px; width:100%; max-width:140px; border-radius:999px; background:#e6ebec; overflow:hidden; vertical-align:middle; }
.pp-supply > i{ display:block; height:100%; background:var(--ok-b); } .pp-supply.low > i{ background:var(--low-b); }
.pp-rail{ background:#fff; border:1px solid var(--line); border-radius:12px; padding:14px; position:sticky; top:16px; }
.pp-rail h3{ font-size:16px; margin:0 0 10px; }
.line{ display:flex; justify-content:space-between; gap:12px; padding:7px 0; border-bottom:1px solid var(--line); font-size:15px; }
.pp-rail .warn{ color:var(--low-b); }
/* locked pours */
.pp-pour{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
.pp-pour > header{ display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; padding:12px 14px; background:#eef4f4; border-bottom:1px solid var(--line); }
.pp-pour > footer{ padding:8px 14px; border-top:1px dashed var(--line); }
.pp-pourgrid-wrap{ overflow-x:auto; }
.pp-pourgrid{ border:none; border-radius:0; }
.pp-pourgrid td.cell{ white-space:nowrap; }
.pp-slot{ display:inline-flex; align-items:center; gap:4px; border-radius:6px; padding:4px 9px; font-size:14px; margin:2px; border:1px solid var(--line); background:#f6f8f8; color:var(--muted); }
.pp-slot i{ font-style:normal; font-weight:700; }
.pp-slot.o-held{ background:#fffbea; border-style:dashed; border-color:#e0c36a; color:var(--warn); } .pp-slot.o-held i{ font-weight:600; }
.o-taken{ background:var(--ok-bg); border-color:#b9dcc6; color:#256b45; }
.o-except{ background:var(--exc-bg); border-color:#f0d0a0; color:var(--exc); }
.o-refused{ background:var(--ref-bg); border-color:#e6b0aa; color:var(--low-b); }
/* administer */
.pp-timegroup{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
.pp-timegroup > header{ padding:10px 14px; background:var(--teal); color:#fff; }
.pp-timegroup > header .pp-sub{ color:rgba(255,255,255,.8); }
/* the whole header folds the block; the caret is the same drawn chevron as Time view, in white */
.pp-timegroup > header{ cursor:pointer; }
.pp-timegroup > header:focus-visible{ outline:2px solid #fff; outline-offset:-4px; }
.pp-timegroup > header .pp-caret{ color:#fff; transform:rotate(0deg); }
/* sized for reading at arm's length from a med cart: the hour leads, the rest stays legible */
.pp-timegroup > header .pp-tg-time{ font-size:22px; }
.pp-timegroup > header .pp-sub{ font-size:16px; }
.pp-timegroup > header .pp-window, .pp-timegroup > header .pp-done{ font-size:16px; }
.pp-timegroup.closed > header .pp-caret{ transform:rotate(-90deg); }
.pp-slotrow{ display:flex; align-items:center; gap:14px; padding:10px 14px; border-top:1px solid var(--line); flex-wrap:wrap; }
.pp-slotrow.o-taken{ background:var(--ok-bg); } .pp-slotrow.o-except{ background:var(--exc-bg); } .pp-slotrow.o-refused{ background:var(--ref-bg); }
.pp-outcome{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.pp-outcome .mark{ font-size:22px; font-weight:700; }
.pp-outcome.big{ font-size:16px; padding:10px 0; }
.o-taken .mark{ color:var(--ok-b); } .o-except .mark{ color:var(--exc); } .o-refused .mark{ color:var(--low-b); }
.pp-outcomes{ display:flex; gap:8px; margin-top:12px; flex-wrap:wrap; }
.pp-out{ flex:1; padding:12px; font-size:15px; border-radius:8px; border:2px solid var(--line); background:#fff; cursor:pointer; font:inherit; font-weight:700; color:var(--muted); }
.pp-out.taken.on{ border-color:var(--ok-b); background:var(--ok-bg); color:#256b45; }
.pp-out.except.on{ border-color:var(--exc); background:var(--exc-bg); color:var(--exc); }
.pp-out.refused.on{ border-color:var(--low-b); background:var(--ref-bg); color:var(--low-b); }
/* modals */
.pp-overlay{ position:fixed; inset:0; background:rgba(17,26,28,.55); z-index:60; display:flex; align-items:center; justify-content:center; padding:20px; }
.pp-modal{ background:#fff; border-radius:14px; padding:20px 22px; width:min(640px, 100%); max-height:90vh; overflow:auto; box-shadow:0 20px 60px rgba(0,0,0,.35); }
.pp-modal.narrow{ width:min(420px, 100%); }
.pp-modal h2{ margin:0 0 4px; font-size:21px; color:var(--teal); }
.pp-modal-list{ border:1px solid var(--line); border-radius:10px; margin-top:12px; max-height:40vh; overflow:auto; }
.pp-modal-line{ display:flex; gap:12px; padding:10px 14px; border-bottom:1px solid var(--line); align-items:center; }
.pp-modal-line:last-child{ border-bottom:none; } .pp-modal-line.skip{ opacity:.55; }
.pp-signer{ margin-top:14px; padding:10px 12px; background:#eef4f4; border:1px solid #cfe0e0; border-radius:8px; font-size:15px; }
.pp-addtime:disabled{ opacity:.45; cursor:not-allowed; }
.pp-tstate{ font-size:14px; font-weight:600; margin:2px 0 6px; }
.pp-tstate.sm{ font-size:13px; margin:4px 0 0; }
.pp-tstate.ok{ color:#256b45; } .pp-tstate.todo{ color:var(--warn); } .pp-tstate.skip{ color:var(--muted); font-style:italic; }
.pp-picker{ margin-top:8px; padding:10px; border:1px dashed #9bbcbc; border-radius:10px; background:#f6fafa; display:flex; flex-direction:column; gap:8px; align-items:flex-start; }
.pp-pchips{ display:flex; flex-wrap:wrap; gap:6px; }
.pp-pchip{ border:1px solid #b9cfcf; background:#fff; color:#245457; border-radius:999px; padding:7px 13px; font:inherit; font-size:15px; cursor:pointer; }
.pp-pchip.on{ background:var(--teal); border-color:var(--teal); color:#fff; font-weight:700; }
.pp-pchip:disabled{ opacity:.4; cursor:not-allowed; }
.pp-pchip.wide{ width:100%; text-align:left; border-radius:8px; }
.pp-skip{ display:flex; align-items:center; gap:6px; font-size:14px; color:var(--muted); margin-top:8px; cursor:pointer; }
.pp-skip input{ width:16px; height:16px; margin:0; }
/* Neutral by default. Every mini button used to be painted in the warning red, so "Details" and
   "Discontinue" were indistinguishable — only .dc is destructive and only .dc should look it. */
.pp-mini{ border:1px solid var(--line); background:#fff; color:#3c5a5a; border-radius:6px; padding:3px 9px; font:inherit; font-size:12px; cursor:pointer; }
.pp-mini:hover{ border-color:var(--teal); color:var(--teal); }
.pp-place{ background:#fff; border:1px solid var(--line); border-radius:12px; }
.pp-placerow{ display:flex; align-items:center; gap:12px; padding:10px 14px; border-bottom:1px solid var(--line); flex-wrap:wrap; }
.pp-placerow:last-child{ border-bottom:none; }
.pp-placerow.todo{ background:var(--warnbg); } .pp-placerow.skip{ background:#f6f8f8; }
.pp-hint-t.warn{ color:var(--warn); font-weight:600; }
.pp-cup .body .pp-addtime{ align-self:center; }
/* mobile date control (hidden on desktop) */
.pp-dates-mob{ display:none; }
.pp-dsum{ display:flex; align-items:center; gap:8px; }
.pp-dsum-v{ font-size:16px; font-weight:600; }
.pp-dpanel{ margin-top:10px; display:flex; flex-direction:column; gap:8px; }
/* PPU-2 / PPU-3 — the "Time view" line: always on screen, with the panel behind it optional.
   It is a real bordered control, not a label with a glyph after it: in review the flat version read
   as static text and the small triangle read as a dot. The caret is one character rotated by CSS, so
   it stays a recognisable chevron at any size and animates between the two states. */
.pp-timeshead{ display:flex; flex-wrap:wrap; align-items:center; gap:10px; padding:2px 0 6px; }
.pp-timestoggle{ display:inline-flex; align-items:center; gap:8px; border:1px solid #cfdcdc; background:#fff; border-radius:8px;
  padding:7px 12px; font:inherit; font-size:15px; font-weight:600; color:var(--ink, #1c2b2b); cursor:pointer; line-height:1; }
.pp-timestoggle:hover{ border-color:var(--teal); color:var(--teal); }
.pp-timestoggle:focus-visible{ outline:2px solid var(--teal); outline-offset:2px; }
.pp-caret{ display:inline-flex; align-items:center; justify-content:center; width:12px; height:12px; color:var(--muted);
  transform:rotate(-90deg); transition:transform .15s ease; }
.pp-caret::before{ content:''; display:block; width:7px; height:7px; margin-top:-3px;
  border-right:2px solid currentColor; border-bottom:2px solid currentColor; transform:rotate(45deg); }
.pp-timestoggle.open .pp-caret{ transform:rotate(0deg); }
.pp-timestoggle:hover .pp-caret{ color:var(--teal); }
.pp-timescount{ display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 6px;
  border-radius:999px; background:#eef4f4; color:#245457; font-size:12px; font-weight:700; }
.pp-timessum{ display:flex; flex-wrap:wrap; align-items:center; gap:2px; min-height:28px; }
.pp-timespanel{ padding-bottom:4px; }
/* PPU-5 — "By PRN" lifts the as-needed block above the schedule instead of reordering the schedule,
   which is what a caregiver checking for an as-needed med is actually after. */
.pp-prnfirst{ display:flex; flex-direction:column; }
.pp-prnfirst > .pp-prnblock, .pp-prnfirst > .pp-crow.pp-prnblock{ order:-1; }
/* ---- variant E: compact rows with the full record on demand ---- */
.pp-ehead{ display:flex; align-items:center; gap:10px; margin:14px 0 8px; }
.pp-elist{ display:flex; flex-direction:column; gap:8px; }
.pp-erow{ background:#fff; border:1px solid var(--line, #dbe4e4); border-left:3px solid transparent; border-radius:10px; padding:10px 12px; }
.pp-erow.ok{ border-left-color:#2f9e7e; }
.pp-erow.todo{ border-left-color:#d8a33c; }
.pp-erow.skip{ border-left-color:#bcc7c7; }
.pp-erow.locked{ background:#f7f9f9; }
.pp-erow.low{ background:#fffaf3; }
.pp-erow.np{ background:#f7f9f9; }
.pp-erow.open{ border-color:#bcd4d4; box-shadow:0 1px 3px rgba(0,0,0,.05); }
.pp-erow.prn{ border-left-color:#8fa6c4; }
.pp-etop{ display:flex; align-items:flex-start; gap:10px; }
/* Sized as a real touch target (36px) rather than an icon: at 26px the chevron read as a dot and was
   hard to aim at. The row header beside it toggles the same thing, so the press area is the whole top
   line and not just this square. */
.pp-ecaret{ flex:none; display:inline-flex; align-items:center; justify-content:center; width:36px; height:36px;
  border:1px solid #cfdcdc; background:#f4f8f8; border-radius:8px; color:#4a6a6a; cursor:pointer; padding:0; }
.pp-ecaret:hover{ border-color:var(--teal); color:var(--teal); background:#eaf3f3; }
.pp-ecaret:focus-visible{ outline:2px solid var(--teal); outline-offset:2px; }
/* inherit, so the chevron follows the button through its states. `.pp-caret` sets its own colour for
   the Time view control, which meant the open (filled teal) button kept a dark grey chevron instead of
   the white its own `color` was asking for. */
.pp-ecaret .pp-caret{ width:16px; height:16px; color:inherit; transform:rotate(-90deg); }
.pp-ecaret .pp-caret::before{ width:9px; height:9px; border-width:2.5px; margin-top:-4px; }
.pp-ecaret.open{ color:#fff; border-color:var(--teal); background:var(--teal); }
.pp-ecaret.open .pp-caret{ transform:rotate(0deg); }
.pp-ecaret.spacer{ border-color:transparent; background:transparent; cursor:default; }
/* the row header is the other half of the same target */
.pp-grouphead{ display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin:18px 0 8px; padding:10px 14px;
  background:var(--teal); border-radius:10px; font-size:17px; color:#fff; }
.pp-grouphead b{ font-size:22px; }
.pp-grouphead .pp-sub{ color:rgba(255,255,255,.85); font-size:16px; }
.pp-grouphead .pp-addtime{ margin-left:auto; background:#fff; color:var(--teal); border:1px solid #fff; font-weight:600; font-size:15px; }
.pp-grouppick{ margin:0 0 8px; }
.pp-groupempty{ padding:4px 12px 8px; }
.pp-grouprow > td{ padding:0 !important; background:transparent !important; border:0 !important; }
/* Scanning the bottle is the check that the right medication is going into the cup, so it is the
   loudest control on a medication — solid, large, and it turns green once the bottle has matched. */
.pp-scanbtn{ display:inline-flex; align-items:center; justify-content:center; gap:6px; min-height:38px; padding:7px 16px;
  border:2px solid var(--scan); background:var(--scan); color:#fff; border-radius:8px; font:inherit; font-size:15px; font-weight:700;
  cursor:pointer; white-space:nowrap; }
.pp-scanbtn::before{ content:''; width:16px; height:12px; flex:none;
  background:repeating-linear-gradient(90deg, currentColor 0 2px, transparent 2px 3px, currentColor 3px 4px, transparent 4px 6px); }
.pp-scanbtn:hover{ filter:brightness(1.1); }
.pp-scanbtn:focus-visible{ outline:2px solid var(--teal); outline-offset:2px; }
.pp-scanbtn.ok{ background:#e7f5ef; border-color:#2f9e7e; color:#1f6f57; }
.pp-scanbtn.ok::before{ display:none; }
.pp-scanline{ display:flex; flex-wrap:wrap; align-items:center; gap:10px; margin:4px 0 8px; }
.pp-scanbtn.block{ width:100%; margin-top:8px; }
.pp-scanstate{ font-size:12px; font-weight:700; padding:1px 7px; border-radius:999px; background:#fdf1dc; color:#8a5a00; }
.pp-scanstate.ok{ background:#e7f5ef; color:#1f6f57; }
.pp-etimechips{ display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.pp-etimechips .pp-picker{ flex-basis:100%; }
.pp-ehit{ cursor:pointer; border-radius:6px; margin:-2px -4px; padding:2px 4px; }
.pp-ehit:hover .pp-name{ color:var(--teal); }
.pp-etimes{ margin-top:8px; padding-left:46px; }
/* the detail is C's grid, indented under its row so it reads as belonging to it */
.pp-edetail{ margin-top:10px; margin-left:46px; padding:12px 14px; background:#f7fafa; border:1px solid #e3ecec; border-radius:8px;
  display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:8px 16px; }
.pp-edetail .kv{ display:flex; flex-direction:column; gap:3px; font-size:14px; }
.pp-edetail .kv b{ font-size:12px; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); font-weight:700; }
.pp-esum{ display:flex; flex-wrap:wrap; gap:8px; margin-top:14px; padding:10px 12px; background:#fff; border:1px solid var(--line, #dbe4e4); border-radius:10px; }
.pp-esum .cell{ display:flex; flex-direction:column; gap:2px; min-width:130px; flex:1; font-size:14px; }
.pp-esum .cell span{ font-size:12px; text-transform:uppercase; letter-spacing:.03em; color:var(--muted); font-weight:700; }
.pp-esum .cell.warn b{ color:#a5651a; }
@media (max-width:640px){
  .pp-dp.narrow{ width:100%; flex:1 1 100%; }
  .pp-timeshead .grow{ display:none; }
  .pp-sortpick{ width:100%; justify-content:space-between; }
  .pp-etimes{ padding-left:0; }
  .pp-edetail{ margin-left:0; }
  .pp-edetail{ grid-template-columns:1fr 1fr; }
}
.pp-seg{ display:flex; background:#eef2f3; border-radius:8px; padding:3px; }
.pp-seg.sm{ flex:0 0 auto; }
.pp-seg.sm button{ flex:0 0 auto; padding:6px 12px; font-size:13px; white-space:nowrap; }
/* Sort sits in the corner like a filter control. A select rather than a segmented group: the segmented
   version wrapped "By medication" onto two lines and grew the whole card by a row. */
.pp-sortpick{ display:inline-flex; align-items:center; gap:7px; flex:0 0 auto; }
.pp-sortpick > span{ font-size:13px; font-weight:600; color:var(--muted); }
.pp-sortpick select{ font:inherit; font-size:14px; font-weight:600; color:var(--teal); background:#fff;
  border:1px solid #cfdcdc; border-radius:8px; padding:7px 28px 7px 11px; cursor:pointer; line-height:1;
  appearance:none; -webkit-appearance:none;
  background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath fill='none' stroke='%23667777' stroke-width='2' d='M2.5 4.5 6 8l3.5-3.5'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center; background-size:11px; }
.pp-sortpick select:hover{ border-color:var(--teal); }
.pp-seg button{ flex:1; border:none; background:transparent; padding:8px; border-radius:6px; font:inherit; font-size:14px; font-weight:600; color:var(--muted); cursor:pointer; }
.pp-seg button.on{ background:#fff; color:var(--teal); box-shadow:0 1px 2px rgba(0,0,0,.08); }
/* re-pour / discontinue / administration window */
.pp-locktag.repour{ background:var(--warnbg); color:var(--warn); }
.pp-locktag.repour a{ color:inherit; margin-left:4px; }
.pp-rowactions{ margin-top:6px; }
/* The one destructive action on the row: its own colour, its own border, and a deliberate gap plus a
   divider so it cannot be hit while reaching for Details. */
.pp-mini.dc{ color:var(--low-b); border-color:#e6c4c4; margin-left:14px; position:relative; }
.pp-mini.dc::before{ content:''; position:absolute; left:-8px; top:2px; bottom:2px; width:1px; background:var(--line); }
.pp-mini.dc:hover{ color:#fff; background:var(--low-b); border-color:var(--low-b); }
.pp-placerow.locked{ background:#eef4f4; }
.pp-window{ font-size:13px; font-weight:600; color:rgba(255,255,255,.85); }
.pp-dclist{ margin-top:22px; }
.pp-fielderr{ color:#a3312a; font-size:14px; margin:6px 2px 0; }
.pp-signote{ margin:14px 0 8px; font-size:14px; color:#245457; font-weight:600; }
.pp-sigrow{ display:flex; flex-wrap:wrap; gap:12px; }
.pp-sigcard{ flex:1 1 220px; border:1px solid var(--line); border-radius:10px; overflow:hidden; background:#fff; }
/* yours to sign: the only card that looks live */
.pp-sigcard.mine{ border:2px solid #2c7a7b; box-shadow:0 0 0 3px rgba(44,122,123,.12); cursor:pointer; }
.pp-sigcard.mine header{ background:#2c7a7b; color:#fff; }
/* theirs: deliberately quiet, and not clickable */
.pp-sigcard.waiting{ border-style:dashed; background:#fafbfb; opacity:.72; }
.pp-sigcard.waiting header{ background:#f1f4f4; color:var(--muted); font-weight:500; }
.pp-sigcard.signed{ border-color:#bcdcc9; }
.pp-sigcard header{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:8px 12px; font-weight:600; font-size:14px; background:#eef4f4; color:#245457; }
.pp-sigcard.signed header{ background:var(--ok-bg); color:var(--ok-b); }
.pp-sigbadge{ border-radius:999px; padding:2px 9px; font-size:12px; font-weight:700; }
.pp-sigbadge.signed{ background:var(--ok-b); color:#fff; }
.pp-sigbadge.mine{ background:#fff; color:#2c7a7b; }
.pp-sigbadge.waiting{ background:#e4e9e9; color:var(--muted); font-weight:600; }
.pp-sigbody{ padding:12px; min-height:62px; }
.pp-sigbody.empty,.pp-sigbody.action{ display:flex; align-items:center; justify-content:center; text-align:center; color:var(--muted); font-size:14px; }
.pp-btn.sign.sm{ padding:7px 16px; font-size:14px; }
.pp-signame{ font-family:'Segoe Script','Bradley Hand',cursive; font-size:20px; color:#1f3f42; }
.pp-sigpreview{ margin-top:12px; padding:10px 12px; border:1px dashed var(--line); border-radius:10px; }
.pp-modal.narrow{ max-width:420px; }
.pp-dcrow.pending{ background:var(--st-hold); }
.pp-dcrow{ display:flex; flex-wrap:wrap; gap:8px 14px; align-items:center; padding:10px 14px; border:1px solid var(--line); border-radius:10px; background:var(--st-dc); margin-bottom:8px; }
.pp-dcrow .grow{ flex:1 1 240px; }
textarea.pp-pill{ resize:vertical; font:inherit; }
/* time selector: list */
.pp-tlist{ display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-top:12px; }
.pp-tlist-h{ font-size:13px; font-weight:700; color:var(--muted); text-transform:uppercase; letter-spacing:.05em; margin-bottom:4px; }
.pp-tlist-row{ display:flex; align-items:center; gap:8px; padding:6px 8px; border-radius:6px; font-size:15px; cursor:pointer; }
.pp-tlist-row.on{ background:#eef4f4; color:#245457; font-weight:600; }
.pp-tlist-row input{ width:18px; height:18px; margin:0; }
/* time selector: presets */
.pp-presets{ margin-top:12px; display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
.pp-preset{ display:flex; border:1px solid #b9cfcf; border-radius:10px; overflow:hidden; background:#fff; }
.pp-preset.on{ border-color:var(--teal); }
.pp-preset-btn{ border:none; background:transparent; padding:8px 12px; font:inherit; cursor:pointer; display:flex; flex-direction:column; align-items:flex-start; line-height:1.2; color:#245457; }
.pp-preset-btn span{ font-size:13px; color:var(--muted); }
.pp-preset.on .pp-preset-btn{ background:var(--teal); color:#fff; } .pp-preset.on .pp-preset-btn span{ color:rgba(255,255,255,.85); }
.pp-preset-sel{ border:none; border-left:1px solid var(--line); background:#f6f8f8; font:inherit; font-size:13px; padding:0 6px; }
.pp-preset-custom{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.pp-presets .pp-tiles-sum{ flex:0 0 100%; margin-top:0; }
/* layout D: compact checklist */
.pp-clist{ background:#fff; border:1px solid var(--line); border-radius:12px; overflow:hidden; }
.pp-crow{ padding:12px 14px; border-bottom:1px solid var(--line); }
.pp-crow:last-child{ border-bottom:none; }
.pp-crow.todo{ border-left:4px solid var(--warn); } .pp-crow.ok{ border-left:4px solid var(--ok-b); } .pp-crow.skip, .pp-crow.np, .pp-crow.prn{ border-left:4px solid #c7cdd0; background:#f8f9f9; }
.pp-crow.locked{ background:#f6f8f8; }
.pp-crow-top{ display:flex; align-items:flex-start; gap:10px; }
.pp-crow-times{ margin-top:8px; }
.pp-crow-times .pp-tstate{ margin:0 0 6px; }
/* administer per block */
.pp-timegroup > header{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
.pp-btn.light{ background:#fff; color:var(--teal); }
.pp-done{ font-weight:700; color:rgba(255,255,255,.9); }
.pp-wlist{ margin:10px 0 0; padding-left:20px; font-size:15px; }
.pp-except{ margin-top:12px; padding:10px 12px; background:var(--exc-bg); border:1px solid #f0d0a0; border-radius:8px; display:flex; flex-direction:column; gap:4px; }
.pp-modal-actions{ display:flex; justify-content:flex-end; gap:10px; margin-top:16px; }
@media (max-width:900px){
  .pp-proto{ padding:12px 12px 40px; }
  .medications-header{ margin:0 0 12px; }
  .medications-title{ font-size:1.7em; margin-bottom:10px; }
  .medications-nav{ display:grid; grid-template-columns:repeat(4,1fr); gap:4px; background:#fff; border:1px solid var(--line); border-radius:10px; padding:4px; }
  .nav-separator{ display:none; }
  .nav-link{ font-size:14px; font-weight:600; padding:8px 4px; text-align:center; border-radius:8px; white-space:nowrap; }
  .nav-link.active{ background:var(--teal); color:#fff; }
  .pp-head{ gap:10px; }
  .pp-row{ gap:8px; } .pp-row .pp-pill, .pp-dp{ width:100%; max-width:none; flex:1 1 100%; }
  .pp-row .pp-btn{ width:100%; } .pp-dates .pp-lbl{ width:100%; }
  .pp-dates-desk{ display:none; } .pp-dates-mob{ display:block; }
  .pp-dsum .pp-btn{ width:auto; }
  .pp-tlist{ grid-template-columns:1fr 1fr; }
  .pp-preset{ flex:1 1 45%; } .pp-preset-btn{ flex:1; }
  .pp-timegroup > header .pp-btn{ width:100%; }
  .pp-layoutC{ grid-template-columns:1fr; } .pp-rail{ position:static; }
  .pp-palette{ grid-template-columns:1fr 1fr; }
  .pp-chiplbl{ width:100%; flex:0 0 100%; }
  .pp-sign{ flex-direction:column; align-items:stretch; } .pp-sign .pp-btn{ width:100%; }
  .pp-mcard .detail{ grid-template-columns:1fr 1fr; }
  .pp-modes{ gap:2px; padding:3px; flex-wrap:wrap; } .pp-mode{ flex:1; padding:9px 4px; font-size:14px; white-space:nowrap; text-align:center; min-width:0; }
  /* Three tabs already fill the row on a phone; the badge takes its own line rather than
     squeezing them. `margin-left:0` undoes the desktop right-park. */
  .pp-variant-badge{ flex:0 0 100%; margin-left:0; text-align:center; }
  .pp-mode .cnt{ min-width:18px; padding:0 5px; font-size:12px; margin-left:4px; }
  .pp-devbar button.reset{ margin-left:0; }
  /* responsive grid: rows become cards, cells become label/value lines */
  .pp-resp, .pp-resp tbody, .pp-resp tr, .pp-resp td{ display:block; width:100%; }
  .pp-resp thead{ display:none; }
  .pp-resp tr{ border:1px solid var(--line); border-radius:12px; margin-bottom:12px; padding:6px 0; background:#fff; }
  .pp-resp tr.low{ background:var(--low-band); } .pp-resp tr.locked{ background:#f6f8f8; }
  .pp-resp td{ border-bottom:none; padding:6px 14px; display:flex; justify-content:space-between; align-items:center; gap:12px; text-align:left; }
  .pp-resp td.num{ text-align:right; }
  .pp-resp td[data-l]::before{ content:attr(data-l); color:var(--muted); font-size:13px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; flex:0 0 auto; }
  .pp-resp td.c-name{ display:block; padding-top:10px; border-bottom:1px solid var(--line); margin-bottom:4px; }
  .pp-resp td.c-gap{ display:none; }
  .pp-resp td.c-times{ flex-wrap:wrap; } .pp-resp td.c-times::before{ flex:0 0 100%; }
  .pp-resp .pp-freq{ max-width:60%; }
}
@media (max-width:520px){
  .pp-palette{ grid-template-columns:1fr; } .pp-hours{ grid-template-columns:repeat(3,1fr); }
  .pp-tiles{ grid-template-columns:repeat(4,1fr); } .pp-tile{ padding:12px 2px; font-size:15px; }
  .pp-mcard .detail{ grid-template-columns:1fr; }
  .pp-modal{ padding:16px; }
  .pp-outcomes{ flex-direction:column; }
}

/* ---- review 2026-09-25: status, holds, pour marks, filters, history ---- */
/* the status select reads as the chip it replaced, tinted by status; choosing opens the status pop-up */
.pp-stsel{ appearance:auto; border:1px solid transparent; border-radius:999px; padding:4px 8px 4px 11px; font:inherit; font-size:14px; font-weight:600; cursor:pointer; max-width:190px; }
.pp-stsel:hover:not(:disabled){ border-color:rgba(0,0,0,.25); }
.pp-stsel:focus-visible{ outline:2px solid var(--teal); outline-offset:2px; }
.pp-stsel:disabled{ cursor:default; opacity:.9; }
.pp-stsel option{ background:#fff; color:#1e2529; }
/* a row carries its status as a soft tint, so a held or changed med stands out while scanning the list */
.pp-proto .st-Hold, .pp-proto .st-DC-pending{ background-color:#fffbea; }
.pp-proto .st-New{ background-color:#f3f6fd; } .pp-proto .st-Change{ background-color:#fcf6ef; }
.pp-proto .st-Pending{ background-color:#faf5fc; } .pp-proto .st-Completed{ background-color:#f3fbf3; }
.pp-proto .st-Partial{ background-color:#fdf3f8; }
.pp-mednote{ display:block; width:fit-content; max-width:100%; margin:4px 0 2px; padding:3px 9px; border-radius:6px; font-size:13px; font-weight:600; line-height:1.35; }
.pp-mednote-c{ margin:0 14px 8px; }
/* pour marks: a dose in its cup turns green with a tick; a card whose every dose is in turns pale green */
.pp-time.tap{ cursor:pointer; }
.pp-time.tap:hover{ border-color:var(--poured); }
.pp-time.poured{ background:var(--poured); border-color:var(--poured); color:#fff; font-weight:700; }
.pp-time.poured .x{ color:#d8f0e3; }
.pp-time .ck{ font-style:normal; font-weight:800; }
.pp-time.held{ background:#fbf1c9; border-color:#e8d48a; color:#7a5c00; text-decoration:line-through; }
.pp-proto .poured:not(.pp-time){ background-color:var(--poured-bg); }
.pp-pourbtn{ display:inline-flex; align-items:center; min-height:38px; padding:7px 14px; border:2px solid var(--poured); background:#fff; color:var(--poured);
  border-radius:8px; font:inherit; font-size:15px; font-weight:700; cursor:pointer; white-space:nowrap; }
.pp-pourbtn.on{ background:var(--poured); color:#fff; }
.pp-pourbtn:focus-visible{ outline:2px solid var(--poured); outline-offset:2px; }
.pp-cardacts{ display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:8px; }
.pp-cardacts > *{ width:100%; justify-content:center; }
.pp-search{ border:1px solid #cfdcdc; border-radius:8px; padding:7px 10px; font:inherit; font-size:14px; min-width:220px; }
.pp-autofill{ display:inline-flex; align-items:center; gap:6px; font-size:14px; font-weight:600; color:#245457; cursor:pointer; white-space:nowrap; }
.pp-stfilters{ display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin:12px 0 8px; }
.pp-stfilter{ display:inline-flex; align-items:center; gap:6px; border:1px solid transparent; border-radius:999px; padding:5px 12px; font:inherit; font-size:14px; font-weight:600; cursor:pointer; }
.pp-stfilter.s-Hold{ background:var(--st-hold); color:#7a5c00; }
.pp-stfilter .cnt{ background:rgba(255,255,255,.7); border-radius:999px; padding:0 7px; font-size:12px; }
.pp-stfilter.on{ border-color:currentColor; box-shadow:0 0 0 1px currentColor; }
.pp-stfilter:disabled{ opacity:.45; cursor:default; }
.pp-stfilter:focus-visible{ outline:2px solid var(--teal); outline-offset:2px; }
.pp-stlist{ border:1px solid var(--line); border-radius:10px; background:#fff; margin:0 0 12px; }
.pp-stlist-h{ display:flex; align-items:center; gap:8px; padding:10px 14px; border-bottom:1px solid var(--line); }
.pp-stlist-h .pp-mini{ margin-left:auto; }
.pp-stlist-row{ display:flex; gap:10px; padding:10px 14px; border-top:1px solid var(--line); }
.pp-stlist-row:first-of-type{ border-top:0; }
.pp-stlist-line{ font-size:14px; margin-top:3px; display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.pp-stradios{ display:flex; flex-direction:column; gap:6px; margin:12px 0 6px; padding:10px 12px; background:#f6f9f9; border-radius:8px; }
.pp-stradios label, .pp-stdates label, .pp-stdoses label{ display:flex; align-items:center; gap:8px; font-size:15px; }
.pp-sttimes{ display:flex; flex-wrap:wrap; gap:8px; margin:4px 0 8px; }
.pp-sttime{ display:inline-flex; align-items:center; gap:6px; border:1px solid #cfdcdc; border-radius:999px; padding:5px 12px; cursor:pointer; }
.pp-sttime.on{ border-color:var(--teal); background:#eef4f4; }
.pp-stdates{ display:flex; flex-wrap:wrap; gap:14px; margin-top:10px; }
.pp-stdoses{ display:flex; flex-wrap:wrap; align-items:center; gap:12px; margin-top:12px; }
.pp-stdoses input{ width:70px; }
.pp-timeact{ margin-top:12px; padding:12px; border:1px solid var(--line); border-radius:10px; display:flex; flex-direction:column; gap:8px; align-items:flex-start; }
.pp-timeact .pp-pchips{ display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.pp-timeact.dc{ border-color:#efc9c9; background:#fdf6f6; }
.pp-btn.dcbtn{ background:var(--low-b); border-color:var(--low-b); color:#fff; }
.pp-dayhead{ margin:16px 0 4px; font-size:18px; font-weight:700; color:#245457; }
.pp-tstate.miss{ color:var(--low-b); }
.pp-changes{ border:1px solid var(--line); border-radius:10px; background:#fff; padding:10px 14px; margin:8px 0 12px; }
.pp-changes-h{ font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--muted); margin-bottom:6px; }
.pp-change{ display:flex; flex-wrap:wrap; align-items:baseline; gap:4px 8px; padding:4px 0; font-size:14px; }
.pp-change + .pp-change{ border-top:1px dashed var(--line); }
.pp-stchip{ border-radius:999px; padding:1px 10px; font-size:12px; font-weight:700; }
.pp-stfilter.of-all{ background:#eef2f3; color:#245457; } .pp-stfilter.of-taken{ background:var(--ok-bg); color:var(--ok-b); }
.pp-stfilter.of-except{ background:var(--exc-bg); color:#8a531a; } .pp-stfilter.of-refused{ background:var(--ref-bg); color:var(--low-b); }
.pp-stfilter.of-missed{ background:var(--low-band); color:var(--low-b); } .pp-stfilter.of-held{ background:var(--st-hold); color:#7a5c00; }
.pp-stfilter.of-changed{ background:var(--st-change); color:#8a531a; }
.pp-slotrow.held{ background:#fffbea; }
.pp-recordrow > td{ background:#f7fafa !important; padding:6px 12px 12px !important; }
.pp-mcard-record{ margin:0 14px 12px; }
@media (max-width: 760px){ .pp-search{ min-width:0; width:100%; } }
</style>
